import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { View, Text, Image, ImageBackground, StyleSheet, TouchableOpacity, ScrollView, Alert, TextInput, Modal, Platform, ActivityIndicator, useWindowDimensions } from 'react-native';
import { resolveSkinChannelInfo, type SkinChannelInfo } from '../../services/skin/resolveSkinChannelInfo';
import { APP_CONSTANTS, API_CONSTANTS } from '../../config/constants';
import { QuickAccessTable } from '../../utils/tableAccess';
import type { ContentRecord, ContentStoreState } from '../../stores/contentStore';
import { AdminSegmentDto, AdminNodeDto, AdminPageDto, AdminQuestionListDto, AdminQuestionDto, AdminHtmlQuestionDto, AdminHtmlAnswerDto } from '../../types/adminSegment';
import { AdminStateDto, AdminAssessmentSession, AdminSegmentNavigation } from '../../types/adminState';
import { computeNextDevagePage, getPasType, getNodeParam, extractNodeAnswers, buildDevelopmentalAgeInput } from '../../utils/devageAdmin';
import { computeDevelopmentalAge } from '../../utils/developmentalAge';
import { AdminStateStorage } from '../../services/AdminStateStorage';
import { AdminResponseStorage } from '../../storage/AdminResponseStorage';
import { AdminResponseDto, AdminSubmitResponseDto } from '../../types/AdminResponseDto';
import { useUserStore } from '../../contexts/StoreContext';
import type { UserState } from '../../types/user';

// Question-page header — skin branding only (logo + background), no menu/nav buttons.
// Matches ProGroupsMainMenu's own header proportions for a consistent brand presentation.
const QUESTION_HEADER_HEIGHT = 90;
const QUESTION_HEADER_LOGO_HEIGHT = 65;

// Below this window width the question card switches to the compact *Phone style overrides.
const PHONE_MAX_WIDTH = 500;

// Single source of truth for the question text's default typography — both
// styles.questionText (native StyleSheet, used below) and HTML_QUESTION_TEXT_STYLE (its web
// mirror, just below) are derived from this so the two can't drift out of sync with each
// other. Change font styling for question text here, not in either derived copy.
const QUESTION_TEXT_FONT = {
  fontSize: 24,
  fontFamily: 'Arial',
  fontWeight: '400' as const,
  color: '#2c3e50',
  lineHeight: 36,
  textAlign: 'left' as const,
};

// overrideQAlign (AdminStateDto Data array) — horizontal alignment of the question text and any
// inline controls (e.g. <<dt:...>>) in it. Maps to a flex justifyContent for the wrapping rows.
type QuestionAlign = 'left' | 'center' | 'right';
const QUESTION_ALIGN_JUSTIFY: Record<QuestionAlign, 'flex-start' | 'center' | 'flex-end'> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

// Plain (non-StyleSheet.create) mirror of QUESTION_TEXT_FONT, for the web
// dangerouslySetInnerHTML host <div> in renderHtmlText — see that function for why a raw
// object is required here. lineHeight is re-expressed with an explicit unit because, unlike
// React Native, a bare DOM lineHeight number is unitless (relative to font size) rather than
// an absolute px value — every other field is used as-is from QUESTION_TEXT_FONT.
const HTML_QUESTION_TEXT_STYLE = {
  ...QUESTION_TEXT_FONT,
  lineHeight: `${QUESTION_TEXT_FONT.lineHeight}px`,
} as const;

// ── Insertion functions ─────────────────────────────────────────────────────────────
// <<fn:args...>> tokens within question text that render as live interactive controls
// (rather than plain substituted text, unlike the <<fieldcode>> preload tokens performed
// by performPreloadSubstitutions). Recognized function names must be listed here so
// performPreloadSubstitutions knows to leave them untouched for renderHtmlText's
// splitInsertionFunctionSegments to pick up instead. Currently only 'dt' (date control)
// is implemented; add new function names here as they're supported.
const INSERTION_FUNCTION_NAMES = new Set(['dt']);

// <<dt:baseName:years:approxFlg:showBlankLabels:attemptPreselect:styleOverride>> — e.g.
// <<dt:dob:-99:false:true:true:font-size:20px;color:#333333>>. Generates baseNameday/
// baseNamemon/baseNameyr answer fields (baseNameday omitted when approxFlg) plus a combined
// baseName field, all saved as normal MVar-style answers via handleAnswerChange — see
// renderDateControl. The optional 6th field is a CSS-declaration-style string
// ("prop:value;prop2:value2", kebab- or camelCase property names either way) overriding the
// dropdowns' own default styling (DATE_CONTROL_SELECT_STYLE / dateControlDropdownText) — see
// parseInlineStyleOverride. It's the raw remainder of the token after the 5th colon, so it
// may itself contain colons (e.g. "font-size:20px") without being mistaken for extra fields.
// One property is layout rather than dropdown styling: "justify-content:center" (or
// flex-start/flex-end) positions the whole month/day/year row on its line — see
// renderDateControl. The control can't be centered from surrounding cText markup.
interface DateControlToken {
  fn: 'dt';
  baseName: string;
  /** Year dropdown range relative to the current year — negative counts back (most recent
   * year first), positive counts forward (for future dates), 0 means just the current year. */
  yearsParam: number;
  /** true = generate only month + year (no day) for an approximate date. */
  approxFlg: boolean;
  /** true = include a blank "Month"/"Day"/"Year" placeholder option in each dropdown. */
  showBlankLabels: boolean;
  /** true = prefill each dropdown from any existing saved answer (blank if none); false =
   * always render blank regardless of any saved value. */
  attemptPreselect: boolean;
  /** Parsed from the token's optional 6th field — CSS-in-JS-style overrides (camelCase keys,
   * string values as authored) merged on top of the dropdowns' default style. Empty object
   * when the 6th field is omitted. */
  styleOverride: Record<string, string>;
}

// Parses a "prop:value;prop2:value2" style-override string (the <<dt:...>> token's optional
// 6th field) into a plain style object with camelCase keys, e.g. "font-size:20px;color:#333"
// -> { fontSize: '20px', color: '#333' }. Used as-is for web <select> inline styles; native
// styles run the result through toNativeStyleOverride first since RN style values can't
// carry units the way DOM inline styles can.
const parseInlineStyleOverride = (raw: string | undefined): Record<string, string> => {
  if (!raw || !raw.trim()) return {};
  const result: Record<string, string> = {};
  // Authors often wrap the whole field in quotes (e.g. ...:true:"font-size:17px;...") —
  // left in, the leading quote sticks to the first property name and silently drops it.
  const unquoted = raw.trim().replace(/^["']+|["']+$/g, '');
  unquoted.split(';').forEach((decl) => {
    const colonIdx = decl.indexOf(':');
    if (colonIdx < 0) return;
    const prop = decl.slice(0, colonIdx).trim();
    const value = decl.slice(colonIdx + 1).trim();
    if (!prop || !value) return;
    const camelProp = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    result[camelProp] = value;
  });
  return result;
};

// React Native style values are typed numbers for most numeric properties (fontSize,
// lineHeight, letterSpacing) — unlike DOM inline styles, they can't carry a "px" suffix.
// Strips a trailing px (if present) and coerces to a number for those properties; every
// other property (color, fontWeight, fontFamily, fontStyle, ...) passes through unchanged
// since RN accepts the same string values DOM CSS does for those.
const NUMERIC_NATIVE_STYLE_KEYS = new Set(['fontSize', 'lineHeight', 'letterSpacing']);
const toNativeStyleOverride = (webStyle: Record<string, string>): Record<string, string | number> => {
  const result: Record<string, string | number> = {};
  Object.entries(webStyle).forEach(([key, value]) => {
    if (NUMERIC_NATIVE_STYLE_KEYS.has(key)) {
      const numeric = parseFloat(value);
      if (!Number.isNaN(numeric)) {
        result[key] = numeric;
        return;
      }
    }
    result[key] = value;
  });
  return result;
};

// Plain (non-StyleSheet.create) style for the raw web <select> elements rendered by
// renderDateControl — same reasoning as HTML_QUESTION_TEXT_STYLE above.
const DATE_CONTROL_SELECT_STYLE = {
  fontFamily: 'Arial',
  fontSize: 15,
  color: '#2c3e50',
  padding: '6px 8px',
  borderRadius: 4,
  border: '1px solid #c7c7c7',
  backgroundColor: '#ffffff',
} as const;

// AdminSegment data structures based on backend content
interface HtmlPage {
  htmlpage_id: string;
  htmlpage_title: string;
  htmlpage_num: number;
  htmlpage_type: number;
}

// PageLogicSyncDto for backend page_logic_put requests
interface PageLogicSyncDto {
  htmPageId: string;
  preScript: string;
  nodePageId: string;
}

// CalcLogicSyncDto for backend calc_logic_put requests
interface CalcLogicPutRequestDto {
  CalcScripts: CalcLogicDto[];
}

interface CalcLogicDto {
  nodeId?: string;
  surveybId: string;
  calcScript: string;
}

interface HtmlQuestion {
  htmlquestion_id: string;
  htmlquestion_question_id: string;
  htmlquestion_answertype_id: string;
  htmlquestion_text: string;
  htmlquestion_name: string;
}

interface HtmlAnswer {
  htmlanswer_id: string;
  htmlanswer_htmlquestion_id: string;
  htmlanswer_text: string;
  htmlanswer_answer_id: string;
}

interface AdminState {
  currentPageNum: number;
  answers: Record<string, string>;
  isComplete: boolean;
  startTime: Date;
  pageHistory: number[];
}

/**
 * Outcome of the fire-and-forget backend submission (`admin_response_submit`) kicked off by
 * the closing "Done" step — handed to `onBackendSubmitSettled` exactly once after it settles.
 * Redirect-based callers await this handshake before navigating away so `window.location`'s
 * unload can't abort the request mid-flight (which is what "the background submit stopped
 * working" turned out to be).
 */
export interface AdminBackendSubmitResult {
  /** true only when `admin_response_submit` round-tripped and the backend accepted it. */
  submitted: boolean;
  /** Server-assigned identifiers when `submitted`; null on failure or when skipped (offline / no account). */
  response: AdminSubmitResponseDto | null;
}

interface AdminViewProps {
  accountId: string;
  profileId: string;
  assessmentId: string;
  languageId: string;
  sourceType?: string;
  sourceId?: string;
  /**
   * Called as soon as local completion succeeds (before backend submission, which continues
   * in the background — see handleDoneNavigation) so the caller can react immediately, e.g.
   * routing to ReportView via `localAdminResponse` (locally-built, no server recId needed)
   * when the closing-segment PreScript set `renderReport`. `adminState.status` at this point
   * is 'ADMIN_COMPLETE', not 'SUBMIT_SUCCESS' — the real backend recId/status arrive later.
   */
  onComplete: (adminState: AdminStateDto, localAdminResponse?: AdminResponseDto) => void;
  /**
   * Fires exactly once after the background `admin_response_submit` started by the closing
   * "Done" step settles — success, failure, or skipped (offline / no account). A redirect-based
   * caller (e.g. ReportView's "Continue") awaits this handshake before navigating away, so the
   * page unload can't abort the in-flight submission. In-app callers that stay mounted after
   * completion (WellVisitView) don't need it and leave it unset.
   */
  onBackendSubmitSettled?: (result: AdminBackendSubmitResult) => void;
  onCancel: () => void;
  useContentStore?: any; // Platform-specific content store hook (optional)
  apiService?: any; // ApiService instance for content operations (optional)
  contentService?: any; // ContentService instance for content operations (optional)
  storage?: any; // Storage interface for AdminSegmentStorage and AdminStateStorage
  showDebugInfo?: boolean; // Show debug information at bottom
  enableEditPageLogic?: boolean; // Enable page logic editing and single-step execution
  enableEditCalcLogic?: boolean; // Enable calc logic editing and single-step execution
  skipContentAutoLoad?: boolean; // Skip automatic content loading (AdminState already prepared)
  initialAdminState?: AdminStateDto | null; // Pre-loaded AdminState to use instead of initializing
  /** Skin type for layout/branding. '2' = ChildNav (default). Controls answerLayout and other UI variants. */
  skinType?: string;
  /**
   * Extra fields merged into the admin_response_submit payload alongside the
   * generic UserId/AccountId/AccessType fields already sent — for flows whose
   * identity model AdminStateDto doesn't otherwise capture (e.g. the /secure
   * token-based session's SessionId/AssessmentId from SecureLoginResponseDto).
   */
  extraSubmitFields?: Record<string, string>;
}

/**
 * Merges a preload array (AdminSegmentDto.preloads/.Preloads shape, or the same-shaped
 * AccountPlaDto.Preloads) into a Map<languageTypeId, Map<fieldCode, value>>, overwriting any
 * existing entries for the same languageTypeId+fieldCode. Called first for the current
 * segment's own preloads, then again for AdminStateDto.accountPreloads — so account-level PLA
 * preloads (configured via Account > Assessment Options) win on a fieldCode collision, and
 * remain visible to getPreload() across every segment since they live on AdminStateDto rather
 * than per-segment content.
 */
function mergePreloadsIntoMap(map: Map<string, Map<string, string>>, preloads: any[] | undefined | null): void {
  (preloads || []).forEach((preloadItem: any) => {
    const rawFieldCode = preloadItem.fieldCode || preloadItem.FieldCode;
    // Strip << >> delimiters that may be stored on the FieldCode (e.g. "<<CloseText>>")
    const fieldCode = rawFieldCode ? rawFieldCode.replace(/^<<|>>$/g, '') : rawFieldCode;
    const value = preloadItem.value || preloadItem.Value;
    const languageTypeId = preloadItem.languageTypeId || preloadItem.LanguageTypeId || '';

    if (fieldCode && value !== undefined) {
      if (!map.has(languageTypeId)) {
        map.set(languageTypeId, new Map<string, string>());
      }
      map.get(languageTypeId)!.set(fieldCode, value);
    }
  });
}

/**
 * Restricts/orders a uvar question's answer set to the account-configured subset
 * (AdminStateDto.uVars — see adminState.ts's uVars field, resolved the same way accountPreloads
 * is, or from the token-flow SecureLoginResponseDto.uVars) instead of showing every
 * content-authored AdminHtmlQuestionDto.HtmlUvarAns answer. Falls back to the full content
 * answer set when the question isn't uvar-typed, or when no matching account uvar config is
 * found (e.g. an older resumed session, or a flow that never populated AdminStateDto.uVars) —
 * so a missing/stale config never leaves a question unanswerable.
 */
function resolveUvarAnswers(uvarTypeId: string, contentAnswers: any[], stateUVars: any[] | undefined): any[] {
  if (!uvarTypeId || uvarTypeId === '0') {
    return contentAnswers;
  }
  const match = (stateUVars || []).find((u) => (u.UvarTypeId || u.uvarTypeId) === uvarTypeId);
  if (!match) {
    return contentAnswers;
  }
  return match.HtmlUvarAns || match.htmlUvarAns || [];
}

export const AdminView: React.FC<AdminViewProps> = ({
  accountId,
  profileId,
  assessmentId = '1987', 
  languageId = '1',
  sourceType = 'ASSESSMENT',
  sourceId = assessmentId,
  onComplete,
  onBackendSubmitSettled,
  onCancel,
  useContentStore,
  apiService,
  contentService,
  storage,
  showDebugInfo = false,
  enableEditPageLogic = false,
  enableEditCalcLogic = false,
  skipContentAutoLoad = false,
  initialAdminState = null,
  skinType = APP_CONSTANTS.DEFAULT_SKIN_TYPE,
  extraSubmitFields,
}) => {
  // Derive answer layout from skinType using the global APP_CONSTANTS mapping.
  // Callers can override skinType per-product; ChildNav defaults to '2' → 'vertical2column'.
  // Unmapped/unrecognized skinType values fall back to skin '3' (Patient Tools), not the
  // generic DEFAULT_* constants — matches the token-flow contract (SecureLoginResponseDto.skinType).
  const answerLayout: string =
    APP_CONSTANTS.ANSWER_LAYOUT_BY_SKIN[skinType] ?? APP_CONSTANTS.ANSWER_LAYOUT_BY_SKIN['3'];
  const answerButtonColor: string =
    APP_CONSTANTS.ANSWER_BUTTON_COLOR_BY_SKIN[skinType] ?? APP_CONSTANTS.ANSWER_BUTTON_COLOR_BY_SKIN['3'];
  // Use sourceId from initialAdminState if provided, otherwise use the prop
  const actualSourceId = initialAdminState?.sourceId || sourceId;
  
  // Get userStore hook and extract session state and methods
  const userStoreHook = useUserStore();
  const userSession = userStoreHook((state: UserState) => state.session);
  const checkStoredAuth = userStoreHook((state: UserState) => state.checkStoredAuth);
  
  // Get current user from session
  const currentUser = userSession?.user || null;
  
  // Content store integration (if useContentStore is available)
  const contentRecords = useContentStore ? useContentStore((state: ContentStoreState) => state.records) : [];
  const isLoading = useContentStore ? useContentStore((state: ContentStoreState) => state.isLoading) : false;
  
  // Loading state for apiService approach
  const [isContentLoading, setIsContentLoading] = useState(false);

  // Web-friendly cancel confirmation dialog
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Phone-width layout: the card/content/question horizontal padding stacks up (~56px a side
  // at desktop values), which on a ~360px screen leaves too little room — see the *Phone
  // style overrides applied where those containers render.
  const { width: windowWidth } = useWindowDimensions();
  const isPhone = windowWidth < PHONE_MAX_WIDTH;

  // Skin branding (logo/background/bg color) for the question-page header — same
  // resolveSkinChannelInfo lookup ProGroupsShell uses for its own main menu header.
  const [skinChannelInfo, setSkinChannelInfo] = useState<SkinChannelInfo | null>(null);
  useEffect(() => {
    if (!storage || !skinType) {
      return;
    }
    let cancelled = false;
    void resolveSkinChannelInfo(storage, skinType).then((resolved) => {
      if (!cancelled) {
        setSkinChannelInfo(resolved);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [storage, skinType]);

  // RN Image needs an explicit numeric width — measure the logo's natural aspect ratio and
  // scale width to match the fixed header logo height (same approach as ProGroupsMainMenu).
  const [skinLogoWidth, setSkinLogoWidth] = useState<number | null>(null);
  useEffect(() => {
    const logoSrc = skinChannelInfo?.logoSrc;
    if (!logoSrc) {
      setSkinLogoWidth(null);
      return;
    }
    let cancelled = false;
    Image.getSize(
      logoSrc,
      (naturalWidth, naturalHeight) => {
        if (!cancelled && naturalHeight > 0) {
          setSkinLogoWidth((naturalWidth / naturalHeight) * QUESTION_HEADER_LOGO_HEIGHT);
        }
      },
      (error) => {
        console.warn('AdminView: failed to measure skin header logo size', error);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [skinChannelInfo?.logoSrc]);

  // Skin branding header — logo + background only, no menu/nav buttons. Shared across every
  // render branch below (loading/error/completion states as well as the active question page).
  const renderSkinHeader = () => {
    const skinLogo = skinChannelInfo?.logoSrc ? (
      <Image
        source={{ uri: skinChannelInfo.logoSrc }}
        style={[styles.skinHeaderLogo, skinLogoWidth ? { width: skinLogoWidth } : null]}
        resizeMode="contain"
      />
    ) : null;

    return (
      <View
        style={[
          styles.skinHeaderShell,
          skinChannelInfo?.backgroundSrc
            ? { backgroundColor: 'transparent' }
            : skinChannelInfo?.skinBgColor
            ? { backgroundColor: skinChannelInfo.skinBgColor }
            : null,
        ]}
      >
        {skinChannelInfo?.backgroundSrc ? (
          <ImageBackground
            source={{ uri: skinChannelInfo.backgroundSrc }}
            style={styles.skinHeaderBackgroundImage}
            resizeMode="stretch"
          >
            {skinLogo}
          </ImageBackground>
        ) : (
          skinLogo
        )}
      </View>
    );
  };

  // Track if loadAdminSegment has been triggered to prevent race conditions
  const [hasLoadAdminSegmentBeenCalled, setHasLoadAdminSegmentBeenCalled] = useState(false);

  // Track segment transitions to prevent rendering conflicts
  const [isSegmentTransitioning, setIsSegmentTransitioning] = useState(false);

  // AdminSegment data (moved to top to avoid dependency issues)
  const [adminSegment, setAdminSegment] = useState<any>(null);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState<number>(0);
  const [availableSegments, setAvailableSegments] = useState<string[]>([]);
  
  // Answer state management for AdminSegment
  const [segmentAnswers, setSegmentAnswers] = useState<Record<string, any>>({});

  // AdminState management - moved to top to avoid dependency issues
  const [adminState, setAdminState] = useState<AdminStateDto | null>(null);
  const [assessmentSession, setAssessmentSession] = useState<AdminAssessmentSession | null>(null);
  const [adminStateStorage, setAdminStateStorage] = useState<AdminStateStorage | null>(null);

  // Wall-clock start of THIS sitting (not cumulative across suspend/resume — a
  // resumed assessment's duration only measures the current active session).
  // Used to replace the previously-hardcoded duration: '0' on submission.
  const sessionStartTimeRef = useRef<number>(Date.now());
  
  // Separate storage for CalcScript operations to avoid conflicts with main storage
  const [calcScriptStorage, setCalcScriptStorage] = useState<AdminStateStorage | null>(null);
  
  // AdminResponse storage for saving completed assessments
  const [adminResponseStorage, setAdminResponseStorage] = useState<AdminResponseStorage | null>(null);

  // Debug info state - can be overridden by storage
  const [debugInfoEnabled, setDebugInfoEnabled] = useState(showDebugInfo);
  
  // Page Logic editing state
  const [editPageLogicEnabled, setEditPageLogicEnabled] = useState(enableEditPageLogic);
  const [editedPreScript, setEditedPreScript] = useState<string>('');
  const [preScriptDirty, setPreScriptDirty] = useState(false);
  const [preScriptExecuted, setPreScriptExecuted] = useState(false);
  const [isSavingPageLogic, setIsSavingPageLogic] = useState(false);
  const [savePageLogicResult, setSavePageLogicResult] = useState<{ success: boolean, message: string } | null>(null);

  // enableDone (closing-segment completion) — isDoneProcessing drives the footer Done
  // button's spinner; doneClickLatchRef guards against double-tap re-entrancy into
  // handleNextOrDone's Done branch (a ref, not state, so the check is synchronous and can't
  // race a rapid double-tap the way state — batched/async — could).
  const [isDoneProcessing, setIsDoneProcessing] = useState(false);
  const doneClickLatchRef = useRef(false);

  // Calc Logic editing state
  const [editCalcLogicEnabled, setEditCalcLogicEnabled] = useState(enableEditCalcLogic);
  const [editedCalcScript, setEditedCalcScript] = useState<string>('');
  const [calcScriptDirty, setCalcScriptDirty] = useState(false);
  const [calcScriptExecuted, setCalcScriptExecuted] = useState(false);
  const [pendingCalcExecution, setPendingCalcExecution] = useState(false);
  const [isSavingCalcLogic, setIsSavingCalcLogic] = useState(false);
  const [saveCalcLogicResult, setSaveCalcLogicResult] = useState<{ success: boolean, message: string } | null>(null);

  // Node-level CalcScript editing state (separate from segment-level CalcScript)
  const [editedNodeCalcScript, setEditedNodeCalcScript] = useState<string>('');
  const [nodeCalcScriptDirty, setNodeCalcScriptDirty] = useState(false);

  // CalcScript execution during segment transitions - track pending updates
  const [pendingCalcUpdates, setPendingCalcUpdates] = useState<{
    dataUpdates: Array<{name: string, value: string}>;
    propertyUpdates: Array<{name: string, value: string}>;
    preloadUpdates: Array<{fieldCode: string, value: string, languageTypeId: string}>;
  } | null>(null);
  
  // Flag to track when CalcScript execution is complete and updates are ready
  const [calcScriptUpdatesReady, setCalcScriptUpdatesReady] = useState(false);
  
  // Assessment completion state
  const [isAssessmentComplete, setIsAssessmentComplete] = useState(false);
  const [isSavingAdminResult, setIsSavingAdminResult] = useState(false);
  const [saveAdminResultResult, setSaveAdminResultResult] = useState<{ success: boolean, message: string } | null>(null);

  // Shared session user state for both local and backend saves
  const [restoredSessionUser, setRestoredSessionUser] = useState<any>(null);

  // Function to restore session user (shared by both local and backend saves)
  const restoreSessionUser = async (): Promise<any> => {
    let currentSessionUser = userSession?.user || null;
    
    // Check if session exists, if not try to restore from storage first
    if (!userSession || !userSession.user) {
      try {
        await checkStoredAuth();
        const restoredSession = userStoreHook.getState().session;
        if (!restoredSession || !restoredSession.user) {
          throw new Error('User session unavailable after restore - please login again');
        }
        currentSessionUser = restoredSession.user;
      } catch (restoreError) {
        console.error('❌ Session restore failed:', restoreError);
        throw new Error('User session unavailable - please login again');
      }
    }

    // Get the user from session (should contain UserInfoDto mappings)
    const latestSession = userStoreHook.getState().session;
    const sessionUser = latestSession?.user || currentSessionUser;
    
    // Handle missing UserInfoDto mappings with fallback logic
    let finalUserId = sessionUser?.userId;
    let finalFamilyId = sessionUser?.familyId;
    
    if (!sessionUser?.userId && sessionUser?.email) {
      finalUserId = sessionUser.email;
      sessionUser.userId = finalUserId;
    }
    
    if (!sessionUser?.familyId && sessionUser?.email) {
      finalFamilyId = 'default-account';
      sessionUser.familyId = finalFamilyId;
    }
    
    if (!finalUserId) {
      throw new Error('User ID not found in session and no email available for fallback');
    }

    if (!finalFamilyId) {
      throw new Error('Account ID not found in session and no fallback available');
    }

    // Cache the restored user for reuse
    setRestoredSessionUser(sessionUser);
    return sessionUser;
  };

  // Cache dirty PreScripts count to avoid calling collectDirtyPreScripts on every render
  const dirtyPreScriptsCount = useMemo(() => {
    // Only calculate if we have AdminSegment and Page Logic is enabled
    if (!editPageLogicEnabled || !adminSegment) return 0;

    const segType = String(adminSegment.SegmentType || adminSegment.segmentType || '');
    const isNodeSeg = segType === '5' || segType === 'PAS';

    let count = 0;

    if (isNodeSeg) {
      // Node segment: count dirty pages across all nodes
      const nodes = adminSegment.Nodes || adminSegment.nodes;
      if (nodes && Array.isArray(nodes)) {
        nodes.forEach((node: any) => {
          const nodePages: any[] = node.Pages || node.pages || [];
          nodePages.forEach((page: any) => {
            const editedScript = page.editedPreScript;
            const originalScript = page.preScript || page.PreScript || '';
            const pageId = page.htmPageId || page.HtmPageId || page.nodePageId || page.NodePageId;
            if (editedScript && editedScript !== originalScript && pageId) {
              count++;
            }
          });
        });
      }

      // Add current page if dirty but not yet saved to AdminSegment
      if (preScriptDirty && adminState) {
        const nodes2 = adminSegment.Nodes || adminSegment.nodes;
        const nodeIdx = adminState.nodeIndex ?? 0;
        const nodePages: any[] = (nodes2?.[nodeIdx]?.Pages || nodes2?.[nodeIdx]?.pages) || [];
        const currentPage = nodePages.find((p: any) => String(p.PageNum || p.pageNum || '') === String(adminState.pageNum));
        const currentPageId = currentPage?.htmPageId || currentPage?.HtmPageId || currentPage?.nodePageId || currentPage?.NodePageId;
        if (currentPageId) {
          const isAlreadyCounted = nodePages.some((page: any) => {
            const pid = page.htmPageId || page.HtmPageId || page.nodePageId || page.NodePageId;
            const pNum = page.pageNum || page.PageNum;
            return pid === currentPageId && String(pNum) === String(adminState.pageNum) && page.editedPreScript && page.editedPreScript !== (page.preScript || page.PreScript || '');
          });
          if (!isAlreadyCounted) count++;
        }
      }
    } else {
      // Flat segment
      const pages = adminSegment.pages || adminSegment.Pages;
      if (!pages || !Array.isArray(pages)) return 0;

      pages.forEach((page: any) => {
        // Check all possible htmPageId field variations
        const htmPageId = page.htmPageId || page.HtmPageId || page.htmlPageId || page.HtmlPageId ||
                         page.pageId || page.PageId || page.id || page.Id || page.ID;
        const editedScript = page.editedPreScript;
        const originalPreScript = page.preScript || page.PreScript || '';

        if (editedScript && editedScript !== originalPreScript && htmPageId) {
          count++;
        }
      });

      // Add current page if it's dirty but not yet saved to AdminSegment
      if (preScriptDirty && adminState) {
        const targetPageNum = adminState.pageNum;
        const currentPage = pages.find((p: any) => (p.pageNum || p.PageNum) === targetPageNum);
        const currentHtmPageId = currentPage?.htmPageId || currentPage?.HtmPageId ||
                                currentPage?.htmlPageId || currentPage?.HtmlPageId ||
                                currentPage?.pageId || currentPage?.PageId ||
                                currentPage?.id || currentPage?.Id || currentPage?.ID;

        if (currentHtmPageId) {
          const isAlreadyCounted = pages.some((page: any) => {
            const pageNum = page.pageNum || page.PageNum;
            const pageEditedPreScript = page.editedPreScript;
            const pageOriginalPreScript = page.preScript || page.PreScript || '';
            return pageNum === targetPageNum && pageEditedPreScript && pageEditedPreScript !== pageOriginalPreScript;
          });
          if (!isAlreadyCounted) count++;
        }
      }
    }

    return count;
  }, [adminSegment, preScriptDirty, editedPreScript, adminState?.pageNum, adminState?.nodeIndex, editPageLogicEnabled]);

  // Cache dirty CalcScripts count to avoid recalculating on every render
  const dirtyCalcScriptsCount = useMemo(() => {
    // Only calculate if we have AdminSegment and Calc Logic is enabled
    if (!editCalcLogicEnabled || !adminSegment) return 0;
    
    let count = 0;

    // Check if current segment has dirty CalcScript edits
    const originalCalcScript = adminSegment.calcScript || adminSegment.CalcScript || '';
    const currentCalcScript = editedCalcScript || originalCalcScript;
    if (calcScriptDirty && currentCalcScript !== originalCalcScript) count++;

    // Check if current node has dirty CalcScript edits
    const segType = String(adminSegment.SegmentType || adminSegment.segmentType || '');
    const isNodeSeg = segType === '5' || segType === 'PAS';
    if (isNodeSeg && adminState) {
      const nodeIdx = adminState.nodeIndex ?? 0;
      const nodes = adminSegment.Nodes || adminSegment.nodes;
      const currentNode = nodes?.[nodeIdx];
      const originalNodeCalcScript = currentNode?.calcScript || currentNode?.CalcScript || '';
      if (nodeCalcScriptDirty && editedNodeCalcScript !== originalNodeCalcScript) count++;
    }

    return count;
  }, [adminSegment, calcScriptDirty, editedCalcScript, nodeCalcScriptDirty, editedNodeCalcScript, adminState?.nodeIndex, editCalcLogicEnabled]);

  // Clear save result message after 5 seconds
  useEffect(() => {
    if (savePageLogicResult) {
      const timer = setTimeout(() => {
        setSavePageLogicResult(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [savePageLogicResult]);

  // Clear save result message after 5 seconds
  useEffect(() => {
    if (savePageLogicResult) {
      const timer = setTimeout(() => {
        setSavePageLogicResult(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [savePageLogicResult]);

  // Clear calc logic save result message after 5 seconds
  useEffect(() => {
    if (saveCalcLogicResult) {
      const timer = setTimeout(() => {
        setSaveCalcLogicResult(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [saveCalcLogicResult]);

  // Clear admin result save result message after 5 seconds
  useEffect(() => {
    if (saveAdminResultResult) {
      const timer = setTimeout(() => {
        setSaveAdminResultResult(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [saveAdminResultResult]);

  // Initialize Page Logic state when component first loads
  useEffect(() => {
    if (editPageLogicEnabled && adminState) {
      const currentPage = getCurrentPageFromSegment();
      if (currentPage) {
        const initialPreScript = currentPage.preScript || currentPage.PreScript || '';
        // console.log('🎯 AdminView: Initializing Page Logic with preScript from AdminSegment:', {
        //   pageNum: adminState.pageNum,
        //   preScript: initialPreScript,
        //   preScriptLength: initialPreScript.length
        // });
        
        console.log('🟡 INIT-EFFECT: fired', { pageNum: adminState.pageNum, initialPreScript: JSON.stringify(initialPreScript), initialized: preScriptInitialized.current });
        // Initialize editedPreScript with the current page's preScript only on first load.
        // Using a ref prevents this from overwriting a user-cleared (empty) PreScript.
        if (!preScriptInitialized.current) {
          preScriptInitialized.current = true;
          console.log('🟡 INIT-EFFECT: setting editedPreScript =', JSON.stringify(initialPreScript));
          setEditedPreScriptWithRef(initialPreScript);
        } else {
          console.log('🟡 INIT-EFFECT: already initialized, skipping');
        }
      }
    }
  }, [editPageLogicEnabled, adminState?.pageNum, adminSegment]); // Depend on adminSegment to re-init when segment loads

  // Reset Page Logic state when page changes
  useEffect(() => {
    if (editPageLogicEnabled && adminState) {
      const currentPageNum = adminState.pageNum;
      const currentNodeIdx = adminState.nodeIndex ?? 0;

      // Reset init flag so the init useEffect does not re-fire for a new page
      preScriptInitialized.current = false;
      // Invalidate any outstanding storage-fallback promise from a previous PAGE-CHANGE-EFFECT
      preScriptStorageGenRef.current += 1;
      const capturedGen = preScriptStorageGenRef.current;

      // Get the current page and load its preScript
      const currentPage = getCurrentPageFromSegment();
      console.log('🔄 PAGE-CHANGE-EFFECT: fired', {
        pageNum: currentPageNum,
        nodeIndex: currentNodeIdx,
        foundPage: !!currentPage,
        pagePreScript: (currentPage?.preScript || currentPage?.PreScript || '').substring(0, 80),
        hasEditedPreScript: !!currentPage?.editedPreScript
      });
      if (currentPage) {
        const originalPreScript = currentPage.preScript || currentPage.PreScript || '';
        
        // Check if there are any saved changes in AdminSegment for this specific page
        // The Continue button should save changes to AdminSegment in memory
        let preScriptToLoad = originalPreScript;
        let foundSavedEdits = false;
        
        // Check if AdminSegment has saved changes for this page (flat or node segment)
        if (adminSegment) {
          const segType = String(adminSegment.SegmentType || adminSegment.segmentType || '');
          const isNodeSeg = segType === '5' || segType === 'PAS';

          if (isNodeSeg) {
            // Node segment: find page in Nodes[nodeIndex].Pages[]
            const nodes = adminSegment.Nodes || adminSegment.nodes;
            const nodeIdx = currentNodeIdx;
            const nodePages: any[] = (nodes?.[nodeIdx]?.Pages || nodes?.[nodeIdx]?.pages) || [];
            const updatedPage = nodePages.find((p: any) => String(p.PageNum || p.pageNum || '') === String(currentPageNum));
            if (updatedPage && (updatedPage.editedPreScript !== undefined || updatedPage.modifiedPreScript !== undefined)) {
              preScriptToLoad = updatedPage.editedPreScript !== undefined ? updatedPage.editedPreScript : (updatedPage.modifiedPreScript !== undefined ? updatedPage.modifiedPreScript : originalPreScript);
              foundSavedEdits = true;
              console.log('🔄 useEffect: ✅ Found saved preScript edits for node page', currentPageNum);
            }
          } else {
            // Flat segment: find page in pages[]
            const pages = adminSegment.pages || adminSegment.Pages;
            if (pages && Array.isArray(pages)) {
              const updatedPage = pages.find((p: any) => (p.pageNum || p.PageNum) === currentPageNum);
              if (updatedPage && (updatedPage.editedPreScript !== undefined || updatedPage.modifiedPreScript !== undefined)) {
                preScriptToLoad = updatedPage.editedPreScript !== undefined ? updatedPage.editedPreScript : (updatedPage.modifiedPreScript !== undefined ? updatedPage.modifiedPreScript : originalPreScript);
                foundSavedEdits = true;
                console.log('🔄 useEffect: ✅ Found saved preScript edits for page', currentPageNum, ':', {
                  savedPreScript: preScriptToLoad.substring(0, 50) + (preScriptToLoad.length > 50 ? '...' : ''),
                  savedLength: preScriptToLoad.length,
                });
              }
            }
          }
        }

        // Load the preScript (either saved edits or original).
        // Mark initialized=true BEFORE setEditedPreScript so that if adminSegment changes
        // synchronously (e.g. savePreScriptEdits → setAdminSegment), INIT-EFFECT sees true
        // and does not overwrite the value we are about to set.
        preScriptInitialized.current = true;
        pageEntryPreScriptRef.current = preScriptToLoad;
        setEditedPreScriptWithRef(preScriptToLoad);
        setPreScriptDirty(preScriptToLoad !== originalPreScript);

        // Always reset execution state for new page
        setPreScriptExecuted(false);

        // If no in-memory edit found, check local storage as fallback
        // (covers the case where the app restarted or the flush happened only to storage)
        if (!foundSavedEdits) {
          const segTypeStr = String(adminSegment?.SegmentType || adminSegment?.segmentType || '');
          const isNodeSegFallback = segTypeStr === '5' || segTypeStr === 'PAS';
          let pageStorageId: string | undefined;
          if (isNodeSegFallback) {
            const nodeIdxFallback = adminState?.nodeIndex ?? 0;
            const nodesFallback = adminSegment?.Nodes || adminSegment?.nodes;
            const nodeObjFallback = nodesFallback?.[nodeIdxFallback];
            const nodeIdFallback = nodeObjFallback?.nodeId || nodeObjFallback?.NodeId
              || nodeObjFallback?.Id || nodeObjFallback?.id || String(nodeIdxFallback);
            const rawId = currentPage.htmPageId || currentPage.HtmPageId
              || currentPage.nodePageId || currentPage.NodePageId;
            pageStorageId = rawId ? `node_${nodeIdFallback}_${rawId}` : undefined;
          } else {
            pageStorageId = currentPage.htmPageId || currentPage.HtmPageId;
          }
          if (pageStorageId) {
            const pageStorageSegmentId = adminState?.segment || '';
            loadPreScriptEditsFromStorage().then(storedEdits => {
              // Discard if a newer PAGE-CHANGE-EFFECT already ran (stale async result)
              if (capturedGen !== preScriptStorageGenRef.current) {
                return;
              }
              const stored = storedEdits[getPreScriptEditEntryKey(pageStorageSegmentId, pageStorageId!)];
              if (stored && stored.editedPreScript !== undefined && stored.editedPreScript !== originalPreScript) {
                preScriptInitialized.current = true;
                pageEntryPreScriptRef.current = stored.editedPreScript;
                setEditedPreScriptWithRef(stored.editedPreScript);
                setPreScriptDirty(true);
                console.log('🔄 useEffect: ✅ Loaded PreScript from local storage for page', currentPageNum);
              }
            });
          }
        }
      } else {
        // No page found, reset everything
        setEditedPreScriptWithRef('');
        setPreScriptDirty(false);
        setPreScriptExecuted(false);
      }
    }
  }, [adminState?.pageNum, adminState?.nodeIndex, editPageLogicEnabled, adminSegment]); // nodeIndex ensures cross-node navigation reloads correct preScript

  // Auto-save the current page's PreScript edit to local storage whenever it changes while dirty.
  // This ensures edits persist even if the user leaves AdminView without navigating first.
  useEffect(() => {
    if (!preScriptDirty || !adminState || !editPageLogicEnabled || !adminSegment) return;

    const timer = setTimeout(() => {
      const currentPage = getCurrentPageFromSegment();
      if (!currentPage) return;

      const segTypeStr = String(adminSegment.SegmentType || adminSegment.segmentType || '');
      const isNodeSeg = segTypeStr === '5' || segTypeStr === 'PAS';
      let pageStorageId: string | undefined;

      if (isNodeSeg) {
        const nodeIdx = adminState.nodeIndex ?? 0;
        const nodes = adminSegment.Nodes || adminSegment.nodes;
        const nodeObj = nodes?.[nodeIdx];
        const nodeId = nodeObj?.nodeId || nodeObj?.NodeId || nodeObj?.Id || nodeObj?.id || String(nodeIdx);
        const rawId = currentPage.htmPageId || currentPage.HtmPageId
          || currentPage.nodePageId || currentPage.NodePageId;
        pageStorageId = rawId ? `node_${nodeId}_${rawId}` : undefined;
      } else {
        pageStorageId = currentPage.htmPageId || currentPage.HtmPageId;
      }

      if (pageStorageId) {
        // Use the ref so the timer reads the current value, not a stale closure.
        savePreScriptEditsToStorage(adminState.segment, pageStorageId, editedPreScriptRef.current);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [editedPreScript, preScriptDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize Calc Logic state when component first loads
  useEffect(() => {
    if (editCalcLogicEnabled && adminSegment) {
      const initialCalcScript = adminSegment.calcScript || adminSegment.CalcScript || '';
      console.log('📊 AdminView: Initializing Calc Logic with CalcScript from AdminSegment:', {
        segment: adminSegment.surveyId || adminSegment.SurveyId,
        calcScript: initialCalcScript,
        calcScriptLength: initialCalcScript.length
      });
      
      // Always initialize editedCalcScript with the current segment's CalcScript
      setEditedCalcScript(initialCalcScript);
      setCalcScriptDirty(false); // Reset dirty state since we're loading original
    }
  }, [editCalcLogicEnabled, adminSegment]);

  // Reset Calc Logic state when segment changes
  useEffect(() => {
    if (editCalcLogicEnabled && adminSegment) {
      const originalCalcScript = adminSegment.calcScript || adminSegment.CalcScript || '';
      
      // Always load the original script when segment changes
      setEditedCalcScript(originalCalcScript);
      setCalcScriptDirty(false); // Reset dirty state since we're loading original
      
      // Reset execution state for new segment
      setCalcScriptExecuted(false);
    } else if (editCalcLogicEnabled) {
      // No segment found, reset everything
      console.log('📊 AdminView: No AdminSegment found, resetting all Calc Logic state');
      setEditedCalcScript('');
      setCalcScriptDirty(false);
      setCalcScriptExecuted(false);
    }
  }, [adminSegment, editCalcLogicEnabled]);

  // Node segment state is managed via nodeIndex + pageNum stored in adminState.
  // nodeIndex is updated explicitly on NEXT_NODE transitions — no self-heal needed.

  // Initialize/reset node CalcScript state when the current node changes (or segment changes)
  useEffect(() => {
    if (!editCalcLogicEnabled || !adminSegment) {
      setEditedNodeCalcScriptWithRef('');
      setNodeCalcScriptDirty(false);
      return;
    }
    const segType = String(adminSegment.SegmentType || adminSegment.segmentType || '');
    const isNodeSeg = segType === '5' || segType === 'PAS';
    if (!isNodeSeg) {
      setEditedNodeCalcScriptWithRef('');
      setNodeCalcScriptDirty(false);
      return;
    }
    const nodes = adminSegment.Nodes || adminSegment.nodes;
    const nodeIdx = adminState?.nodeIndex ?? 0;
    const currentNode = nodes?.[nodeIdx];
    const originalNodeCalcScript = currentNode?.calcScript || currentNode?.CalcScript || '';

    // Check storage for any persisted edits for this node
    const nodeId = currentNode?.nodeId || currentNode?.NodeId || String(nodeIdx);
    loadNodeCalcScriptEditsFromStorage().then((storedEdits) => {
      if (storedEdits[nodeId] !== undefined && storedEdits[nodeId] !== originalNodeCalcScript) {
        setEditedNodeCalcScriptWithRef(storedEdits[nodeId]);
        setNodeCalcScriptDirty(true);
        console.log('📊 nodeCalcScript useEffect: Loaded persisted node CalcScript edit for nodeId', nodeId);
      } else {
        setEditedNodeCalcScriptWithRef(originalNodeCalcScript);
        setNodeCalcScriptDirty(false);
        console.log('📊 nodeCalcScript useEffect: Loaded original node CalcScript for nodeId', nodeId);
      }
    });
  }, [adminSegment, adminState?.nodeIndex, editCalcLogicEnabled]);

  // Apply pending CalcScript updates to AdminState if ready (async function for sequential processing)
  const applyPendingCalcScriptUpdates = async (): Promise<AdminStateDto | null> => {
    if (calcScriptUpdatesReady && pendingCalcUpdates && adminState && calcScriptStorage) {
      console.log('📊 AdminView: Applying pending CalcScript updates to AdminState (using separate storage):', {
        dataUpdates: pendingCalcUpdates.dataUpdates.length,
        propertyUpdates: pendingCalcUpdates.propertyUpdates.length,
        preloadUpdates: pendingCalcUpdates.preloadUpdates.length,
        currentSegment: adminState.segment,
        currentPageNum: adminState.pageNum
      });

      try {
        const updatedState = await applyCalcScriptUpdatesSync(adminState, pendingCalcUpdates);
        
        // Use separate CalcScript storage for saving updates (avoids conflicts with main storage)
        console.log('📊 AdminView: Using separate CalcScript storage for save operation to avoid conflicts');
        await calcScriptStorage.saveAdminState(updatedState);
        setAdminState(updatedState);

        // Clear the updates since they've been applied
        setPendingCalcUpdates(null);
        setCalcScriptUpdatesReady(false);

        console.log('📊 AdminView: ✅ CalcScript updates applied and saved to separate storage');
        return updatedState;
      } catch (error) {
        console.error('📊 AdminView: ❌ Error applying/saving CalcScript updates to separate storage:', error);
        // Clear the updates even on error to prevent infinite retry
        setPendingCalcUpdates(null);
        setCalcScriptUpdatesReady(false);
        return null;
      }
    }
    
    // console.log('📊 applyPendingCalcScriptUpdates: No updates to apply or CalcScript storage not ready:', {
    //   calcScriptUpdatesReady,
    //   hasPendingUpdates: !!pendingCalcUpdates,
    //   hasAdminState: !!adminState,
    //   hasCalcScriptStorage: !!calcScriptStorage,
    //   separateStorageEnabled: !!calcScriptStorage
    // });
    return null;
  };

  // Function to collect all dirty PreScripts from AdminSegment for backend sync
  const collectDirtyPreScripts = (): PageLogicSyncDto[] => {
    if (!adminSegment) {
      return [];
    }

    const dirtyPreScripts: PageLogicSyncDto[] = [];

    const processPagesForDirty = (pageList: any[], defaultNodePageId: string) => {
      pageList.forEach((page: any) => {
        const htmPageId = page.htmPageId || page.HtmPageId;
        const editedPreScript = page.editedPreScript;
        const originalPreScript = page.preScript || page.PreScript || '';
        console.log('🔍 collectDirtyPreScripts: page', page.pageNum || page.PageNum, {
          htmPageId,
          editedPreScript: JSON.stringify(editedPreScript),
          editedPreScriptType: typeof editedPreScript,
          originalPreScript: JSON.stringify(originalPreScript),
          isDirty: editedPreScript !== undefined && editedPreScript !== originalPreScript,
        });
        if (editedPreScript !== undefined && editedPreScript !== originalPreScript) {
          dirtyPreScripts.push({
            htmPageId: htmPageId,
            preScript: editedPreScript,
            nodePageId: page.NodePageId || page.nodePageId
          });
        }
      });
    };

    // Collect from flat pages (SegmentType '4')
    const pages = adminSegment.pages || adminSegment.Pages;
    if (pages && Array.isArray(pages)) {
      processPagesForDirty(pages, '');
    }

    // Collect from node pages (SegmentType '5'/'PAS')
    const nodes = adminSegment.Nodes || adminSegment.nodes;
    if (nodes && Array.isArray(nodes)) {
      nodes.forEach((node: any) => {
        const nodePages = node.Pages || node.pages;
        if (nodePages && Array.isArray(nodePages)) {
          processPagesForDirty(nodePages, '1');
        }
      });
    }

    return dirtyPreScripts;
  };

  // Function to save dirty PreScripts to backend using page_logic_put
  const savePageLogicToBackend = async () => {
    if (!apiService) {
      console.error('📤 savePageLogicToBackend: No apiService available');
      setSavePageLogicResult({ success: false, message: 'Sync service not available' });
      return false;
    }
    
    setIsSavingPageLogic(true);
    setSavePageLogicResult(null);
    
    try {
      console.log('📤 SAVE-BACKEND: entry', {
        preScriptDirty,
        editedPreScript: JSON.stringify(editedPreScript),
        editedPreScriptType: typeof editedPreScript,
        editedPreScriptLength: editedPreScript?.length,
        adminStatePageNum: adminState?.pageNum,
      });

      // ── Step 1: Collect dirty scripts from in-memory adminSegment FIRST ──────────
      // collectDirtyPreScripts reads the adminSegment closure synchronously.
      // We MUST do this before calling savePreScriptEdits, which queues setAdminSegment
      // async — after that call the closure still sees the OLD adminSegment and would
      // either miss the current page's edit or double-count it.
      const dirtyPreScripts = collectDirtyPreScripts();
      console.log('📤 SAVE-BACKEND: collectDirtyPreScripts (pre-flush):', dirtyPreScripts.length, 'entries');

      // ── Step 2: Inject current page edit directly if dirty ───────────────────────
      // collectDirtyPreScripts won't see the current in-flight edit when editedPreScript
      // hasn't been flushed to adminSegment yet (e.g. blank-clear before first navigate).
      if (preScriptDirty && adminState) {
        const currentPage = getCurrentPageFromSegment();
        const currentHtmPageId = currentPage?.htmPageId || currentPage?.HtmPageId;
        const currentNodePageId = currentPage?.nodePageId || currentPage?.NodePageId;
        const alreadyIncluded = dirtyPreScripts.some(p =>
          (currentHtmPageId && p.htmPageId === currentHtmPageId) ||
          (currentNodePageId && p.nodePageId === currentNodePageId)
        );
        console.log('📤 SAVE-BACKEND: current page dirty', { currentHtmPageId, currentNodePageId, alreadyIncluded, editedPreScript: JSON.stringify(editedPreScript) });
        if (!alreadyIncluded) {
          dirtyPreScripts.push({
            htmPageId: currentHtmPageId,
            preScript: editedPreScript,
            nodePageId: currentNodePageId
          });
        }
      }

      // ── Step 3: Flush current edit to adminSegment in-memory + local storage ─────
      if (preScriptDirty && adminState) {
        const saveResult = savePreScriptEdits(adminState.pageNum, editedPreScript);
        console.log('📤 SAVE-BACKEND: savePreScriptEdits result', saveResult, 'for editedPreScript=', JSON.stringify(editedPreScript));
        if (saveResult) {
          setPreScriptDirty(false);
        } else {
          console.error('📤 savePageLogicToBackend: ❌ Failed to flush current page edits to AdminSegment');
        }
      }

      console.log('📤 SAVE-BACKEND: final dirtyPreScripts', dirtyPreScripts.length, JSON.stringify(dirtyPreScripts.map(p => ({ htmPageId: p.htmPageId, preScript: JSON.stringify(p.preScript) }))));

      if (dirtyPreScripts.length === 0) {
        setSavePageLogicResult({ success: true, message: 'No changes to save' });
        setIsSavingPageLogic(false);
        return true;
      }

      console.log('📤 savePageLogicToBackend: Saving', dirtyPreScripts.length, 'dirty PreScripts to backend');

      // Prepare the service request according to C# ServiceRequestDto structure
      // Payload must match PageLogicPutRequestDto with PreScripts property containing PageLogicDto array
      const pageLogicPutRequest = {
        PreScripts: dirtyPreScripts // Array of PageLogicDto objects with htmPageId and preScript
      };

      const serviceRequest = {
        RequestType: 'page_logic_put',
        Payload: JSON.stringify(pageLogicPutRequest)
      };

      // Make the API call with automatic 401 retry handling
      let response;
      try {
        response = await apiService.processServiceRequestWithRefresh(serviceRequest, 'content', {
          userStore: userStoreHook,
          storage,
        });
      } catch (apiError) {
        console.error('📤 savePageLogicToBackend: ❌ API call threw an error:', apiError);
        throw apiError; // Re-throw to be caught by outer try-catch
      }
      
      if (response?.success) {
        console.log('📤 savePageLogicToBackend: ✅ Successfully saved PreScripts to backend');
        
        // Clear dirty flags for saved PreScripts
        if (adminSegment) {
          const updatedAdminSegment = { ...adminSegment };

          // Clear flat pages
          const pages = updatedAdminSegment.pages || updatedAdminSegment.Pages;
          if (pages && Array.isArray(pages)) {
            updatedAdminSegment.pages = pages.map((page: any) => {
              const htmPageId = page.htmPageId || page.HtmPageId;
              const savedPreScript = dirtyPreScripts.find(p => p.htmPageId === htmPageId);
              if (savedPreScript) {
                return {
                  ...page,
                  preScript: savedPreScript.preScript,
                  PreScript: savedPreScript.preScript,
                  editedPreScript: undefined
                };
              }
              return page;
            });
          }

          // Clear node pages
          const nodes = updatedAdminSegment.Nodes || updatedAdminSegment.nodes;
          if (nodes && Array.isArray(nodes)) {
            const updatedNodes = nodes.map((node: any) => {
              const nodePages: any[] = node.Pages || node.pages || [];
              const updatedNodePages = nodePages.map((page: any) => {
                const pageId = page.htmPageId || page.HtmPageId || page.nodePageId || page.NodePageId;
                const savedPreScript = dirtyPreScripts.find(p =>
                  (p.htmPageId && p.htmPageId === (page.htmPageId || page.HtmPageId)) ||
                  (p.nodePageId && p.nodePageId === (page.nodePageId || page.NodePageId))
                );
                if (savedPreScript) {
                  return {
                    ...page,
                    preScript: savedPreScript.preScript,
                    PreScript: savedPreScript.preScript,
                    editedPreScript: undefined
                  };
                }
                return page;
              });
              return { ...node, Pages: updatedNodePages, pages: updatedNodePages };
            });
            updatedAdminSegment.Nodes = updatedNodes;
            updatedAdminSegment.nodes = updatedNodes;
          }

          setAdminSegment(updatedAdminSegment);
        }

        // Clear local storage entries for every page that was just saved to the backend.
        // If we don't do this, the async storage-fallback in PAGE-CHANGE-EFFECT will reload
        // the old stale value and overwrite the in-memory edit (including blank clears).
        // Node segments use compound keys (node_${nodeId}_${rawPageId}) in storage.
        const savedPageStorageIds: string[] = [];
        dirtyPreScripts.forEach(p => {
          if (p.htmPageId) savedPageStorageIds.push(p.htmPageId);
          if (p.nodePageId) savedPageStorageIds.push(p.nodePageId);
        });
        // Also collect compound keys from node pages
        const nodesForClear = adminSegment?.Nodes || adminSegment?.nodes;
        if (nodesForClear && Array.isArray(nodesForClear)) {
          nodesForClear.forEach((node: any) => {
            const nodeId = node.nodeId || node.NodeId || node.Id || node.id;
            const nodePages: any[] = node.Pages || node.pages || [];
            nodePages.forEach((page: any) => {
              const rawPageId = page.htmPageId || page.HtmPageId || page.nodePageId || page.NodePageId;
              if (rawPageId && nodeId) {
                savedPageStorageIds.push(`node_${nodeId}_${rawPageId}`);
              }
            });
          });
        }
        if (savedPageStorageIds.length > 0) {
          // Mark storage entries as saved-to-backend rather than deleting them.
          // On reload, applyPreScriptEditsToAdminSegment treats these as the new baseline
          // (updates page.preScript) so they do not appear as pending dirty edits.
          markPreScriptEditsAsSaved(adminState?.segment || '', savedPageStorageIds);
        }
        
        // Update current page dirty state if it was saved
        if (adminState && preScriptDirty) {
          const currentPage = getCurrentPageFromSegment();
          const currentHtmPageId = currentPage?.htmPageId || currentPage?.HtmPageId;
          const currentNodePageId = currentPage?.nodePageId || currentPage?.NodePageId;
          const wasSaved = dirtyPreScripts.some(p =>
            (currentHtmPageId && p.htmPageId === currentHtmPageId) ||
            (currentNodePageId && p.nodePageId === currentNodePageId)
          );
          if (wasSaved) {
            setPreScriptDirty(false);
          }
        }
        
        setSavePageLogicResult({ 
          success: true, 
          message: `Successfully saved ${dirtyPreScripts.length} PreScript${dirtyPreScripts.length > 1 ? 's' : ''}` 
        });
        
        return true;
        
      } else {
        console.error('📤 savePageLogicToBackend: ❌ Failed to save PreScripts:', response);
        setSavePageLogicResult({ 
          success: false, 
          message: response?.message || 'Save failed - check console for details' 
        });
        
        return false;
      }
      
    } catch (error) {
      console.error('📤 savePageLogicToBackend: ❌ Error saving PreScripts:', error);
      setSavePageLogicResult({ 
        success: false, 
        message: 'Save error: ' + (error instanceof Error ? error.message : 'Unknown error') 
      });
      
      return false;
    } finally {
      setIsSavingPageLogic(false);
    }
  };

  // Function to collect dirty CalcScript from AdminSegment for backend sync
  const collectDirtyCalcScripts = (): CalcLogicDto[] => {
    if (!adminSegment) {
      return [];
    }

    const surveybId = adminSegment.surveybId || adminSegment.SurveybId;
    
    if (!surveybId) {
      return [];
    }

    const dirtyCalcScripts: CalcLogicDto[] = [];

    // Check if current segment has dirty CalcScript edits
    // nodeId is explicitly empty string for segment-level scripts (nodeId only set for node-level scripts)
    if (calcScriptDirty && editedCalcScript) {
      dirtyCalcScripts.push({
        nodeId: '',
        surveybId: surveybId,
        calcScript: editedCalcScript
      });
    }

    // Check if current node has dirty CalcScript edits (node segments only)
    const segType = String(adminSegment.SegmentType || adminSegment.segmentType || '');
    const isNodeSeg = segType === '5' || segType === 'PAS';
    if (isNodeSeg && nodeCalcScriptDirty && editedNodeCalcScript && adminState) {
      const nodeIdx = adminState.nodeIndex ?? 0;
      const nodes = adminSegment.Nodes || adminSegment.nodes;
      const currentNode = nodes?.[nodeIdx];
      if (currentNode) {
        const nodeId = currentNode.nodeId || currentNode.NodeId || String(nodeIdx);
        dirtyCalcScripts.push({
          nodeId: nodeId,
          surveybId: surveybId,
          calcScript: editedNodeCalcScript
        });
      }
    }

    return dirtyCalcScripts;
  };

  // Save dirty CalcScripts to backend using calc_logic_put
  const saveCalcLogicToBackend = async () => {
    if (!apiService) {
      console.error('📊 saveCalcLogicToBackend: No apiService available');
      setSaveCalcLogicResult({ success: false, message: 'Api service not available' });
      return false;
    }

    setIsSavingCalcLogic(true);
    setSaveCalcLogicResult(null);

    try {
      // Collect all dirty CalcScripts from AdminSegment BEFORE modifying it
      const dirtyCalcScripts = collectDirtyCalcScripts();
      
      if (dirtyCalcScripts.length === 0) {
        setSaveCalcLogicResult({ success: true, message: 'No changes to save' });
        return true;
      }

      // Now save current edits to AdminSegment in memory for consistency
      if (calcScriptDirty && editedCalcScript && adminSegment) {
        adminSegment.calcScript = editedCalcScript;
        adminSegment.CalcScript = editedCalcScript;
      }

      // Also save node CalcScript edits to the node object in memory
      const segTypeForSave = String(adminSegment?.SegmentType || adminSegment?.segmentType || '');
      const isNodeSegForSave = segTypeForSave === '5' || segTypeForSave === 'PAS';
      if (isNodeSegForSave && nodeCalcScriptDirty && editedNodeCalcScript && adminState && adminSegment) {
        const nodesForSave = adminSegment.Nodes || adminSegment.nodes;
        const nodeIdxForSave = adminState.nodeIndex ?? 0;
        const nodeForSave = nodesForSave?.[nodeIdxForSave];
        if (nodeForSave) {
          nodeForSave.calcScript = editedNodeCalcScript;
          nodeForSave.CalcScript = editedNodeCalcScript;
        }
      }

      console.log('📊 saveCalcLogicToBackend: Saving', dirtyCalcScripts.length, 'dirty CalcScript(s) to backend');
      
      // Create the request payload
      const requestPayload: CalcLogicPutRequestDto = {
        CalcScripts: dirtyCalcScripts
      };

      const serviceRequest = {
        RequestType: 'calc_logic_put',
        Payload: JSON.stringify(requestPayload)
      };

      let response;

      try {
        response = await apiService.processServiceRequestWithRefresh(serviceRequest, 'content', {
          userStore: userStoreHook,
          storage,
        });
      } catch (apiError) {
        console.error('📊 saveCalcLogicToBackend: ❌ API call threw an error:', apiError);
        throw apiError; // Re-throw to be caught by outer try-catch
      }
      
      if (response?.success) {
        console.log('📊 saveCalcLogicToBackend: ✅ Successfully saved CalcScripts to backend');
        
        // Clear dirty flags for saved segment CalcScript and update the in-memory source value
        // so the dirty comparison stays correct without relying on a stale content-cache value.
        if (adminSegment && calcScriptDirty && editedCalcScript) {
          adminSegment.calcScript = editedCalcScript;
          adminSegment.CalcScript = editedCalcScript;
          setCalcScriptDirty(false);
        }

        // Clear dirty flag for node CalcScript if it was saved
        const segType = String(adminSegment?.SegmentType || adminSegment?.segmentType || '');
        const isNodeSeg = segType === '5' || segType === 'PAS';
        if (isNodeSeg && nodeCalcScriptDirty && editedNodeCalcScript && adminState && adminSegment) {
          const nodeIdx = adminState.nodeIndex ?? 0;
          const nodes = adminSegment.Nodes || adminSegment.nodes;
          const currentNode = nodes?.[nodeIdx];
          if (currentNode) {
            const savedNodeId = currentNode.nodeId || currentNode.NodeId || String(nodeIdx);
            // Update the in-memory node object so the dirty comparison doesn't re-trigger
            currentNode.calcScript = editedNodeCalcScript;
            currentNode.CalcScript = editedNodeCalcScript;
            setNodeCalcScriptDirty(false);
            // Clear the persisted storage entry so remounting doesn't re-raise the dirty flag
            clearNodeCalcScriptEditsForNodeIds([savedNodeId]).catch(() => {});
          }
        }
        
        setSaveCalcLogicResult({ 
          success: true, 
          message: `Successfully saved ${dirtyCalcScripts.length} CalcScript${dirtyCalcScripts.length > 1 ? 's' : ''}` 
        });
        
        return true;
        
      } else {
        console.error('📊 saveCalcLogicToBackend: ❌ Failed to save CalcScripts:', response);
        setSaveCalcLogicResult({ 
          success: false, 
          message: response?.message || 'Save failed - check console for details' 
        });
        
        return false;
      }
      
    } catch (error) {
      console.error('📊 saveCalcLogicToBackend: ❌ Error saving CalcScripts:', error);
      setSaveCalcLogicResult({ 
        success: false, 
        message: 'Save error: ' + (error instanceof Error ? error.message : 'Unknown error') 
      });
      
      return false;
    } finally {
      setIsSavingCalcLogic(false);
    }
  };

  // Function to save final AdminState result to backend
  const saveAdminResultToBackend = async (sessionUser: any): Promise<AdminSubmitResponseDto | null> => {
    if (!apiService) {
      console.error('🏁 saveAdminResultToBackend: No apiService available');
      setSaveAdminResultResult({ success: false, message: 'Sync service not available' });
      return null;
    }

    if (!adminState) {
      console.error('🏁 saveAdminResultToBackend: No AdminState available');
      setSaveAdminResultResult({ success: false, message: 'No admin state available' });
      return null;
    }

    if (!sessionUser) {
      console.error('🏁 saveAdminResultToBackend: No sessionUser provided');
      setSaveAdminResultResult({ success: false, message: 'No session user available' });
      return null;
    }

    console.log('🏁 saveAdminResultToBackend: Starting final result save process');

    setIsSavingAdminResult(true);
    setSaveAdminResultResult(null);

    try {
      // Use the provided session user directly
      const userId = sessionUser.userId;
      const accountId = sessionUser.familyId; // UserInfoDto.AccountId mapped to AppUser.familyId
      const accessType = API_CONSTANTS.DEFAULT_ACCESS_TYPE;

      // Create a clean copy of AdminState without prefill and backTable.
      // Status is set to SUBMITTED in the payload so the backend receives the correct status.
      const cleanAdminState = {
        ...adminState,
        prefill: undefined,
        backTable: undefined,
        status: 'SUBMITTED',
        UserId: userId,
        AccountId: accountId,
        AccessType: accessType,
        ...extraSubmitFields,
      };

      // Convert segmentList from SurveybId to SurveyAliasId for backend compatibility
      if (cleanAdminState.segmentList && contentRecords.length > 0) {
        const originalSegmentList = cleanAdminState.segmentList.split(',');
        
        const convertedSegmentList = originalSegmentList.map((surveybId: string) => {
          // Find the corresponding ContentRecord for this segment
          const segmentRecord = contentRecords.find((record: ContentRecord) => 
            record.contentType === 'AdminSegment' && 
            record.contentTypeId === surveybId.trim()
          );
          
          if (segmentRecord && segmentRecord.contentBody) {
            try {
              // Parse the AdminSegment to get both SurveybId and SurveyAliasId
              const decodedContent = atob(segmentRecord.contentBody);
              const adminSegmentDto = JSON.parse(decodedContent) as AdminSegmentDto;
              
              const surveyAliasId = adminSegmentDto.SurveyAliasId || adminSegmentDto.SurveybId;
              return surveyAliasId;
            } catch (parseError) {
              console.warn(`⚠️ Failed to parse segment ${surveybId} for SurveyAliasId mapping:`, parseError);
              return surveybId; // Fallback to original
            }
          } else {
            console.warn(`⚠️ No ContentRecord found for SurveybId ${surveybId}, keeping original`);
            return surveybId; // Fallback to original
          }
        });
        
        cleanAdminState.segmentList = convertedSegmentList.join(',');
      }

      // For snapshot assessments (sourceId=4), prepend the fixed snapshot segment IDs.
      // adminState.sourceId holds the assessmentId set when the assessment was started.
      if (adminState.sourceId === '4') {
        const snapshotPrefix = '11255,11270,11272,';
        cleanAdminState.segmentList = snapshotPrefix + (cleanAdminState.segmentList || '');
        console.log('🏁 saveAdminResultToBackend: Snapshot (sourceId=4) — prepended snapshot segments:', cleanAdminState.segmentList);
      }

      // Remove the undefined properties completely
      delete (cleanAdminState as any).prefill;
      delete (cleanAdminState as any).backTable;

      // Stringify the clean AdminState for the service payload
      const adminStatePayload = JSON.stringify(cleanAdminState);

      const serviceRequest = {
        RequestType: 'admin_response_submit',
        Payload: adminStatePayload
      };

      let response;

      try {
        // Use apiService with 'data' ServiceType for admin response submission with offline support and 401 retry handling
        response = await apiService.processServiceRequestWithRefresh(serviceRequest, 'data', {
          userStore: userStoreHook,
          storage,
        });
      } catch (apiError) {
        console.error('🏁 saveAdminResultToBackend: ❌ Data service API call threw an error:', apiError);
        throw apiError; // Re-throw to be caught by outer try-catch
      }

      if (response?.success) {
        console.log('🏁 saveAdminResultToBackend: ✅ Successfully submitted AdminState to data service:', response);
        
        setSaveAdminResultResult({ 
          success: true, 
          message: 'Assessment results successfully submitted to data service' 
        });

        // Extract server-assigned identifiers (handles both PascalCase and camelCase serialisation)
        const submitResponse: AdminSubmitResponseDto = {
          RecId:         response.data?.RecId        || response.data?.recId        || '',
          RecUTM:        response.data?.RecUTM       || response.data?.recUTM       || '',
          ID:            response.data?.ID           || response.data?.id           || '',
          RecPatient:    response.data?.RecPatient   || response.data?.recPatient   || '',
          RecAssessment: response.data?.RecAssessment || response.data?.recAssessment || '',
        };
        console.log('🏁 saveAdminResultToBackend: Parsed AdminSubmitResponseDto:', submitResponse);
        return submitResponse;
        
      } else {
        console.error('🏁 saveAdminResultToBackend: ❌ Failed to submit AdminState to data service:', response);
        setSaveAdminResultResult({ 
          success: false, 
          message: response?.message || 'Data service submission failed - check console for details' 
        });
        
        return null;
      }
      
    } catch (error) {
      console.error('🏁 saveAdminResultToBackend: ❌ Error submitting AdminState to data service:', error);
      setSaveAdminResultResult({ 
        success: false, 
        message: 'Data service submission error: ' + (error instanceof Error ? error.message : 'Unknown error') 
      });
      
      return null;
    } finally {
      setIsSavingAdminResult(false);
    }
  };

  // Function to check if assessment is complete (last segment reached)
  const checkAssessmentCompletion = (): boolean => {
    if (!adminState || !availableSegments.length) {
      return false;
    }

    const currentSegmentId = adminState.segment;
    const lastSegmentId = availableSegments[availableSegments.length - 1];
    const isLastSegment = currentSegmentId === lastSegmentId;
    
    return isLastSegment;
  };

  // Function to save AdminState to local storage as AdminResponseDto
  // Returns the local recId (a temp ID when adminState.recId is blank) plus the full
  // AdminResponseDto that was saved, or null on failure. The caller uses the recId to update
  // the record after backend confirms, and can hand the AdminResponseDto itself to a report
  // viewer immediately — it needs no server-assigned recId to be usable.
  const saveAdminResultToLocal = async (sessionUser: any): Promise<{ localId: string; adminResponse: AdminResponseDto } | null> => {
    if (!adminResponseStorage || !adminState) {
      console.error('💾 saveAdminResultToLocal: No adminResponseStorage or adminState available');
      return null;
    }

    if (!sessionUser) {
      console.error('💾 saveAdminResultToLocal: No sessionUser provided');
      return null;
    }

    try {
      // Use the existing recId if already confirmed (e.g. resuming an assessment that was
      // previously submitted). Otherwise generate a client-side temporary ID so the record
      // can be located and updated once the backend confirms with the real RecId.
      const localId = adminState.recId || `local_${adminState.profileId}_${Date.now()}`;

      // Create AdminResponseDto with field mappings as specified
      // Status is explicitly set to ADMIN_COMPLETE: the local save happens during
      // handleDoneNavigation after the state has been set to ADMIN_COMPLETE, but
      // adminState.status hasn't been committed to the stored object yet at this
      // point in the call chain, so we stamp it here rather than relying on the
      // current in-memory value (which may still read IN_PROGRESS).
      const adminResponse: AdminResponseDto = {
        recId: localId,
        recStatus: 'ADMIN_COMPLETE',
        recDate: adminState.modified, // RecDate = Modified
        recClient: '',
        recPractice: sessionUser.familyId, // RecPractice = AccountId (UserInfoDto.AccountId)
        recSite: '',
        recStation: sessionUser.userId, // recStation = UserId (UserInfoDto.SraId)
        recSN: adminState.segment || '1',
        recPeriod: '',
        recWeek: '',
        recSurvey: adminState.sourceId, // RecSurvey = SourceId (legacy local field)
        recAssessment: adminState.sourceId, // RecAssessment = SourceId (C# field)
        surveybList: adminState.segmentList, // SurveybList = SegmentList (C# field)
        recAlias: adminState.surveyAliasId || adminState.sourceId || 'ASSESSMENT',
        id: adminState.profileId, // ID = profileId
        seq_Num: adminState.pageNum || '1',
        recstate: 'ADMIN_COMPLETE',
        recYear: new Date().getFullYear().toString(),
        recQtr: Math.ceil((new Date().getMonth() + 1) / 3).toString(),
        recMon: (new Date().getMonth() + 1).toString().padStart(2, '0'),
        recDay: new Date().getDate().toString().padStart(2, '0'),
        duration: String(Math.max(0, Math.round((Date.now() - sessionStartTimeRef.current) / 1000))), // seconds, this sitting only
        recFirst: 'N',
        recProvider: 'Self-Assessment',
        recDepartment: '',
        recSpecialty: '',
        recGroup: '',
        recLTM: '',
        recUTM: adminState.modified, // RecUTM = Modified
        id_RC: '',
        recLang: adminState.languageTypeId, // RecLang = LanguageTypeId
        surveyAliasList: adminState.segmentList, // SurveyAliasList = SegmentList
        recPatient: adminState.profileId,
        // Snapshot of the profile's PHI as captured on AdminStateDto for THIS administration —
        // not necessarily the same as whatever the profile's PHI reads as later (e.g. edited
        // afterward), so this is the authoritative record for what ReportView's Debug
        // Information PHI list should show for this specific response.
        phi: adminState.phi,
        // ResponseData array = Data array with field mapping
        responseData: adminState.data?.map((dataItem) => ({
          name: dataItem.name, // AdminResponseDataDto.Name = AdminDataDto.Name
          value: dataItem.value.toString() // AdminResponseDataDto.Value = AdminDataDto.Value
        })) || []
      };

      // Save to local storage
      const success = await adminResponseStorage.saveAdminResponse(
        adminState.profileId,
        adminResponse
      );

      if (success) {
        console.log('✅ saveAdminResultToLocal: AdminResponse saved to local storage successfully, localId:', localId);
        return { localId, adminResponse };
      } else {
        console.error('❌ saveAdminResultToLocal: Failed to save AdminResponse to local storage');
        return null;
      }
    } catch (error) {
      console.error('❌ saveAdminResultToLocal: Error saving AdminResponse to local storage:', error);
      return null;
    }
  };

  // Function to handle Done navigation (called when assessment is complete)
  const handleDoneNavigation = async (): Promise<void> => {
    console.log('🏁 handleDoneNavigation: Done button clicked');

    // Check for any completed CalcScript updates from background processing before proceeding
    await applyPendingCalcScriptUpdates();

    if (!adminState) {
      console.error('🏁 handleDoneNavigation: ❌ No AdminState available for submission');
      throw new Error('No AdminState available for submission');
    }

    const isComplete = checkAssessmentCompletion();
    if (!isComplete) {
      throw new Error('checkAssessmentCompletion() returned false');
    }

    try {
      // ── Step 1: Mark ADMIN_COMPLETE and persist (fast, local) ──────────────
      const adminCompleteState: AdminStateDto = { ...adminState, status: 'ADMIN_COMPLETE' };
      await adminStateStorage!.saveAdminState(adminCompleteState);
      setAdminState(adminCompleteState);
      console.log('🏁 handleDoneNavigation: Status set to ADMIN_COMPLETE');

      // ── Step 2: Save local AdminResponseDto (fast, local — works offline) ──
      const sessionUser = await restoreSessionUser();
      console.log('💾 handleDoneNavigation: Saving to local storage');
      const localResult = await saveAdminResultToLocal(sessionUser);

      if (!localResult) {
        console.error('🏁 handleDoneNavigation: ❌ Local save failed — aborting');
        throw new Error('Local save failed');
      }
      const { localId, adminResponse } = localResult;

      // ── Step 3: Hand off immediately — do NOT wait for backend submission ──
      // A respondent must not wait on a network round-trip to see their report (if the
      // closing-segment PreScript set renderReport). The locally-built AdminResponseDto
      // already has everything a report needs (no server recId required). Backend
      // submission continues below, in the background, after this hand-off.
      setIsAssessmentComplete(true);
      if (onComplete) onComplete(adminCompleteState, adminResponse);

      // The backend-submission handshake — fired exactly once, from whichever terminal
      // branch of the background task below is reached first. Redirect-based callers await
      // this (via onBackendSubmitSettled) before navigating away, so the fire-and-forget
      // admin_response_submit isn't aborted mid-flight by the page unload.
      let submitSettled = false;
      const settleBackendSubmit = (result: AdminBackendSubmitResult) => {
        if (submitSettled) return;
        submitSettled = true;
        try {
          onBackendSubmitSettled?.(result);
        } catch (cbError) {
          console.warn('🏁 handleDoneNavigation(bg): ⚠️ onBackendSubmitSettled threw:', cbError);
        }
      };

      // ── Step 4: Background backend submission ───────────────────────────────
      // Fire-and-forget from this function's perspective — NOT awaited, so onComplete has
      // already fired above. AdminView is typically unmounted shortly after (the caller
      // usually navigates away immediately on completion), so this continuation only
      // touches durable storage — never component state — making it safe to keep running
      // after unmount without triggering React "state update on unmounted component"
      // warnings. The single call the redirect handshake depends on, settleBackendSubmit,
      // only resolves a promise / sets state on the still-mounted parent.
      void (async () => {
        try {
          const canSubmitToBackend = !!apiService && !!sessionUser?.familyId;

          if (!canSubmitToBackend) {
            // No account / offline — leave as ADMIN_COMPLETE for background retry
            console.log('🏁 handleDoneNavigation(bg): No backend available — remaining as ADMIN_COMPLETE for retry');
            settleBackendSubmit({ submitted: false, response: null });
            return;
          }

          // Mark SUBMITTED before sending so the payload carries the correct status
          const submittedState: AdminStateDto = { ...adminCompleteState, status: 'SUBMITTED' };
          await adminStateStorage!.saveAdminState(submittedState);
          console.log('🏁 handleDoneNavigation(bg): Status set to SUBMITTED — sending to backend');

          const serverResponse = await saveAdminResultToBackend(sessionUser);

          if (serverResponse) {
            console.log('🏁 handleDoneNavigation(bg): ✅ Backend submission succeeded');
            // Release the redirect handshake as soon as the backend has acknowledged the
            // submission — the local bookkeeping below doesn't need to gate navigation.
            settleBackendSubmit({ submitted: true, response: serverResponse });

            // Update local AdminResponseDto with server-assigned identifiers
            if (adminResponseStorage && serverResponse.RecId) {
              await adminResponseStorage.updateResponseRecId(adminCompleteState.profileId, localId, serverResponse);
              console.log('🏁 handleDoneNavigation(bg): Local record updated with server RecId:', serverResponse.RecId);
            }

            // Set SUBMIT_SUCCESS on AdminStateDto and persist
            const successState: AdminStateDto = {
              ...submittedState,
              status: 'SUBMIT_SUCCESS',
              recId: serverResponse.RecId || submittedState.recId,
            };
            await adminStateStorage!.saveAdminState(successState);
            console.log('🏁 handleDoneNavigation(bg): Status set to SUBMIT_SUCCESS');

            // Remove AdminStateDto from storage now that the local AdminResponseDto is the
            // canonical record. This keeps storage clean; background retry has nothing left to do.
            try {
              await adminStateStorage!.deleteAdminState(successState.profileId, successState.sourceId, successState.recId);
              console.log('🏁 handleDoneNavigation(bg): AdminStateDto removed from storage after SUBMIT_SUCCESS');
            } catch (deleteErr) {
              // Non-fatal — the record will be orphaned but won't cause incorrect retries
              console.warn('🏁 handleDoneNavigation(bg): ⚠️ Could not delete AdminStateDto after success:', deleteErr);
            }
          } else {
            console.error('🏁 handleDoneNavigation(bg): ❌ Backend submission failed — marking SUBMIT_FAILED for retry');

            const failedState: AdminStateDto = { ...submittedState, status: 'SUBMIT_FAILED' };
            await adminStateStorage!.saveAdminState(failedState);
            console.log('🏁 handleDoneNavigation(bg): Status set to SUBMIT_FAILED (local record intact for retry)');
            settleBackendSubmit({ submitted: false, response: null });
          }
        } catch (bgError) {
          console.error('🏁 handleDoneNavigation(bg): ❌ Error during background submission:', bgError);
        } finally {
          // Safety net — never leave a redirect-based caller waiting on a handshake that
          // an unexpected throw skipped. No-op if a branch above already settled.
          settleBackendSubmit({ submitted: false, response: null });
        }
      })();

    } catch (error) {
      console.error('🏁 handleDoneNavigation: ❌ Error during local completion:', error);
      throw error;
    }
  };

  // PreScript persistence functions for app restart/reload survival
  const getPreScriptStorageKey = (assessmentId: string, languageId: string) => {
    return `prescript_edits_${assessmentId}_${languageId}`;
  };

  // The edits blob above is shared across every segment of this assessment (keyed only by
  // assessmentId+languageId), but htmPageId (and the node_{nodeId}_{rawPageId} composite) is
  // only unique WITHIN a segment — two different segments can and do reuse the same page
  // numbering. Every read/write into that blob must therefore key each entry by segment too,
  // or edits for one segment silently collide with/overwrite another's identically-numbered
  // page. This composes the segment into the entry key consistently everywhere.
  const getPreScriptEditEntryKey = (segmentId: string, pageId: string) => `${segmentId}_${pageId}`;

  // Save PreScript edits to storage (persists across app restarts)
  const savePreScriptEditsToStorage = async (segmentId: string, htmPageId: string, editedPreScript: string) => {
    if (!storage) return;

    try {
      const storageKey = getPreScriptStorageKey(assessmentId, languageId);
      const existingEdits = await storage.getItem(storageKey);
      const editsData = existingEdits ? JSON.parse(existingEdits) : {};

      // Store the edited PreScript by segment-scoped htmPageId (see getPreScriptEditEntryKey)
      editsData[getPreScriptEditEntryKey(segmentId, htmPageId)] = {
        editedPreScript,
        timestamp: new Date().toISOString(),
        assessmentId,
        languageId
      };

      await storage.setItem(storageKey, JSON.stringify(editsData));
    } catch (error) {
      console.error('💾 savePreScriptEditsToStorage: Error saving to storage:', error);
    }
  };

  // Load PreScript edits from storage
  const loadPreScriptEditsFromStorage = async () => {
    if (!storage) return {};
    
    try {
      const storageKey = getPreScriptStorageKey(assessmentId, languageId);
      const existingEdits = await storage.getItem(storageKey);
      if (existingEdits) {
        const editsData = JSON.parse(existingEdits);
        return editsData;
      }
    } catch (error) {
      console.error('💾 loadPreScriptEditsFromStorage: Error loading from storage:', error);
    }
    
    return {};
  };

  // Clear PreScript edits from storage (called when new AdminSegment content is loaded)
  const clearPreScriptEditsFromStorage = async () => {
    if (!storage) return;
    
    try {
      const storageKey = getPreScriptStorageKey(assessmentId, languageId);
      await storage.removeItem(storageKey);
      console.log('💾 clearPreScriptEditsFromStorage: Cleared PreScript edits from storage:', storageKey);
    } catch (error) {
      console.error('💾 clearPreScriptEditsFromStorage: Error clearing storage:', error);
    }
  };

  // After a successful backend save, mark the storage entries as already-persisted so that on
  // the next load applyPreScriptEditsToAdminSegment can use them as the new baseline (updating
  // page.preScript) rather than showing them as pending edits.
  const markPreScriptEditsAsSaved = async (segmentId: string, pageIds: string[]) => {
    if (!storage || pageIds.length === 0) return;
    try {
      const storageKey = getPreScriptStorageKey(assessmentId, languageId);
      const existing = await storage.getItem(storageKey);
      if (!existing) return;
      const editsData = JSON.parse(existing);
      let changed = false;
      pageIds.forEach(id => {
        const key = getPreScriptEditEntryKey(segmentId, id);
        if (editsData[key]) {
          editsData[key] = { ...editsData[key], savedToBackend: true };
          changed = true;
        }
      });
      if (changed) {
        await storage.setItem(storageKey, JSON.stringify(editsData));
        console.log('💾 markPreScriptEditsAsSaved: Marked', pageIds.length, 'entry(ies) as savedToBackend');
      }
    } catch (error) {
      console.error('💾 markPreScriptEditsAsSaved: Error:', error);
    }
  };

  // Clear only the PreScript edits belonging to a specific set of page IDs within one segment.
  // Used when fresh segment content is detected — prevents wiping edits from OTHER segments
  // (including another segment's page that happens to share the same bare page id).
  const clearPreScriptEditsForPageIds = async (segmentId: string, pageIds: string[]) => {
    if (!storage || pageIds.length === 0) return;
    try {
      const storageKey = getPreScriptStorageKey(assessmentId, languageId);
      const existing = await storage.getItem(storageKey);
      if (!existing) return;
      const editsData = JSON.parse(existing);
      let changed = false;
      pageIds.forEach(id => {
        const key = getPreScriptEditEntryKey(segmentId, id);
        if (editsData[key]) {
          delete editsData[key];
          changed = true;
        }
      });
      if (changed) {
        await storage.setItem(storageKey, JSON.stringify(editsData));
        console.log('💾 clearPreScriptEditsForPageIds: Cleared edits for', pageIds.length, 'page(s)');
      }
    } catch (error) {
      console.error('💾 clearPreScriptEditsForPageIds: Error:', error);
    }
  };

  // Extract all page IDs (htmPageId / nodePageId) from raw AdminSegment content.
  // Used to scope edit-clearing to only the pages belonging to a freshly-downloaded segment.
  const extractPageIdsFromAdminSegmentContent = async (adminContent: ContentRecord): Promise<string[]> => {
    const pageIds: string[] = [];
    try {
      let adminSegmentData: any = null;

      if (adminContent.tableData) {
        let parsedData: any = adminContent.tableData;
        if (typeof parsedData === 'string') parsedData = JSON.parse(parsedData);

        let encodedContent: string | null = null;
        if (parsedData.Tables && Array.isArray(parsedData.Tables)) {
          const contentTable = parsedData.Tables.find((t: any) => t.Name === 'content');
          if (contentTable?.Data?.Columns?.includes('content_body')) {
            const idx = contentTable.Data.Columns.indexOf('content_body');
            encodedContent = contentTable.Data.Rows[0]?.[idx] ?? null;
          }
        } else if (parsedData.Name === 'content' && parsedData.Data?.Columns?.includes('content_body')) {
          const idx = parsedData.Data.Columns.indexOf('content_body');
          encodedContent = parsedData.Data.Rows[0]?.[idx] ?? null;
        }

        if (encodedContent) {
          adminSegmentData = JSON.parse(atob(encodedContent));
        }
      } else if ((adminContent as any).contentBody) {
        adminSegmentData = JSON.parse(atob((adminContent as any).contentBody));
      }

      if (adminSegmentData) {
        const pages = adminSegmentData.pages || adminSegmentData.Pages;
        if (Array.isArray(pages)) {
          pages.forEach((p: any) => {
            const id = p.htmPageId || p.HtmPageId;
            if (id) pageIds.push(String(id));
          });
        }
        const nodes = adminSegmentData.Nodes || adminSegmentData.nodes;
        if (Array.isArray(nodes)) {
          nodes.forEach((node: any) => {
            const nodeId = node.nodeId || node.NodeId || node.Id || node.id;
            const nodePages = node.Pages || node.pages;
            if (Array.isArray(nodePages)) {
              nodePages.forEach((p: any) => {
                const rawId = p.htmPageId || p.HtmPageId || p.nodePageId || p.NodePageId;
                if (rawId) {
                  const id = nodeId ? `node_${nodeId}_${rawId}` : String(rawId);
                  pageIds.push(id);
                }
              });
            }
          });
        }
      }
    } catch (e) {
      // Ignore parse errors — caller will fall back to no-op (no pages to clear)
    }
    return pageIds;
  };

  // Apply loaded PreScript edits to AdminSegment
  const applyPreScriptEditsToAdminSegment = (adminSegmentData: any, savedEdits: any, segmentId: string) => {

    if (!savedEdits || Object.keys(savedEdits).length === 0) {
      return adminSegmentData;
    }

    const updatedAdminSegment = { ...adminSegmentData };

    // Apply to flat pages
    const pages = updatedAdminSegment.pages || updatedAdminSegment.Pages;
    if (pages && Array.isArray(pages)) {
      updatedAdminSegment.pages = pages.map((page: any) => {
        const pageId = page.htmPageId || page.HtmPageId;
        const editKey = pageId ? getPreScriptEditEntryKey(segmentId, pageId) : undefined;
        if (editKey && savedEdits[editKey]) {
          const edit = savedEdits[editKey];
          // When a backend save previously flagged this entry, promote the stored value to
          // the new baseline so it does not appear dirty on the next load.
          if (edit.savedToBackend) {
            return { ...page, preScript: edit.editedPreScript, PreScript: edit.editedPreScript, editedPreScript: undefined };
          }
          return { ...page, editedPreScript: edit.editedPreScript };
        }
        return page;
      });
    }

    // Apply to node pages
    const nodes = updatedAdminSegment.Nodes || updatedAdminSegment.nodes;
    if (nodes && Array.isArray(nodes)) {
      const updatedNodes = nodes.map((node: any) => {
        const nodeId = node.nodeId || node.NodeId || node.Id || node.id;
        const nodePages: any[] = node.Pages || node.pages || [];
        const updatedNodePages = nodePages.map((page: any) => {
          const rawPageId = page.htmPageId || page.HtmPageId
            || page.nodePageId || page.NodePageId;
          const pageId = (rawPageId && nodeId) ? `node_${nodeId}_${rawPageId}` : rawPageId;
          const editKey = pageId ? getPreScriptEditEntryKey(segmentId, pageId) : undefined;
          if (editKey && savedEdits[editKey]) {
            const edit = savedEdits[editKey];
            if (edit.savedToBackend) {
              return { ...page, preScript: edit.editedPreScript, PreScript: edit.editedPreScript, editedPreScript: undefined };
            }
            return { ...page, editedPreScript: edit.editedPreScript };
          }
          return page;
        });
        return { ...node, Pages: updatedNodePages, pages: updatedNodePages };
      });
      updatedAdminSegment.Nodes = updatedNodes;
      updatedAdminSegment.nodes = updatedNodes;
    }

    if (!pages && !nodes) {
      return adminSegmentData;
    }

    return updatedAdminSegment;
  };

  // Helper function to save preScript edits to AdminSegment for persistence across navigation
  const savePreScriptEdits = (pageNum: string, editedPreScript: string) => {
    console.log('💾 savePreScriptEdits: called', { pageNum, editedPreScript: JSON.stringify(editedPreScript), hasAdminSegment: !!adminSegment });

    if (!adminSegment) {
      console.warn('💾 savePreScriptEdits: no adminSegment, returning false');
      return false;
    }

    try {
      const segType = String(adminSegment.SegmentType || adminSegment.segmentType || '');
      const isNodeSeg = segType === '5' || segType === 'PAS';

      if (isNodeSeg) {
        // Node segment: find page in Nodes[nodeIndex].Pages[] by PageNum — same as flat
        const nodes = adminSegment.Nodes || adminSegment.nodes;
        if (!nodes || !Array.isArray(nodes)) return false;
        const nodeIdx = adminState?.nodeIndex ?? 0;
        const nodePages: any[] = (nodes[nodeIdx]?.Pages || nodes[nodeIdx]?.pages) || [];
        const pageIndex = nodePages.findIndex((p: any) => String(p.PageNum || p.pageNum || '') === String(pageNum));
        if (pageIndex < 0 || pageIndex >= nodePages.length) return false;

        const updatedAdminSegment = { ...adminSegment };
        const updatedNodes = nodes.map((node: any, ni: number) => {
          if (ni !== nodeIdx) return node;
          const updatedPages = [...nodePages];
          updatedPages[pageIndex] = { ...nodePages[pageIndex], editedPreScript };
          return { ...node, Pages: updatedPages, pages: updatedPages };
        });
        updatedAdminSegment.Nodes = updatedNodes;
        updatedAdminSegment.nodes = updatedNodes;
        setAdminSegment(updatedAdminSegment);

        const originalPage = nodePages[pageIndex];
        const nodeObject = nodes[nodeIdx];
        const nodeId = nodeObject?.nodeId || nodeObject?.NodeId || nodeObject?.Id || nodeObject?.id || String(nodeIdx);
        const rawPageId = originalPage.htmPageId || originalPage.HtmPageId
          || originalPage.nodePageId || originalPage.NodePageId;
        const pageStorageId = rawPageId ? `node_${nodeId}_${rawPageId}` : undefined;
        if (pageStorageId) {
          savePreScriptEditsToStorage(adminState?.segment || '', pageStorageId, editedPreScript);
        } else {
          console.warn('🔧 savePreScriptEdits: No htmPageId or nodePageId found in node page, cannot save to storage');
        }
        return true;
      }

      // Flat segment: find page in Pages[]
      const pages = adminSegment.pages || adminSegment.Pages;
      if (!pages || !Array.isArray(pages)) {
        return false;
      }

      // Find the page to update
      const pageIndex = pages.findIndex((p: any) => (p.pageNum || p.PageNum) === pageNum);
      if (pageIndex === -1) {
        return false;
      }

      // Create updated AdminSegment with the saved preScript edit
      const updatedAdminSegment = { ...adminSegment };
      updatedAdminSegment.pages = [...pages];
      const originalPage = pages[pageIndex];
      updatedAdminSegment.pages[pageIndex] = {
        ...originalPage,
        editedPreScript: editedPreScript // Save the edited version
      };

      // Update the AdminSegment state
      setAdminSegment(updatedAdminSegment);
      
      // Also save to storage for persistence across app restarts
      const htmPageId = originalPage.htmPageId || originalPage.HtmPageId;
      if (htmPageId) {
        savePreScriptEditsToStorage(adminState?.segment || '', htmPageId, editedPreScript);
      } else {
        console.warn('🔧 savePreScriptEdits: No htmPageId found, cannot save to storage');
      }
      
      return true;
    } catch (error) {
      console.error('🔧 savePreScriptEdits: ❌ Error saving preScript edits:', error);
      return false;
    }
  };

  // Ref to track if initial page evaluation has been performed
  const hasPerformedInitialEvaluation = useRef(false);

  // Set right before goToNextPage's NEXT_SEGMENT block returns (see the comment there) —
  // signals to the "Perform initial page evaluation" effect below that adminSegment just
  // changed because of a forward segment transition (not a resume/BackTable jump into a
  // later segment), so it should evaluate the new segment's first page unconditionally,
  // not just when that segment happens to be the assessment's first one.
  const justAdvancedSegmentRef = useRef(false);

  // Navigation lock — prevents two concurrent goToNextPage calls from racing against
  // each other through shared storage.  One call must complete (or fail) before the
  // next is allowed to start.
  const isNavigatingRef = useRef(false);

  // Ref to track if editedPreScript has been initialized for the current page (prevents
  // the init useEffect from overwriting a user-cleared (empty) PreScript with the original)
  const preScriptInitialized = useRef(false);

  // Increment each time PAGE-CHANGE-EFFECT fires so stale storage-fallback .then() callbacks
  // can detect they are obsolete and discard their results.
  const preScriptStorageGenRef = useRef(0);
  // Tracks the effective preScript value that was loaded when the page was entered (may differ
  // from raw page.preScript when adminSegment has a saved edit). Used as dirty baseline.
  const pageEntryPreScriptRef = useRef<string>('');
  // Mirror of editedPreScript state kept always up-to-date so async callbacks (nav flush,
  // auto-save timer) read the current value instead of a stale closure.
  const editedPreScriptRef = useRef<string>('');
  // Wrapper: update both state and the live ref together.
  const setEditedPreScriptWithRef = (value: string) => {
    editedPreScriptRef.current = value;
    setEditedPreScript(value);
  };

  // Mirror of editedNodeCalcScript state — the nodeCalcScript useEffect sets this via an async
  // .then() callback, so goToNextPage closures may capture a stale React state value.  The ref
  // is always current and is used by navigation code instead of the state variable.
  const editedNodeCalcScriptRef = useRef<string>('');
  // Wrapper: update both the live ref and the React state together.
  const setEditedNodeCalcScriptWithRef = (value: string) => {
    editedNodeCalcScriptRef.current = value;
    setEditedNodeCalcScript(value);
  };

  // Storage key for node CalcScript edits (keyed by nodeId, scoped to assessmentId+languageId)
  const getNodeCalcScriptStorageKey = (asmId: string, langId: string) =>
    `node_calcscript_edits_${asmId}_${langId}`;

  // Persist an edited node CalcScript (keyed by nodeId)
  const saveNodeCalcScriptToStorage = async (nodeId: string, editedScript: string) => {
    if (!storage) return;
    try {
      const key = getNodeCalcScriptStorageKey(assessmentId, languageId);
      const existing = await storage.getItem(key);
      const data = existing ? JSON.parse(existing) : {};
      data[nodeId] = { editedCalcScript: editedScript, timestamp: new Date().toISOString() };
      await storage.setItem(key, JSON.stringify(data));
      console.log('💾 saveNodeCalcScriptToStorage: saved for nodeId', nodeId);
    } catch (e) {
      console.error('💾 saveNodeCalcScriptToStorage: error', e);
    }
  };

  // Load all persisted node CalcScript edits
  const loadNodeCalcScriptEditsFromStorage = async (): Promise<Record<string, string>> => {
    if (!storage) return {};
    try {
      const key = getNodeCalcScriptStorageKey(assessmentId, languageId);
      const existing = await storage.getItem(key);
      if (existing) {
        const data = JSON.parse(existing);
        const result: Record<string, string> = {};
        Object.keys(data).forEach(k => { result[k] = data[k].editedCalcScript || ''; });
        return result;
      }
    } catch (e) {
      console.error('💾 loadNodeCalcScriptEditsFromStorage: error', e);
    }
    return {};
  };

  // Clear node CalcScript edits for a specific set of nodeIds (used when fresh content detected)
  const clearNodeCalcScriptEditsForNodeIds = async (nodeIds: string[]) => {
    if (!storage || nodeIds.length === 0) return;
    try {
      const key = getNodeCalcScriptStorageKey(assessmentId, languageId);
      const existing = await storage.getItem(key);
      if (!existing) return;
      const data = JSON.parse(existing);
      let changed = false;
      nodeIds.forEach(id => { if (data[id]) { delete data[id]; changed = true; } });
      if (changed) await storage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.error('💾 clearNodeCalcScriptEditsForNodeIds: error', e);
    }
  };

  // Cache for processed content to prevent redundant base64 decoding
  const processedContentCache = useRef<Map<string, any>>(new Map());

  // Parallel to processedContentCache — stores the AdminSegment WITH saved PreScript edits applied.
  // Used for segmentOverrideForNavRef so edited PreScripts carry through async navigation loops
  // even before React state settles (processedContentCache intentionally stores the clean version).
  const processedContentWithEditsCacheRef = useRef<Map<string, any>>(new Map());

  // When goToPreviousPage loads a different segment, adminSegment React state is still the
  // old segment because setAdminSegment hasn't re-rendered yet.  This ref lets us supply
  // the freshly-cached segment directly to getCurrentPageFromSegment for that one call.
  const segmentOverrideForNavRef = useRef<any>(null);

  // Runtime substitution overrides populated synchronously from the current page's PreScript
  // putPreload() calls.  Checked first in performPreloadSubstitutions Pass 1 (<<xxxx>>) so
  // the substituted value is visible on the same render that shows the page — no reliance on
  // React batching a setAdminSegment update.  Cleared at the start of each page evaluation.
  const runtimePreloadOverridesRef = useRef<Map<string, string>>(new Map());

  // Content tables
  const [contentTables, setContentTables] = useState<{
    pages?: QuickAccessTable;
    questions?: QuickAccessTable;
    answers?: QuickAccessTable;
    questionList?: QuickAccessTable;
  }>({});

  // Render AdminStateDto-based assessment (simplified interface)
  // renderAdminStateDtoAssessment function removed - no longer using hardcoded 3-question fallback
  // AdminView now uses loadAdminSegment to ensure proper content is available or shows "no questions found"

  // Initialize AdminStateStorage
  useEffect(() => {
    if (storage) {
      const stateStorage = new AdminStateStorage(storage);
      setAdminStateStorage(stateStorage);
      
      // Create separate storage instance for CalcScript operations with different key prefix
      const calcStorage = new AdminStateStorage(storage);
      setCalcScriptStorage(calcStorage);
      
      // Initialize AdminResponseStorage for saving completed assessments
      const responseStorage = new AdminResponseStorage(storage);
      setAdminResponseStorage(responseStorage);
      
    }
  }, [storage]);

  // Load debug info state from storage
  useEffect(() => {
    const loadDebugState = async () => {
      if (!storage) return;
      try {
        const debugState = await storage.getItem('debug_show_info');
        if (debugState === 'true') {
          setDebugInfoEnabled(true);
        }
        
        const editPageLogicState = await storage.getItem('debug_edit_page_logic');
        const editCalcLogicState = await storage.getItem('debug_edit_calc_logic');
        const wantsPageLogicEdit = editPageLogicState === 'true';
        const wantsCalcLogicEdit = editCalcLogicState === 'true';

        if (wantsPageLogicEdit) {
          setEditPageLogicEnabled(true);
        }
        if (wantsCalcLogicEdit) {
          setEditCalcLogicEnabled(true);
        }
      } catch (error) {
        console.error('🐛 AdminView: Failed to load debug state:', error);
      }
    };
    loadDebugState();
  }, [storage]);

  // Account uvar config for sessions whose AdminStateDto.uVars is empty — a session resumed from
  // a snapshot saved before uVars existed (or before the account configured any), or one whose
  // creation resolved nothing. adminState.uVars still wins whenever it has entries (that's the
  // token-flow value from the launch/PLA, which respondents can't re-resolve locally).
  const [accountUVarsFallback, setAccountUVarsFallback] = useState<any[]>([]);
  const stateUVarCount = adminState?.uVars?.length ?? 0;
  const stateSourceId = adminState?.sourceId;
  useEffect(() => {
    if (stateUVarCount > 0 || !adminStateStorage || !accountId || !stateSourceId) {
      return;
    }
    let cancelled = false;
    void adminStateStorage.resolveAccountUVars(accountId, stateSourceId).then((resolved) => {
      if (!cancelled) setAccountUVarsFallback(resolved);
    });
    return () => { cancelled = true; };
  }, [stateUVarCount, adminStateStorage, accountId, stateSourceId]);
  const effectiveUVars: any[] = stateUVarCount > 0 ? (adminState?.uVars as any[]) : accountUVarsFallback;

  // Logs once per UvarTypeId when a uvar question can't find its account config and falls back
  // to the full content answer set — the console line names what was (and wasn't) available.
  const warnedUvarMissesRef = useRef<Set<string>>(new Set());
  const warnUvarFallback = (uvarTypeId: string, mvarName: string) => {
    if (warnedUvarMissesRef.current.has(uvarTypeId)) return;
    warnedUvarMissesRef.current.add(uvarTypeId);
    console.warn(
      `AdminView: no account uvar config matches question "${mvarName}" (UvarTypeId "${uvarTypeId}") — ` +
      `showing the full content answer set. Available uvars: ` +
      JSON.stringify(effectiveUVars.map((u) => `${u.MvarName ?? u.mvarName}=${u.UvarTypeId ?? u.uvarTypeId}`)) +
      ` (source: ${stateUVarCount > 0 ? 'adminState.uVars' : 'local account settings'})`
    );
  };

  // Helper function to get current answer value by question ID - checks Data then Prefill
  const getCurrentAnswerValue = (questionId: string | number): number | string | undefined => {
    if (!adminState || !adminStateStorage) return undefined;
    
    // Use the MVar name directly - questionId should be the MVar name
    const mvarName = questionId.toString();
    
    // Get answer from AdminStateStorage which checks Data first, then Prefill
    const result = adminStateStorage.getAnswerData(adminState, mvarName, adminState.segment);
    return result || undefined;
  };

  // Initialize AdminState immediately if initialAdminState is provided
  useEffect(() => {
    if (initialAdminState) {
      // Sanitize pageNum: the string 'undefined' can be persisted by data corruption bugs;
      // treat it as missing and reset to '1' so page lookup succeeds.
      const sanitizedInitialState = (!initialAdminState.pageNum || initialAdminState.pageNum === 'undefined')
        ? { ...initialAdminState, pageNum: '1', nodeIndex: 0 }
        : initialAdminState;
      setAdminState(sanitizedInitialState);
      
      // Parse segment list to get available segments
      if (initialAdminState.segmentList) {
        const segments = initialAdminState.segmentList.split(',').map(s => s.trim()).filter(s => s);
        setAvailableSegments(segments);
        
        // Set current segment index
        const currentSegment = parseInt(initialAdminState.segment) - 1;
        setCurrentSegmentIndex(currentSegment);
      }
      
      // Set flag to enable initial evaluation when AdminSegment loads
      hasPerformedInitialEvaluation.current = true;

      return;
    }
  }, [initialAdminState]);

  // Initialize or load AdminState (skip if skipContentAutoLoad is true or initialAdminState is provided)
  useEffect(() => {
    if (initialAdminState) {
      return;
    }
    
    if (skipContentAutoLoad) {
      return;
    }
    
    const initializeAdminState = async () => {
      if (!adminStateStorage) return;
      
      try {
        // Try to load existing AdminState
        let state = await adminStateStorage.loadAdminState(profileId, actualSourceId!);
        
        if (!state) {
          // Create new AdminState if none exists
          state = await adminStateStorage.createAdminState(
            accountId,
            profileId,
            sourceType!, // Now mapped to surveyId parameter
            actualSourceId!,
            assessmentId, // segmentList - for now using assessmentId
            languageId,
            undefined, // assessmentName
            adminSegment?.SurveyAliasId // surveyAliasId from AdminSegment
          );
        }
        
        setAdminState(state);
        
        // Parse segment list to get available segments
        const segments = state.segmentList.split(',').map(s => s.trim()).filter(s => s);
        setAvailableSegments(segments);
        
        // Set current segment index
        const currentSegment = parseInt(state.segment) - 1;
        setCurrentSegmentIndex(currentSegment);
        
        // Create assessment session
        const session = adminStateStorage.createAssessmentSession(state, segments);
        setAssessmentSession(session);
        
        console.log('✅ AdminState initialized:', {
          recId: state.recId,
          segment: state.segment,
          pageNum: state.pageNum
        });
        
        // Load AdminSegment content first, then perform initial evaluation
        const segmentLoaded = await loadAdminSegment(assessmentId, languageId);
        
        if (segmentLoaded) {
          // Mark that we're performing initial evaluation to prevent backup useEffect
          hasPerformedInitialEvaluation.current = true;
        } else {
          console.warn('⚠️ AdminSegment failed to load, skipping initial evaluation');
        }
        
      } catch (error) {
        console.error('❌ Failed to initialize AdminState:', error);
      }
    };
    
    initializeAdminState();
  }, [adminStateStorage, accountId, profileId, actualSourceId, sourceType, assessmentId, languageId, skipContentAutoLoad, initialAdminState]);













  // Function to execute PreScript JavaScript with AdminState data access
  // Returns navigation instructions, custom text, and modified state
  interface PreScriptResult { skipPage?: number; gotoPage?: string; skipNode?: number; cText?: string; enableConsent?: number; disableBack?: number; disableSave?: number; enableDone?: string; renderReport?: string; modifiedState: AdminStateDto; }
  const executePreScript = async (preScript: string, currentPage: any, currentState?: AdminStateDto): Promise<PreScriptResult> => {
    if (!preScript || preScript.trim() === '') {
     return { modifiedState: currentState || adminState! }; // Continue with page rendering
    }

    const pageNum = currentPage?.pageNum || currentPage?.PageNum || adminState?.pageNum;

    try {
      // Package AdminState properties into easily accessible structures
      const stateToUse = currentState || adminState;
      const adminStateProperties = new Map<string, string>();
      if (stateToUse) {
        adminStateProperties.set('profileId', stateToUse.profileId || '');
        adminStateProperties.set('pageNum', stateToUse.pageNum || '');
        adminStateProperties.set('segment', stateToUse.segment || '');
        adminStateProperties.set('segmentList', stateToUse.segmentList || '');
        adminStateProperties.set('modified', stateToUse.modified || '');
        adminStateProperties.set('dueDate', stateToUse.dueDate || '');
        adminStateProperties.set('surveyId', stateToUse.surveyId || '');
        adminStateProperties.set('sourceId', stateToUse.sourceId || '');
        adminStateProperties.set('dName', stateToUse.dName || '');
        adminStateProperties.set('languageTypeId', stateToUse.languageTypeId || '');
        adminStateProperties.set('launchId', stateToUse.launchId || '');
        adminStateProperties.set('adminRequestId', stateToUse.adminRequestId || '');
        adminStateProperties.set('patEvntId', stateToUse.patEvntId || '');
        adminStateProperties.set('patRefId', stateToUse.patRefId || '');
        adminStateProperties.set('recId', stateToUse.recId || '');
        adminStateProperties.set('status', stateToUse.status || '');
      }

      // Package AdminState data array into Map<string, string> name-value pairs
      const adminStateData = new Map<string, string>();
      if (stateToUse?.data) {
        stateToUse.data.forEach(dataItem => {
          if (dataItem.name && dataItem.value !== undefined) {
            adminStateData.set(dataItem.name, dataItem.value);
          }
        });
      }



      // Package AdminSegment preloads array by languageTypeId and fieldCode.
      // CRITICAL: Use segmentOverrideForNavRef.current (not adminSegment React state) because
      // after a NEXT_SEGMENT transition in the inner loop, React has called setAdminSegment()
      // but the component has not yet re-rendered — adminSegment still points to the PREVIOUS
      // segment's data.  Searching the wrong segment's preloads causes all getPreload("enableXxx")
      // calls to return "" and cascades navigation to the end of the assessment.
      // segmentOverrideForNavRef.current is updated synchronously by performSegmentTransition
      // (via processAdminContent) before its promise resolves, so it is always current.
      const effectiveSegmentForPreloads = segmentOverrideForNavRef.current || adminSegment;
      const adminSegmentPreloads = new Map<string, Map<string, string>>();
      mergePreloadsIntoMap(adminSegmentPreloads, effectiveSegmentForPreloads?.preloads || effectiveSegmentForPreloads?.Preloads);
      // Account-level PLA preloads (AccountPlaDto.Preloads, e.g. surveyblist, adminreportid) —
      // resolved once at session creation (AdminStateStorage.createAdminState/
      // refreshAccountPreloads) and carried on stateToUse.accountPreloads, so they're visible
      // on every segment, not just whichever one is currently loaded. Merged in AFTER the
      // segment's own preloads so an account-configured value wins on a fieldCode collision.
      mergePreloadsIntoMap(adminSegmentPreloads, stateToUse?.accountPreloads);

      // Update tracking lists
      const dataUpdates: Array<{ name: string, value: string }> = [];
      const propertyUpdates: Array<{ name: string, value: string }> = [];
      const preloadUpdates: Array<{ fieldCode: string, value: string, languageTypeId: string }> = [];

      // Diagnostic: track getData / getPreload keys that return empty — helps diagnose PreScript skips
      const getDataMisses: string[] = [];
      const getPreloadMisses: string[] = [];
      
      // Create a safe execution context with helper functions
      const context = {
        // AdminState access (read-only maps)
        adminStateProperties,
        adminStateData,
        adminSegmentPreloads,
        
        // Helper functions for getting values
        getData: (name: string) => {
          const val = adminStateData.get(name);
          if (!val) getDataMisses.push(name);
          return val || '';
        },
        getAdmin: (name: string) => adminStateProperties.get(name) || '',
        getPreload: (fieldCode: string, languageTypeId?: string) => {
          // Lookup order: explicit lang → current session lang → empty/no-lang
          const langsToTry = [
            languageTypeId !== undefined ? languageTypeId : null,
            languageId,   // current session language (prop, defaults to '1')
            '',           // language-neutral fallback
          ].filter((l): l is string => l !== null);
          for (const lang of langsToTry) {
            const val = adminSegmentPreloads.get(lang)?.get(fieldCode);
            if (val !== undefined && val !== '') return val;
          }
          getPreloadMisses.push(fieldCode);
          return '';
        },
        
        // Helper functions for setting values (updates local maps and tracking)
        putData: (name: string, value: string) => {
          adminStateData.set(name, value);
          dataUpdates.push({ name, value });
        },
        putAdmin: (name: string, value: string) => {
          adminStateProperties.set(name, value);
          propertyUpdates.push({ name, value });
        },
        putPreload: (fieldCode: string, value: string, languageTypeId: string = languageId) => {
          if (!adminSegmentPreloads.has(languageTypeId)) {
            adminSegmentPreloads.set(languageTypeId, new Map<string, string>());
          }
          adminSegmentPreloads.get(languageTypeId)!.set(fieldCode, value);
          preloadUpdates.push({ fieldCode, value, languageTypeId });
        },
        
        // Page navigation control
        skipPage: 0, // PreScript can set this to number of pages to skip (+ forward, - backward)
        gotoPage: '', // PreScript can set this to page name to jump to
        skipNode: 0, // PreScript can set this to number of nodes to skip (node segments only)
        
        // Custom question text for pageType 1 rendering
        cText: '', // PreScript can set this for custom AdminHtmlQuestionDto qText

        // PreScript can set this to 1 (on a pageType 1 page) to replace the Continue button
        // with an Agree/Exit consent gate — the chosen answer is written to APPM_Disclaimer
        // ('1' = Agree, '2' = Exit) in the AdminState Data array for the next page to read.
        enableConsent: 0,

        // PreScript can set these to 1 to hide (while preserving footer layout spacing) the
        // Back arrow / 'Save and finish later' footer controls, respectively.
        disableBack: 0,
        disableSave: 0,

        // PreScript can set this to non-empty button text (e.g. 'Submit', 'Finish', 'Exit Now')
        // on isLastPageOfLastSegment() pages to enable the footer Done button with that label —
        // it stays disabled until this is set, so closing-segment logic controls exactly when
        // (and how) the assessment can actually be completed.
        enableDone: '',

        // PreScript can set this to a report's reportId (AdminAssessmentReportDto.reportId,
        // from the AssessmentDto's reports[] — NOT dName, which is just a display label) to
        // select which report to show after this completion is submitted — NOT rendered inline
        // here; consumed downstream by the onComplete caller (see AdminView's onComplete prop).
        renderReport: '',

        // Logging function
        log: (message: string) => console.log('📝 PreScript:', message),
        
        // Lookup helper: returns the mapped value for prm1 from a comma-delimited
        // "name=value" pair list in prm2. Returns '' when no match is found.
        // Example: recode('2', '1=Low,2=Medium,3=High') => 'Medium'
        recode: (prm1: string, prm2: string): string => {
          if (!prm2) return '';
          const pairs = prm2.split(',');
          for (const pair of pairs) {
            const eqIdx = pair.indexOf('=');
            if (eqIdx < 0) continue;
            const key = pair.substring(0, eqIdx).trim();
            if (key === String(prm1).trim()) {
              return pair.substring(eqIdx + 1).trim();
            }
          }
          return '';
        },
        
        // Current page info
        currentPageNum: stateToUse?.pageNum || '',
        currentSegment: stateToUse?.segment || '',
        
        // Safe Math and basic utilities (read-only)
        Math: Math,
        parseInt: parseInt,
        parseFloat: parseFloat,
        String: String,
        Number: Number,
        Boolean: Boolean
      };

      // Log execution context for debugging
      // console.log('📊 executePreScript: Execution context:', {
      //   propertiesCount: adminStateProperties.size,
      //   dataCount: adminStateData.size,
      //   preloadsCount: adminSegmentPreloads.size,
      //   currentPageNum: context.currentPageNum,
      //   currentSegment: context.currentSegment
      // });

      // Create a safe function to execute the PreScript
      const safeExecute = new Function(
        'context',
        `
        const {
          adminStateProperties,
          adminStateData,
          adminSegmentPreloads,
          getData,
          getAdmin,
          getPreload,
          putData,
          putAdmin,
          putPreload,
          log,
          recode,
          currentPageNum,
          currentSegment,
          Math,
          parseInt,
          parseFloat,
          String,
          Number,
          Boolean
        } = context;
        
        // Page navigation and text control variables (can be modified by PreScript)
        let skipPage = context.skipPage;
        let gotoPage = context.gotoPage;
        let cText = context.cText;
        let skipNode = context.skipNode;
        let enableConsent = context.enableConsent;
        let disableBack = context.disableBack;
        let disableSave = context.disableSave;
        let enableDone = context.enableDone;
        let renderReport = context.renderReport;

        // Execute the PreScript wrapped in an IIFE so that 'return;' inside the script
        // exits only the script body — the context-writeback lines below still run.
        (function() {
        ${preScript}
        })();

        // Update context with any changes
        context.skipPage = skipPage;
        context.gotoPage = gotoPage;
        context.cText = cText;
        context.skipNode = skipNode;
        context.enableConsent = enableConsent;
        context.disableBack = disableBack;
        context.disableSave = disableSave;
        context.enableDone = enableDone;
        context.renderReport = renderReport;

        // Return the context to access any changes
        return context;
        `
      );

      // Execute the PreScript with the context
      const result = safeExecute(context);
      
      // console.log('✅ executePreScript: PreScript executed successfully');
      
      // Create modified state if there are any updates
      const baseState = stateToUse || adminState!;
      let modifiedState: AdminStateDto = baseState;
      
      if (dataUpdates.length > 0 || propertyUpdates.length > 0 || preloadUpdates.length > 0) {
        modifiedState = { ...baseState };
        
        // Apply data updates
        if (dataUpdates.length > 0) {
          const newData = [...(modifiedState.data || [])];
          dataUpdates.forEach(update => {
            const existingIndex = newData.findIndex(item => item.name === update.name);
            if (existingIndex >= 0) {
              newData[existingIndex] = { ...newData[existingIndex], value: update.value };
            } else {
              // Create proper AdminDataDto structure
              newData.push({ 
                name: update.name, 
                value: update.value,
                segment: modifiedState.segment,
                pageNum: modifiedState.pageNum
              });
            }
          });
          modifiedState.data = newData;
        }
        
        // Apply property updates
        if (propertyUpdates.length > 0) {
          propertyUpdates.forEach(update => {
            (modifiedState as any)[update.name] = update.value;
          });
        }
        
      }
      
      // Return navigation instructions, custom text, modified state, and any preload updates
      const navigationResult: { skipPage?: number; gotoPage?: string; skipNode?: number; cText?: string; enableConsent?: number; disableBack?: number; disableSave?: number; enableDone?: string; renderReport?: string; modifiedState: AdminStateDto; preloadUpdates?: { fieldCode: string; value: string; languageTypeId: string }[] } = {
        modifiedState
      };

      if (preloadUpdates.length > 0) {
        navigationResult.preloadUpdates = preloadUpdates;
      }

      if (result.skipPage && result.skipPage !== 0) {
        navigationResult.skipPage = result.skipPage;
      }

      if (result.gotoPage && result.gotoPage.trim() !== '') {
        console.log('🎯 executePreScript: PreScript requested jump to page:', result.gotoPage);
        navigationResult.gotoPage = result.gotoPage;
      }

      if (result.skipNode && result.skipNode !== 0) {
        navigationResult.skipNode = result.skipNode;
      }

      if (result.cText && result.cText.trim() !== '') {
        navigationResult.cText = result.cText;
      }

      if (result.enableConsent && result.enableConsent !== 0) {
        navigationResult.enableConsent = result.enableConsent;
      }

      if (result.disableBack && result.disableBack !== 0) {
        navigationResult.disableBack = result.disableBack;
      }

      if (result.disableSave && result.disableSave !== 0) {
        navigationResult.disableSave = result.disableSave;
      }

      if (result.enableDone && result.enableDone.trim() !== '') {
        navigationResult.enableDone = result.enableDone;
      }

      if (result.renderReport && result.renderReport.trim() !== '') {
        navigationResult.renderReport = result.renderReport;
      }

      return navigationResult;
      
    } catch (error) {
      console.log('❌ executePreScript: Error executing PreScript:', error);
      console.log('❌ executePreScript: PreScript content:', preScript);
      
      // On error, continue with page rendering (fail-safe)
      return { modifiedState: currentState || adminState! };
    }
  };














  
  // // Function to execute PreScript JavaScript with AdminState data access
  // // Returns navigation instructions, custom text, and modified state
  // interface PreScriptResult { skipPage?: number; gotoPage?: string; cText?: string; modifiedState: AdminStateDto; }
  // const executePreScript = async (preScript: string, currentPage: any, currentState?: AdminStateDto): Promise<PreScriptResult> => {
  //   if (!preScript || preScript.trim() === '') {
  //    return { modifiedState: currentState || adminState! }; // Continue with page rendering
  //   }

  //   const pageNum = currentPage?.pageNum || currentPage?.PageNum || adminState?.pageNum;

  //   try {
  //     // Package AdminState properties into easily accessible structures
  //     const stateToUse = currentState || adminState;
  //     const adminStateProperties = new Map<string, string>();
  //     if (stateToUse) {
  //       // Extract Data values and convert to simple key-value pairs
  //       if (stateToUse.data && Array.isArray(stateToUse.data)) {
  //         stateToUse.data.forEach(dataItem => {
  //           if (dataItem.name && dataItem.value !== undefined) {
  //             adminStateProperties.set(dataItem.name, String(dataItem.value));
  //           }
  //         });
  //       }
        
  //       // Extract Prefill values and add them (Data takes precedence over Prefill)
  //       if (stateToUse.prefill && Array.isArray(stateToUse.prefill)) {
  //         stateToUse.prefill.forEach(prefillItem => {
  //           const name = prefillItem.name;
  //           const value = prefillItem.value;
  //           if (name && value !== undefined && !adminStateProperties.has(name)) {
  //             adminStateProperties.set(name, String(value));
  //           }
  //         });
  //       }
  //     }

  //     // console.log('📊 executePreScript: Execution context:', {
  //     //   pageNum,
  //     //   scriptLength: preScript.length,
  //     //   adminStatePropertiesCount: adminStateProperties.size,
  //     //   availableProperties: Array.from(adminStateProperties.keys()).slice(0, 10)
  //     // });
      
  //     // Create context with property access
  //     const context = {
  //       // Property access
  //       Property: (name: string): string => {
  //         const value = adminStateProperties.get(name);
  //         // console.log(`Property("${name}") = "${value || 'undefined'}"`);
  //         return value || '';
  //       },
        
  //       // Data access (alias for Property)
  //       Data: (name: string): string => {
  //         const value = adminStateProperties.get(name);
  //         // console.log(`Data("${name}") = "${value || 'undefined'}"`);
  //         return value || '';
  //       },
        
  //       // SetText function for custom display text
  //       SetText: (text: string) => {
  //         // console.log(`SetText called with: "${text}"`);
  //         context._cText = text;
  //       },
        
  //       // Built-in JavaScript objects
  //       Math,
  //       String,
  //       Number,
  //       Boolean,
  //       parseInt,
  //       parseFloat,
  //       JSON,
  //       console: {
  //         log: (...args: any[]) => console.log('🔧 PreScript:', ...args)
  //       },
        
  //       // Storage for custom text
  //       _cText: null as string | null
  //     };
      
  //     // Execute the script in the context
  //     const scriptFunction = new Function(...Object.keys(context), preScript);
  //     scriptFunction(...Object.values(context));
      
  //     // console.log('✅ executePreScript: PreScript executed successfully');
      
  //     // Create the result with potential state modifications
  //     const result: PreScriptResult = {
  //       modifiedState: stateToUse!,
  //       cText: context._cText || undefined
  //     };
      
  //     if (context._cText) {
  //       console.log('📝 executePreScript: Applying state modifications:', {
  //         hasCText: !!context._cText,
  //         cTextLength: context._cText?.length || 0
  //       });
        
  //       // Create new state with cText property
  //       const modifiedState = {
  //         ...stateToUse!,
  //         cText: context._cText
  //       } as any; // Type assertion since AdminStateDto doesn't include cText
        
  //       result.modifiedState = modifiedState;
  //       result.cText = context._cText;
  //     }
      
  //     return result;
      
  //   } catch (error) {
  //     console.error('❌ executePreScript: Error executing PreScript:', error);
  //     console.error('Script content:', preScript);
      
  //     // Return unmodified state on error
  //     return {
  //       modifiedState: currentState || adminState!,
  //       cText: undefined
  //     };
  //   }
  // };

  // Page Logic Continue handler - called by Continue button in Page Logic card
  const handleContinue = async () => {
    // Check for any completed CalcScript updates from background processing before proceeding
    await applyPendingCalcScriptUpdates();
    
    // console.log('🔵 handleContinue: Continue button clicked - processing Page Logic execution');
    
    if (!adminState || !editPageLogicEnabled) {
      console.log('🔵 handleContinue: ❌ Missing requirements:', {
        hasAdminState: !!adminState,
        editPageLogicEnabled
      });
      return;
    }

    // Save the current preScript edits to AdminSegment for persistence
    if (preScriptDirty) {
      // console.log('🔵 handleContinue: Saving preScript edits to AdminSegment...');
      
      // Additional pre-save validation
      if (!adminSegment) {
        console.error('🔵 handleContinue: ❌ CRITICAL ERROR - adminSegment is null/undefined:', adminSegment);
        return;
      }

      // For node segments (PAS/5) pages live inside Nodes[].Pages[], not at the top level
      const segType = String(adminSegment.SegmentType || adminSegment.segmentType || '');
      const isNodeSeg = segType === '5' || segType === 'PAS';
      if (!isNodeSeg) {
        const pages = adminSegment.pages || adminSegment.Pages;
        if (!pages || !Array.isArray(pages)) {
          console.error('🔵 handleContinue: ❌ CRITICAL ERROR - adminSegment pages not available:', {
            hasPages: !!pages,
            isArray: Array.isArray(pages),
            pagesType: typeof pages
          });
          return;
        }
      }
      
      // console.log('🔵 handleContinue: Pre-save validation passed - proceeding with save');
      const saveResult = savePreScriptEdits(adminState.pageNum, editedPreScript);
      console.log('🔵 handleContinue: Save result:', saveResult);
      
      if (saveResult) {
        setPreScriptDirty(false); // Mark as saved
      } else {
        return; // Don't proceed if save failed
      }
    } 

    setPreScriptExecuted(true);
    await goToNextPage(false, true); //refresh the current page with the updated PreScript, force PreScript execution

    // // For pageType 1 pages, execute PreScript to get cText and update the page display
    // const currentPage = getCurrentPageFromSegment(adminState);
    // if (currentPage && (currentPage.pageType === 1 || currentPage.PageType === 1 || currentPage.Type === 1 || currentPage.Type === '1')) {
    //   console.log('🔵 handleContinue: PageType 1 detected - executing PreScript for cText processing');
    //   await goToNextPage(false); //refresh the current page with the updated PreScript
    // }    
    // else {
    //   console.log('🔵 handleContinue: Not pageType 1  PreScript execution');
    //   await goToNextPage(true); 
    // }

  };


  
  // AdminSegment data (new approach) - moved to top of component
  // const [adminSegment, setAdminSegment] = useState<any>(null);
  // const [currentSegmentIndex, setCurrentSegmentIndex] = useState<number>(0);
  // const [availableSegments, setAvailableSegments] = useState<string[]>([]);
  // Note: Removed currentPageIndex - AdminState.pageNum is the source of truth
  
  // Answer state management for AdminSegment - moved to top of component
  // const [segmentAnswers, setSegmentAnswers] = useState<Record<string, any>>({});
  
  // Current page and questions (legacy table approach)
  const [currentPage, setCurrentPage] = useState<HtmlPage | null>(null);
  const [currentQuestions, setCurrentQuestions] = useState<HtmlQuestion[]>([]);
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, HtmlAnswer[]>>({});

  // LoadAdminSegment function - centralized content loading and unpacking
  const loadAdminSegment = async (segmentId: string, languageId: string): Promise<boolean> => {
    // Only prevent loading if currently loading (not if already loaded - we might need to reprocess with saved edits)
    if (isContentLoading) {
      return true;
    }

    setHasLoadAdminSegmentBeenCalled(true);
    setIsContentLoading(true);
    
    try {
      // Step 1: Check if segment exists in content store
      let adminContent: ContentRecord | null = null;
      
      if (useContentStore && contentRecords.length > 0) {
        adminContent = contentRecords.find((record: ContentRecord) => 
          record.contentType === 'AdminSegment' &&
          record.contentTypeId === segmentId &&
          record.languageTypeId === languageId
        );
        
        if (adminContent) {
        }
      }
      
      // Step 2: If not found, attempt to download from backend
      if (!adminContent && apiService) {
        console.log('⬇️ loadAdminSegment: Segment not found, downloading from backend');
        
        try {
          const serviceRequest = {
            Email: '',
            Password: '',
            Signature: 'testsignature123',
            AccessType: API_CONSTANTS.DEFAULT_ACCESS_TYPE,
            SkinType: '1',
            ContentType: 'AdminSegment',
            ContentTypeId: segmentId,
            LanguageTypeId: languageId
          };
          
          const response = await apiService.processServiceRequestWithRefresh(serviceRequest, 'sync', {
            userStore: userStoreHook,
            storage,
          });
          
          if (response?.success && response?.data) {
            console.log('✅ loadAdminSegment: Successfully downloaded from backend');
            
            // Re-check content store after download
            if (useContentStore) {
              const updatedRecords = useContentStore.getState().records || [];
              adminContent = updatedRecords.find((record: ContentRecord) => 
                record.contentType === 'AdminSegment' &&
                record.contentTypeId === segmentId &&
                record.languageTypeId === languageId
              );
            }
          } else {
            console.warn('⚠️ loadAdminSegment: Failed to download from backend:', response);
          }
        } catch (downloadError) {
          console.error('❌ loadAdminSegment: Error downloading from backend:', downloadError);
        }
      }
      
        // Step 3: If we have content, process it with processAdminContent
        if (adminContent) {
          // Check if this is truly fresh content by comparing with cached version
          // Use the processedContentCache to determine if we've seen this exact content before
          const cacheKey = `${adminContent.contentTypeId}-${adminContent.languageTypeId}`;
          const cachedContent = processedContentCache.current.get(cacheKey);

          // Only clear saved edits if this is genuinely new content (different cache key or different content signature)
          const contentSignature = `${adminContent.contentTypeId}-${adminContent.languageTypeId}-${adminContent.lastUpdated || adminContent.lastSynced || 'unknown'}`;
          // Use a PER-SEGMENT key so loading segment 2 never invalidates edits from segment 1.
          const sigStorageKey = `last_content_signature_${segmentId}_${languageId}`;
          const lastContentSignature = await storage?.getItem(sigStorageKey) || '';
          const isFreshContent = contentSignature !== lastContentSignature;
          
          if (isFreshContent) {
            console.log('💾 loadAdminSegment: Fresh AdminSegment content detected, clearing saved PreScript edits for this segment:', {
              newSignature: contentSignature,
              lastSignature: lastContentSignature,
              segmentId,
              languageId
            });
            // Clear ONLY the pages belonging to this segment — edits for other segments are preserved.
            const pageIds = await extractPageIdsFromAdminSegmentContent(adminContent);
            await clearPreScriptEditsForPageIds(segmentId, pageIds);
            
            // Save the new content signature (per-segment key)
            if (storage) {
              await storage.setItem(sigStorageKey, contentSignature);
            }
          }
          
          try {
            await processAdminContent(adminContent);
          } catch (processError) {
            console.error('💾 loadAdminSegment: ERROR in processAdminContent:', processError);
            console.error('💾 loadAdminSegment: processError details:', {
              message: processError instanceof Error ? processError.message : String(processError),
              stack: processError instanceof Error ? processError.stack?.substring(0, 500) : undefined,
              adminContent: adminContent ? {
                contentTypeId: adminContent.contentTypeId,
                languageTypeId: adminContent.languageTypeId,
                hasTableData: !!adminContent.tableData
              } : 'null'
            });
          }
          return true;
        }      console.log('❌ loadAdminSegment: No content found or loaded');
      return false;
      
    } catch (error) {
      console.error('❌ loadAdminSegment: Unexpected error:', error);
      return false;
    } finally {
      setIsContentLoading(false);
    }
  };
  
  // Load AdminSegment when AdminState is ready (for all AdminState-managed assessments)
  const lastLoadedSegmentRef = useRef<string | null>(null);
  useEffect(() => {
    if (adminState && adminStateStorage && assessmentId && languageId) {
      const segmentKey = `${assessmentId}-${languageId}`;
      // Only load if we don't already have the segment loaded and haven't just loaded this segment
      if ((!adminSegment || adminSegment.surveyId !== assessmentId) &&
          lastLoadedSegmentRef.current !== segmentKey) {
        lastLoadedSegmentRef.current = segmentKey;
        loadAdminSegment(assessmentId, languageId);
      }
    }
  }, [adminState, adminStateStorage, assessmentId, languageId]);

  // Perform initial page evaluation when AdminSegment becomes available after initialization
  useEffect(() => {
    if (!adminState || !adminSegment || !adminStateStorage) return;

    // A NEXT_SEGMENT transition just committed (goToNextPage returned early so this effect —
    // firing once adminSegment's React state has caught up — could evaluate the new segment's
    // first page with a fresh, non-stale closure). This must fire regardless of which segment
    // we landed on: the old isFirstSegment-only gate below left every later segment (including
    // a closing segment) permanently unevaluated after a forward transition in normal
    // (non-single-step) mode, since nothing else calls goToNextPage for it in that mode.
    if (justAdvancedSegmentRef.current) {
      justAdvancedSegmentRef.current = false;
      setTimeout(async () => {
        await goToNextPage(false);
      }, 50);
      return;
    }

    if (hasPerformedInitialEvaluation.current) {
      // Only trigger evaluation-only mode for the first segment in the segment list
      // This prevents interference with BackTable navigation to later segments
      const segmentList = adminState.segmentList?.split(',') || [];
      const firstSegment = segmentList[0];
      const isFirstSegment = adminState.segment === firstSegment;

      if (isFirstSegment) {
        // Reset the flag to false — must stay false throughout the evaluation so that
        // any setAdminSegment call (e.g. from savePreScriptEdits in handleContinue) does
        // NOT re-arm this useEffect and cause an infinite evaluation loop.
        hasPerformedInitialEvaluation.current = false;

        setTimeout(async () => {
          // For continuing assessments, just evaluate the current page without advancing
          await goToNextPage(false); // false = don't advance, just evaluate current page
        }, 50);
      } else {
        hasPerformedInitialEvaluation.current = false; // Reset flag for future use
      }
    }
  }, [adminSegment]); // Only trigger when adminSegment changes

  // Monitor assessment completion status
  useEffect(() => {
    if (adminState && availableSegments.length > 0) {
      const isComplete = checkAssessmentCompletion();
      if (isComplete !== isAssessmentComplete) {
        console.log('🏁 Assessment completion status changed:', {
          wasComplete: isAssessmentComplete,
          nowComplete: isComplete,
          currentSegment: adminState.segment,
          totalSegments: availableSegments.length
        });
        setIsAssessmentComplete(isComplete);
      }
    }
  }, [adminState?.segment, availableSegments.length]);

 
  // Apply CalcScript updates to AdminState and return updated state
  const applyCalcScriptUpdatesSync = async (adminState: AdminStateDto, updates: { dataUpdates: Array<{name: string, value: string}>, propertyUpdates: Array<{name: string, value: string}>, preloadUpdates: Array<{fieldCode: string, value: string, languageTypeId: string}> }): Promise<AdminStateDto> => {
    console.log('📊 applyCalcScriptUpdatesSync: Applying calcScript updates synchronously:', {
      dataUpdates: updates.dataUpdates.length,
      propertyUpdates: updates.propertyUpdates.length,
      preloadUpdates: updates.preloadUpdates.length,
      currentSegment: adminState.segment,
      currentPageNum: adminState.pageNum
    });
    console.log('📊 applyCalcScriptUpdatesSync: BEFORE - AdminState data array:', {
      dataCount: adminState.data?.length || 0,
      dataEntries: adminState.data?.map(d => ({ name: d.name, value: d.value, segment: d.segment, pageNum: d.pageNum })) || []
    });

    let updatedState = { ...adminState };

    // Apply data updates
    if (updates.dataUpdates.length > 0) {
      const newDataArray = [...(updatedState.data || [])];
      
      for (const update of updates.dataUpdates) {
        // Find existing entry with same name and segment
        const existingIndex = newDataArray.findIndex(entry => 
          entry.name === update.name && entry.segment === updatedState.segment
        );
        
        if (existingIndex >= 0) {
          // Update existing entry
          newDataArray[existingIndex] = {
            ...newDataArray[existingIndex],
            value: update.value
          };
        } else {
          // Add new entry
          newDataArray.push({
            name: update.name,
            value: update.value,
            segment: updatedState.segment,
            pageNum: updatedState.pageNum
          });
        }
        
        console.log('📊 applyCalcScriptUpdatesSync: Applied data update:', {
          name: update.name,
          value: update.value
        });
      }
      
      updatedState = { ...updatedState, data: newDataArray };
    }

    // Apply property updates - AdminStateDto doesn't have properties field, log for now
    if (updates.propertyUpdates.length > 0) {
      console.log('📊 applyCalcScriptUpdatesSync: Property updates detected but AdminStateDto has no properties field:', {
        propertyUpdates: updates.propertyUpdates.map(u => ({ name: u.name, value: u.value }))
      });
      
      // Note: AdminStateDto structure doesn't include a properties field
      // Property updates may need to be handled differently or stored elsewhere
      for (const update of updates.propertyUpdates) {
        console.log('📊 applyCalcScriptUpdatesSync: Would apply property update:', {
          name: update.name,
          value: update.value
        });
      }
    }

    // Note: Preload updates would need to be applied to AdminSegment, not AdminState
    // For now, we'll log them but not apply them
    if (updates.preloadUpdates.length > 0) {
      console.log('📊 applyCalcScriptUpdatesSync: Preload updates detected but not applied to AdminState:', updates.preloadUpdates);
    }

    console.log('📊 applyCalcScriptUpdatesSync: ✅ CalcScript updates applied synchronously');
    console.log('📊 applyCalcScriptUpdatesSync: AFTER - AdminState data array:', {
      dataCount: updatedState.data?.length || 0,
      dataEntries: updatedState.data?.map(d => ({ name: d.name, value: d.value, segment: d.segment, pageNum: d.pageNum })) || []
    });
    return updatedState;
  };

  // Execute calc script for AdminSegment transitions (modified version of executePreScript)
  const executeCalcScript = async (
    calcScript: string, 
    currentState: AdminStateDto,
    onStateUpdate: (updates: { dataUpdates: Array<{name: string, value: string}>, propertyUpdates: Array<{name: string, value: string}>, preloadUpdates: Array<{fieldCode: string, value: string, languageTypeId: string}> }) => void
  ): Promise<void> => {
    if (!calcScript || calcScript.trim() === '') {
      console.log('📊 executeCalcScript: No CalcScript to execute');
      return;
    }

    try {
      console.log('📊 executeCalcScript: Executing CalcScript for segment transition');
      console.log('📊 executeCalcScript: BEFORE - AdminState data array:', {
        segment: currentState.segment,
        pageNum: currentState.pageNum,
        dataCount: currentState.data?.length || 0,
        dataEntries: currentState.data?.map(d => ({ name: d.name, value: d.value, segment: d.segment, pageNum: d.pageNum })) || []
      });
      
      // Package AdminState properties into easily accessible structures
      const adminStateProperties = new Map<string, string>();
      if (currentState) {
       adminStateProperties.set('profileId', currentState.profileId || '');
        adminStateProperties.set('pageNum', currentState.pageNum || '');
        adminStateProperties.set('segment', currentState.segment || '');
        adminStateProperties.set('segmentList', currentState.segmentList || '');
        adminStateProperties.set('modified', currentState.modified || '');
        adminStateProperties.set('dueDate', currentState.dueDate || '');
        adminStateProperties.set('surveyId', currentState.surveyId || '');
        adminStateProperties.set('sourceId', currentState.sourceId || '');
        adminStateProperties.set('dName', currentState.dName || '');
        adminStateProperties.set('languageTypeId', currentState.languageTypeId || '');
        adminStateProperties.set('launchId', currentState.launchId || '');
        adminStateProperties.set('adminRequestId', currentState.adminRequestId || '');
        adminStateProperties.set('patEvntId', currentState.patEvntId || '');
        adminStateProperties.set('patRefId', currentState.patRefId || '');
        adminStateProperties.set('recId', currentState.recId || '');
        adminStateProperties.set('status', currentState.status || '');
      }

      // Package AdminState data array into Map<string, string> name-value pairs
      const adminStateData = new Map<string, string>();
      if (currentState?.data) {
        currentState.data.forEach(dataItem => {
          if (dataItem.name && dataItem.value !== undefined) {
            adminStateData.set(dataItem.name, dataItem.value);
          }
        });
      }

      // Package AdminSegment preloads array by languageTypeId and fieldCode
      const adminSegmentPreloads = new Map<string, Map<string, string>>();
      mergePreloadsIntoMap(adminSegmentPreloads, adminSegment?.preloads || adminSegment?.Preloads);
      // Account-level PLA preloads — see the matching comment in executePreScript above.
      // Merged in AFTER the segment's own preloads so an account-configured value wins on a
      // fieldCode collision.
      mergePreloadsIntoMap(adminSegmentPreloads, currentState?.accountPreloads);

      // Update tracking lists (only data, property and preload updates - no navigation)
      const dataUpdates: Array<{ name: string, value: string }> = [];
      const propertyUpdates: Array<{ name: string, value: string }> = [];
      const preloadUpdates: Array<{ fieldCode: string, value: string, languageTypeId: string }> = [];
      
      // Create a safe execution context with helper functions (no navigation controls)
      const context = {
        // AdminState access (read-only maps)
        adminStateProperties,
        adminStateData,
        adminSegmentPreloads,
        
        // Helper functions for getting values
        getData: (name: string) => adminStateData.get(name) || '',
        getAdmin: (name: string) => adminStateProperties.get(name) || '',
        getPreload: (fieldCode: string, languageTypeId?: string) => {
          // Lookup order: explicit lang → current session lang → empty/no-lang
          const langsToTry = [
            languageTypeId !== undefined ? languageTypeId : null,
            languageId,   // current session language (prop, defaults to '1')
            '',           // language-neutral fallback
          ].filter((l): l is string => l !== null);
          for (const lang of langsToTry) {
            const val = adminSegmentPreloads.get(lang)?.get(fieldCode);
            if (val !== undefined && val !== '') return val;
          }
          return '';
        },
        
        // Helper functions for setting values (updates local maps and tracking)
        putData: (name: string, value: string) => {
          adminStateData.set(name, value);
          dataUpdates.push({ name, value });
        },
        putAdmin: (name: string, value: string) => {
          adminStateProperties.set(name, value);
          propertyUpdates.push({ name, value });
        },
        putPreload: (fieldCode: string, value: string, languageTypeId: string = languageId) => {
          if (!adminSegmentPreloads.has(languageTypeId)) {
            adminSegmentPreloads.set(languageTypeId, new Map<string, string>());
          }
          adminSegmentPreloads.get(languageTypeId)!.set(fieldCode, value);
          preloadUpdates.push({ fieldCode, value, languageTypeId });
        },
        
        // Logging function
        log: (message: string) => console.log('📊 CalcScript:', message),
        
        // Lookup helper: returns the mapped value for prm1 from a comma-delimited
        // "name=value" pair list in prm2. Returns '' when no match is found.
        // Example: recode('2', '1=Low,2=Medium,3=High') => 'Medium'
        recode: (prm1: string, prm2: string): string => {
          if (!prm2) return '';
          const pairs = prm2.split(',');
          for (const pair of pairs) {
            const eqIdx = pair.indexOf('=');
            if (eqIdx < 0) continue;
            const key = pair.substring(0, eqIdx).trim();
            if (key === String(prm1).trim()) {
              return pair.substring(eqIdx + 1).trim();
            }
          }
          return '';
        },
        
        // Current state info
        currentPageNum: currentState?.pageNum || '',
        currentSegment: currentState?.segment || '',
        
        // Safe Math and basic utilities (read-only)
        Math: Math,
        parseInt: parseInt,
        parseFloat: parseFloat,
        String: String,
        Number: Number,
        Boolean: Boolean
      };

      // Create a safe function to execute the CalcScript
      const safeExecute = new Function(
        'context',
        `
        const {
          adminStateProperties,
          adminStateData,
          adminSegmentPreloads,
          getData,
          getAdmin,
          getPreload,
          putData,
          putAdmin,
          putPreload,
          log,
          recode,
          currentPageNum,
          currentSegment,
          Math,
          parseInt,
          parseFloat,
          String,
          Number,
          Boolean
        } = context;
        
        // Execute the CalcScript
        ${calcScript}
        
        // Return the context to access any changes
        return context;
        `
      );

      // Execute the CalcScript with the context asynchronously to prevent blocking
      setTimeout(() => {
        try {
          const result = safeExecute(context);
          
          console.log('📊 executeCalcScript: CalcScript executed successfully');
          console.log('📊 executeCalcScript: Generated updates:', {
            dataUpdates: dataUpdates.map(u => ({ name: u.name, value: u.value })),
            propertyUpdates: propertyUpdates.map(u => ({ name: u.name, value: u.value })),
            preloadUpdates: preloadUpdates.map(u => ({ fieldCode: u.fieldCode, value: u.value, languageTypeId: u.languageTypeId }))
          });
          
          // Always call the callback so awaiting callers (e.g. blocking NEXT_NODE Promise) always resolve.
          // Callers that only care about non-empty updates can check the array lengths themselves.
          console.log('📊 executeCalcScript: Applying state modifications via callback:', {
            dataUpdates: dataUpdates.length,
            propertyUpdates: propertyUpdates.length,
            preloadUpdates: preloadUpdates.length
          });
          onStateUpdate({ dataUpdates, propertyUpdates, preloadUpdates });
        } catch (innerError) {
          console.error('❌ executeCalcScript: Error in async CalcScript execution:', innerError);
          console.error('❌ executeCalcScript: CalcScript content:', calcScript);
          // CalcScript errors are non-fatal, continue with normal segment transition
        }
      }, 0); // Yield control immediately to prevent blocking
      
    } catch (error) {
      console.error('❌ executeCalcScript: Error executing CalcScript:', error);
      console.error('❌ executeCalcScript: CalcScript content:', calcScript);
      // CalcScript errors are non-fatal, continue with normal segment transition
    }
  };

  // Function to perform preload substitutions in text
  const performPreloadSubstitutions = (text: string): string => {
    if (!text || typeof text !== 'string') return text || '';

    // While the page-logic editor is open, leave tokens raw on the page's initial render so
    // the author can see and edit them directly — but once they click Continue and the
    // PreScript actually executes for this page (scriptExecutedForPage, set alongside
    // cText/enableConsent/etc. in goToNextPage), substitute normally, same as script effects.
    if (editPageLogicEnabled && !(adminState as any)?.scriptExecutedForPage) return text;

    // ── Pass 1: <<xxxx>> → segment preload value ─────────────────────────────
    // <<fieldcode>> maps directly to a preload entry. Runtime overrides from the current
    // page's putPreload() calls are checked first so PreScript-supplied values are visible
    // on the very same render. An optional ":default" suffix — <<fieldcode:default>> —
    // supplies the text to use when fieldcode isn't found in the preload list; without it,
    // an unresolved token still resolves to '' as before.
    let result = text.replace(/<<([^>]+)>>/g, (match, inner) => {
      const stripped = inner.trim();

      // Insertion-function tokens (e.g. <<dt:base:years:approx:blank:preselect>>) render as
      // live controls, not plain substituted text — leave them untouched here for
      // renderHtmlText's splitInsertionFunctionSegments to pick up.
      if (INSERTION_FUNCTION_NAMES.has(stripped.split(':')[0].trim().toLowerCase())) {
        return match;
      }

      const colonIdx = stripped.indexOf(':');
      const fieldCode = (colonIdx >= 0 ? stripped.slice(0, colonIdx) : stripped).trim();
      const defaultValue = colonIdx >= 0 ? stripped.slice(colonIdx + 1) : undefined;

      // Check runtime overrides first (populated synchronously from PreScript putPreload calls)
      if (runtimePreloadOverridesRef.current.has(fieldCode)) {
        const value = runtimePreloadOverridesRef.current.get(fieldCode) as string;
        console.log('🔄 Preload substitution (runtime override):', { pattern: match, fieldCode, value });
        return value;
      }

      if (adminSegment?.preloads || adminSegment?.Preloads) {
        const preloads = adminSegment.preloads || adminSegment.Preloads;
        const preloadItem = preloads.find((item: any) => {
          // FieldCode may be stored with its delimiters (e.g. "<<PatientName>>")
          // Normalize by stripping leading << and trailing >> before comparing.
          const raw = item.fieldCode || item.FieldCode || '';
          const itemFieldCode = raw.replace(/^<<|>>$/g, '');
          return itemFieldCode === fieldCode;
        });
        if (preloadItem) {
          const value = preloadItem.value || preloadItem.Value || '';
          return value;
        }
      }

      if (defaultValue !== undefined) {
        console.log('🔄 Preload not found, using default:', { pattern: match, fieldCode, defaultValue });
        return defaultValue;
      }
      console.log('⚠️ Preload not found for fieldCode:', fieldCode);
      return ''; // replace unresolved token with empty string
    });

    // ── Pass 2: <#xxxx#> → adminState data value (getAdmin / data array) ────
    result = result.replace(/<#([^#]+)#>/g, (match, varName) => {
      const name = varName.trim();

      // Check data array first (mvar responses)
      if (adminState?.data) {
        const entry = adminState.data.find(d => d.name === name);
        if (entry !== undefined) {
          console.log('🔄 AdminState data substitution:', { pattern: match, name, value: entry.value });
          return entry.value;
        }
      }

      // Fall back to AdminState top-level properties (same map as getAdmin())
      const stateProps: Record<string, string | undefined> = {
        profileId:      adminState?.profileId,
        pageNum:        adminState?.pageNum,
        segment:        adminState?.segment,
        segmentList:    adminState?.segmentList,
        surveyId:       adminState?.surveyId,
        sourceId:       adminState?.sourceId,
        dName:          adminState?.dName,
        languageTypeId: adminState?.languageTypeId,
        recId:          adminState?.recId,
        status:         adminState?.status,
        dueDate:        adminState?.dueDate,
        modified:       adminState?.modified,
      };
      if (name in stateProps && stateProps[name]) {
        console.log('🔄 AdminState property substitution:', { pattern: match, name, value: stateProps[name] });
        return stateProps[name] as string;
      }

      console.log('⚠️ AdminState variable not found:', name);
      return ''; // replace unresolved token with empty string
    });

    // ── Pass 3: <*xxxx*> → adminState top-level property value ─────────────────
    result = result.replace(/<\*([^*]+)\*>/g, (match, propName) => {
      const name = propName.trim();

      const adminProps: Record<string, string | undefined> = {
        profileId:      adminState?.profileId,
        pageNum:        adminState?.pageNum,
        segment:        adminState?.segment,
        segmentList:    adminState?.segmentList,
        sourceId:       adminState?.sourceId,
        sourceType:     (adminState as any)?.sourceType,
        dName:          adminState?.dName,
        languageTypeId: adminState?.languageTypeId,
        recId:          adminState?.recId,
        status:         adminState?.status,
        dueDate:        adminState?.dueDate,
        modified:       adminState?.modified,
        launchId:       adminState?.launchId,
        adminRequestId: adminState?.adminRequestId,
        patEvntId:      adminState?.patEvntId,
        patRefId:       adminState?.patRefId,
      };
      if (name in adminProps && adminProps[name] !== undefined) {
        return adminProps[name] as string;
      }

      // Dynamic fallback: putAdmin() can set arbitrary properties that aren't in the
      // hardcoded map above (e.g. putAdmin("PCL_1_qtext", "...")).  By render time
      // setAdminState has been called with the modifiedState that carries those values.
      const dynamicValue = (adminState as any)?.[name];
      if (dynamicValue !== undefined && dynamicValue !== null && dynamicValue !== '') {
        return String(dynamicValue);
      }

      console.log('⚠️ Admin property not found:', name);
      return ''; // replace unresolved token with empty string
    });

    return result;
  };

  // Function to evaluate the current page and return navigation instructions
  // Returns: { shouldRender: boolean, skipPage?: number, gotoPage?: string, skipNode?: number, cText?: string, modifiedState: AdminStateDto, preloadUpdates? }
  const evaluateCurrentPage = async (currentState: AdminStateDto, forceExecuteScript: boolean = false): Promise<{ shouldRender: boolean, skipPage?: number, gotoPage?: string, skipNode?: number, cText?: string, enableConsent?: number, disableBack?: number, disableSave?: number, enableDone?: string, renderReport?: string, scriptExecuted?: boolean, modifiedState: AdminStateDto, preloadUpdates?: { fieldCode: string; value: string; languageTypeId: string }[] }> => {
    // Use the segment override ref so that the correct segment is used during the async
    // goToNextPage inner loop (adminSegment React closure is stale after NEXT_SEGMENT transitions).
    const effectiveSegment = segmentOverrideForNavRef.current || adminSegment;
    if (!currentState || !effectiveSegment) {
      return { shouldRender: false, modifiedState: currentState };
    }
    
    const currentPage = getCurrentPageFromSegment(currentState);
    if (!currentPage) {
      console.log('⚠️ No current page found for evaluation');
      return { shouldRender: false, modifiedState: currentState };
    }
    
    // Debug: Log which page object we got
    // const pageNum = currentPage.pageNum || currentPage.PageNum;
    // const debugPageType = currentPage.pageType || currentPage.type || currentPage.Type;
    // console.log('🎯 evaluateCurrentPage: Found page object:', { pageNum, pageType: debugPageType }, 'for state pageNum:', currentState.pageNum);
    
    const pageType = parseInt(currentPage.pageType || currentPage.type || currentPage.Type || '1');
    
    // PAS nodes: bypass PreScript entirely — navigation is driven by devageAdmin
    const segTypeForPasEval = String(effectiveSegment.SegmentType || effectiveSegment.segmentType || '');
    if (segTypeForPasEval === '5' || segTypeForPasEval === 'PAS') {
      const nodesForPasEval = (effectiveSegment.Nodes || (effectiveSegment as any).nodes) as any[];
      const nodeIdxForPasEval = currentState.nodeIndex ?? 0;
      const nodeForPasEval = nodesForPasEval?.[nodeIdxForPasEval];
      const nodeTypeForPasEval = String(nodeForPasEval?.NodeType || (nodeForPasEval as any)?.nodeType || '');
      if (nodeTypeForPasEval === 'PAS') {
        console.log('🔍 evaluateCurrentPage: PAS node — skipping PreScript, always rendering page', currentState.pageNum);
        return { shouldRender: true, modifiedState: currentState };
      }
    }

    let scriptResult: { skipPage?: number; gotoPage?: string; skipNode?: number; cText?: string; enableConsent?: number; disableBack?: number; disableSave?: number; enableDone?: string; renderReport?: string; modifiedState: AdminStateDto; preloadUpdates?: { fieldCode: string; value: string; languageTypeId: string }[] } = { modifiedState: currentState };
    
    // Execute PreScript if present and conditions are met
    // Always prefer editedPreScript when present (saved by Page Logic editor, regardless of whether
    // editing mode is currently active) — fall back to the original preScript from the segment data
    const originalPreScript = currentPage.preScript || currentPage.PreScript;
    const editedPreScript = currentPage.editedPreScript;
    const preScriptToExecute = editedPreScript || originalPreScript;

    // In single-step mode (editPageLogicEnabled), only execute after the continue button is clicked
    // forceExecuteScript is passed as true from handleContinue to bypass the stale React state
    const shouldExecuteScript = forceExecuteScript || !editPageLogicEnabled || preScriptExecuted;
      
    if (shouldExecuteScript) {
      
      scriptResult = await executePreScript(preScriptToExecute, currentPage, currentState);

      // Auto-skip non-renderable pageType 2 if no navigation provided
      if (!scriptResult.skipPage && !scriptResult.gotoPage && !scriptResult.skipNode && (pageType === 2)) {
        scriptResult.skipPage = 1; 
      }

      // Reset execution flag for next page
      if (editPageLogicEnabled) {
        setPreScriptExecuted(false);
      }
    } else {
      // In single-step mode, set up the edited script for display
      if (!editedPreScript && editPageLogicEnabled) {
        setEditedPreScriptWithRef(preScriptToExecute);
      }
    }

 
    // If we have navigation instructions (skipPage, gotoPage, or skipNode), don't render
    if (scriptResult.skipPage || scriptResult.gotoPage || scriptResult.skipNode) {
      // console.log('⏭️ Page', currentState.pageNum, 'has navigation instructions, not rendering');
      return { shouldRender: false, ...scriptResult };
    }
    
    // Found a renderable page - cText is used to render pageType 1. scriptExecuted records
    // whether the PreScript actually ran this pass (vs. edit mode's single-step gate holding
    // it back) — see performPreloadSubstitutions, which uses it to know whether preload/
    // insertion-function tokens should render substituted or raw for this page.
    return { shouldRender: true, cText: scriptResult.cText, skipNode: scriptResult.skipNode, enableConsent: scriptResult.enableConsent, disableBack: scriptResult.disableBack, disableSave: scriptResult.disableSave, enableDone: scriptResult.enableDone, renderReport: scriptResult.renderReport, scriptExecuted: shouldExecuteScript, modifiedState: scriptResult.modifiedState, preloadUpdates: scriptResult.preloadUpdates };
  };

  // Function to find next page based on navigation instructions
  const findNextPageSync = (currentState: AdminStateDto, skipPage?: number, gotoPage?: string, skipNode?: number): string => {
    // Use the override ref when present — it bridges the React-state lag after a cross-segment
    // transition inside a single goToNextPage loop run (adminSegment closure is still the old seg).
    const segmentSource = segmentOverrideForNavRef.current || adminSegment;
    if (!segmentSource) return 'NEXT_SEGMENT';

    const segType = String(segmentSource.SegmentType || segmentSource.segmentType || '');
    const isNodeSeg = segType === '5' || segType === 'PAS';

    // ---- SegmentType '5'/'PAS': positional (index-based) node navigation ----
    // adminState.pageNum = 1-based position within Nodes[nodeIndex].Pages[]
    // When NodePageId is set, pageNum is the actual PageNum field value (direct lookup)
    if (isNodeSeg) {
      // Node pages navigate by PageNum — same logic as flat segment pages
      const nodes = segmentSource.Nodes || segmentSource.nodes;
      if (!nodes || !Array.isArray(nodes)) return 'NEXT_SEGMENT';
      const nodeIdx = currentState.nodeIndex ?? 0;
      const nodePages = ((nodes[nodeIdx]?.Pages || nodes[nodeIdx]?.pages) as any[]) || [];
      const sortedNodePages = [...nodePages]
        .map((p: any) => ({ ...p, _numInt: parseInt(String(p.PageNum || p.pageNum || '1')) }))
        .sort((a: any, b: any) => a._numInt - b._numInt);
      const currentNum = parseInt(String(currentState.pageNum || '1'));
      const currentIdx = sortedNodePages.findIndex((p: any) => p._numInt === currentNum);

      // gotoPage: jump to named page within this node (same as flat)
      if (gotoPage) {
        const target = nodePages.find((p: any) => (p.Name || p.name) === gotoPage);
        if (target) return String(target.PageNum || target.pageNum);
      }

      // skipNode: skip N nodes ahead, landing on the first page of the target node
      if (skipNode && skipNode !== 0) {
        const targetNodeIdx = nodeIdx + skipNode;
        for (let ni = targetNodeIdx; ni < nodes.length; ni++) {
          const targetNodePages = ((nodes[ni]?.Pages || nodes[ni]?.pages) as any[]) || [];
          if (targetNodePages.length > 0) {
            const firstPage = [...targetNodePages]
              .map((p: any) => ({ ...p, _numInt: parseInt(String(p.PageNum || p.pageNum || '1')) }))
              .sort((a: any, b: any) => a._numInt - b._numInt)[0];
            return `NEXT_NODE:${ni}:${String(firstPage.PageNum || firstPage.pageNum)}`;
          }
        }
        return 'NEXT_SEGMENT';
      }

      // skipPage (or default +1): advance to next page by sorted PageNum
      const targetIdx = currentIdx + (skipPage || 1);
      if (targetIdx >= 0 && targetIdx < sortedNodePages.length) {
        const targetPage = sortedNodePages[targetIdx];
        return String(targetPage.PageNum || targetPage.pageNum);
      }

      // No more pages in this node — find next non-empty node
      for (let ni = nodeIdx + 1; ni < nodes.length; ni++) {
        const nextNodePages = ((nodes[ni]?.Pages || nodes[ni]?.pages) as any[]) || [];
        if (nextNodePages.length > 0) {
          const firstPage = [...nextNodePages]
            .map((p: any) => ({ ...p, _numInt: parseInt(String(p.PageNum || p.pageNum || '1')) }))
            .sort((a: any, b: any) => a._numInt - b._numInt)[0];
          return `NEXT_NODE:${ni}:${String(firstPage.PageNum || firstPage.pageNum)}`;
        }
      }

      return 'NEXT_SEGMENT';
    }

    // ---- Flat page navigation (SegmentType '4' / legacy) ----
    const pages = segmentSource.pages || segmentSource.Pages;
    if (!pages || !Array.isArray(pages)) return 'NEXT_SEGMENT';
    
    const currentPageNum = parseInt(currentState.pageNum || '1');
    
    // Handle gotoPage (absolute branch by page name)
    if (gotoPage) {
      const targetPage = pages.find((p: any) => {
        const pageName = p.name || p.Name;
        return pageName === gotoPage;
      });
      if (targetPage) return (targetPage.pageNum || targetPage.PageNum).toString();
      console.warn('⚠️ findNextPageSync: Page name not found:', gotoPage, 'proceeding to next page');
      skipPage = 1;
    }
    
    // Handle skipPage (relative branch)
    const targetPageNum = currentPageNum + (skipPage || 1);
    
    const targetPage = pages.find((p: any) => (p.pageNum || p.PageNum) === targetPageNum.toString());
    if (targetPage) return targetPageNum.toString();
    
    // If exact target not found, find the next available page after target
    for (let pageNum = targetPageNum; pageNum <= targetPageNum + 100; pageNum++) {
      const page = pages.find((p: any) => (p.pageNum || p.PageNum) === pageNum.toString());
      if (page) return pageNum.toString();
    }
    
    return 'NEXT_SEGMENT';
  };

  // Helper function to add current page to BackTable after all evaluation is complete
  // Note: This is now replaced by inline BackTable handling in goToNextPage to fix state synchronization issues
  const addCurrentPageToBackTable = async (currentPage: any) => {
    if (!adminState || !adminStateStorage) return;
    
    const pageTitle = currentPage.name || currentPage.Name || `Page ${adminState.pageNum}`;
    const currentBackTableEntry = {
      name: pageTitle,
      value: '', // Value should be ignored per requirements
      segment: adminState.segment,
      pageNum: adminState.pageNum,
      nodeIndex: adminState.nodeIndex,
      node: adminState.node
    };
    
    // Check if this entry already exists to avoid duplicates
    // Note: BackTable uses page names (AdminPageDto.name), Data uses mvar names (AdminQuestionDto.mvarName)
    // Duplicate check should only compare segment/pageNum, NOT names (they serve different purposes)
    const lastEntry = adminState.backTable[adminState.backTable.length - 1];
    
    if (!lastEntry || lastEntry.segment !== adminState.segment || lastEntry.pageNum !== adminState.pageNum
        || (lastEntry.nodeIndex ?? 0) !== (adminState.nodeIndex ?? 0)) {
      adminState.backTable.push(currentBackTableEntry);
      console.log('📝 Added BackTable entry for rendered page:', { name: pageTitle, segment: adminState.segment, pageNum: adminState.pageNum });
      
      // Save the updated AdminState with the new BackTable entry
      await adminStateStorage.saveAdminState(adminState);
      
      // Force React re-render to update debug information immediately
      const newAdminState = { ...adminState };
      setAdminState(newAdminState);
      
      // Debug: Log the current BackTable state (legacy function - now handled inline in goToNextPage)
      console.log('🔍 Legacy BackTable function - BackTable after adding entry:', {
        backTableLength: newAdminState.backTable.length,
        entries: newAdminState.backTable,
        lastEntry: newAdminState.backTable[newAdminState.backTable.length - 1]
      });
    } else {
      console.log('🔍 Legacy BackTable function - entry already exists, not adding duplicate');
    }
  };

 
  // Get current page from AdminSegment using AdminState.pageNum as source of truth
  const getCurrentPageFromSegment = (stateOverride?: AdminStateDto | null) => {
    const currentAdminState = stateOverride || adminState;
    if (!adminSegment || !currentAdminState) {
      console.log('🔍 getCurrentPageFromSegment: Missing requirements:', {
        hasAdminSegment: !!adminSegment,
        hasCurrentAdminState: !!currentAdminState
      });
      return null;
    }

    // Use override segment when present (set by goToPreviousPage to bridge the React-state
    // lag after loadAdminSegment — processedContentCache is updated synchronously but
    // the adminSegment React state hasn't re-rendered yet).
    const segmentToUse = segmentOverrideForNavRef.current || adminSegment;

    const segType = String(segmentToUse.SegmentType || segmentToUse.segmentType || '');
    const targetPageNum = currentAdminState.pageNum;

    // SegmentType '5'/'PAS': navigate from Nodes[nodeIndex].Pages by PageNum — same as flat pages
    if (segType === '5' || segType === 'PAS') {
      const nodes = segmentToUse.Nodes || segmentToUse.nodes;
      if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
        console.error('getCurrentPageFromSegment: No Nodes array in node segment');
        return null;
      }
      const nodeIdx = currentAdminState.nodeIndex ?? 0;
      const currentNode = nodes[nodeIdx];
      if (!currentNode) {
        console.error('getCurrentPageFromSegment: Node not found at index', nodeIdx);
        return null;
      }
      const nodePages = (currentNode.Pages || currentNode.pages) as any[];
      if (!nodePages || nodePages.length === 0) {
        console.error('getCurrentPageFromSegment: No Pages in node', nodeIdx, '— node keys:', Object.keys(currentNode));
        return null;
      }
      // Find by PageNum field — same as flat segment pages
      const page = nodePages.find((p: any) => String(p.PageNum || p.pageNum || '') === String(targetPageNum || '')) || null;
      if (!page) {
        console.error('getCurrentPageFromSegment: Page not found in node', nodeIdx, 'for pageNum', targetPageNum);
      }
      return page;
    }

    // SegmentType '4' (and legacy): flat Pages navigation
    const pages = segmentToUse.pages || segmentToUse.Pages;
    if (!pages || !Array.isArray(pages)) {
      console.log('🔍 getCurrentPageFromSegment: No pages available:', {
        hasPages: !!pages,
        isArray: Array.isArray(pages),
        pagesType: typeof pages
      });
      return null;
    }
    
    // Find page by AdminState.pageNum instead of index
    const page = pages.find((p: any) => (p.pageNum || p.PageNum) === targetPageNum);
    
    if (!page) {
      // Try with string conversion in case of type mismatch — guard against undefined
      const pageWithStringMatch = pages.find((p: any) => {
        const pNum = p.pageNum ?? p.PageNum;
        return pNum !== undefined && pNum !== null && String(pNum) === String(targetPageNum);
      });
      if (pageWithStringMatch) {
        return pageWithStringMatch;
      }
      
      console.error('getCurrentPageFromSegment: Page not found for AdminState.pageNum:', targetPageNum);
      return null;
    }
    
    return page;
  };

  // Helper function to find next page number, with segment navigation and completion logic

  // Compute PAS devage scores for a leaving node and merge the domain results into adminState.data.
  // Called before the node's CalcScript so that CalcScript can read the freshly computed scores.
  const computeAndStorePasDevageScores = async (
    node: AdminNodeDto,
    state: AdminStateDto
  ): Promise<AdminStateDto> => {
    try {
      const ageVarName = getNodeParam(node, 'agevar') || 'agephysical';
      const ageEntry = [...(state.data || []), ...(state.prefill || [])]
        .find((d: any) => d.name === ageVarName);
      const childAge = parseFloat(ageEntry?.value || '0');
      if (childAge <= 0) {
        console.warn('⚠️ PAS devage: Physical age unavailable (agevar=' + ageVarName + ') — skipping score computation');
        return state;
      }
      const nodeAnswers = extractNodeAnswers(node, state.data || []);
      if (nodeAnswers.size === 0) {
        console.warn('⚠️ PAS devage: No answers collected for node', node.Name, '— skipping score computation');
        return state;
      }
      console.log('🧮 PAS devage: Computing developmental age for node', node.Name, 'childAge:', childAge, 'answers:', nodeAnswers.size);
      const input = buildDevelopmentalAgeInput(node, childAge, nodeAnswers, state.languageTypeId || '1');
      const result = await computeDevelopmentalAge(input);
      if (result.domainResults) {
        const dr = result.domainResults;
        const dataUpdates = [
          { name: dr.scoreVarName,       value: String(result.developmentalAge.toFixed(1)) },
          { name: dr.delayVarName,       value: String(result.delay.toFixed(1)) },
          { name: dr.resultVarName,      value: dr.resultValue || '' },
          { name: dr.resultScoreVarName, value: String(Math.round((dr.resultScoreValue ?? 0) * 10) / 10) },
        ];
        console.log('🧮 PAS devage: Injecting scores into adminState.data:', dataUpdates);
        return await applyCalcScriptUpdatesSync(state, { dataUpdates, propertyUpdates: [], preloadUpdates: [] });
      }
    } catch (err) {
      console.error('❌ PAS devage: Error computing developmental age scores:', err);
    }
    return state;
  };

  const findNextPageNum = async (stateOverride?: AdminStateDto | null): Promise<string | null> => {
    // Use stateOverride when provided (avoids stale React closure for nodeIndex/pageNum)
    const effectiveState = stateOverride || adminState;
    if (!adminSegment || !effectiveState || !adminStateStorage) return null;

    const segType = String(adminSegment.SegmentType || adminSegment.segmentType || '');
    const isNodeSeg = segType === '5' || segType === 'PAS';

    // ---- SegmentType '5'/'PAS': node navigation by PageNum — same as flat pages ----
    // adminState.nodeIndex = current node (0-based); adminState.pageNum = PageNum field value
    if (isNodeSeg) {
      const nodes = adminSegment.Nodes || adminSegment.nodes;
      if (!nodes || !Array.isArray(nodes)) return null;
      const currentNodeIdx = effectiveState.nodeIndex ?? 0;
      const currentNodeForPas = nodes[currentNodeIdx] as any;

      // ---- PAS devage: adaptive next-page via devageAdmin ----
      const nodeTypeForPas = String(currentNodeForPas?.NodeType || currentNodeForPas?.nodeType || '');
      let handledAsPas = false;
      if (nodeTypeForPas === 'PAS') {
        const pstype = getPasType(currentNodeForPas as AdminNodeDto);
        if (pstype === 'devage') {
          handledAsPas = true;
          const ageVarName = getNodeParam(currentNodeForPas as AdminNodeDto, 'agevar') || 'agephysical';
          const ageEntry = [...(effectiveState.data || []), ...(effectiveState.prefill || [])]
            .find((d: any) => d.name === ageVarName);
          const childAge = parseFloat(ageEntry?.value || '0');
          const nodeAnswers = extractNodeAnswers(currentNodeForPas as AdminNodeDto, effectiveState.data || []);
          const pasResult = computeNextDevagePage({
            node: currentNodeForPas as AdminNodeDto,
            childPhysicalAge: childAge,
            answers: nodeAnswers,
          });
          console.log('🧩 findNextPageNum: PAS devage result:', {
            nextPageNum: pasResult.nextPageNum, isComplete: pasResult.isComplete,
            devFloor: pasResult.devFloor, devCeiling: pasResult.devCeiling,
            yesCount: pasResult.yesCount, noCount: pasResult.noCount,
            totalAdministered: pasResult.totalAdministered,
          });
          if (!pasResult.isComplete && pasResult.nextPageNum) {
            return pasResult.nextPageNum;
          }
          // Administration complete — find the next non-empty node
          for (let ni = currentNodeIdx + 1; ni < nodes.length; ni++) {
            const nextNPages = ((nodes[ni]?.Pages || (nodes[ni] as any)?.pages) as any[]) || [];
            if (nextNPages.length > 0) {
              const firstPage = [...nextNPages]
                .map((p: any) => ({ ...p, _numInt: parseInt(String(p.PageNum || p.pageNum || '1')) }))
                .sort((a: any, b: any) => a._numInt - b._numInt)[0];
              return `NEXT_NODE:${ni}:${String(firstPage.PageNum || firstPage.pageNum)}`;
            }
          }
          // No next node — fall through to segment transition below
        }
      }

      if (!handledAsPas) {
        // ---- Sequential node page navigation (non-PAS nodes) ----
        const nodePages = ((currentNodeForPas?.Pages || currentNodeForPas?.pages) as any[]) || [];

        // Advance within current node by sorted PageNum — same as flat page navigation
        if (nodePages.length > 0) {
          const sortedNodePages = [...nodePages]
            .map((p: any) => ({ ...p, _numInt: parseInt(String(p.PageNum || p.pageNum || '1')) }))
            .sort((a: any, b: any) => a._numInt - b._numInt);
          const currentNum = parseInt(String(effectiveState.pageNum || '1'));
          const currentIdx = sortedNodePages.findIndex((p: any) => p._numInt === currentNum);
          if (currentIdx >= 0 && currentIdx < sortedNodePages.length - 1) {
            const nextPage = sortedNodePages[currentIdx + 1];
            return String(nextPage.PageNum || nextPage.pageNum);
          }
        }

        // No more pages in current node — find next node with pages
        for (let ni = currentNodeIdx + 1; ni < nodes.length; ni++) {
          const nextNodePages = ((nodes[ni]?.Pages || nodes[ni]?.pages) as any[]) || [];
          if (nextNodePages.length > 0) {
            const firstPage = [...nextNodePages]
              .map((p: any) => ({ ...p, _numInt: parseInt(String(p.PageNum || p.pageNum || '1')) }))
              .sort((a: any, b: any) => a._numInt - b._numInt)[0];
            return `NEXT_NODE:${ni}:${String(firstPage.PageNum || firstPage.pageNum)}`;
          }
        }
        // All sequential nodes done — fall through to segment transition
      }

    } else {
      // ---- Flat page navigation (SegmentType '4' / legacy) ----
      const pages = adminSegment.pages || adminSegment.Pages;
      if (!pages || !Array.isArray(pages)) return null;

      const currentPageNum = parseInt(effectiveState.pageNum || '1');
      const sortedPages = pages
        .map((p: any) => ({ ...p, pageNumInt: parseInt(p.pageNum || p.PageNum || '1') }))
        .sort((a: any, b: any) => a.pageNumInt - b.pageNumInt);

      const currentIndex = sortedPages.findIndex((p: any) => p.pageNumInt === currentPageNum);
      if (currentIndex >= 0 && currentIndex < sortedPages.length - 1) {
        const nextPage = sortedPages[currentIndex + 1];
        return (nextPage.pageNum || nextPage.PageNum).toString();
      }
      console.log('🔄 findNextPageNum: No more pages in current segment, attempting next segment');
    }

    // ---- Segment transition ----
    const currentSegmentId = effectiveState.segment;
    const currentSegmentIndex = availableSegments.findIndex(seg => seg === currentSegmentId);

    if (currentSegmentIndex >= 0 && currentSegmentIndex < availableSegments.length - 1) {
      return 'NEXT_SEGMENT';
    }

    // No more segments — complete
    console.log('🏁 findNextPageNum: No more segments available, completing assessment');
    try {
      const completedState = { ...effectiveState, status: '5' };
      await adminStateStorage.saveAdminState(completedState);
      setAdminState(completedState);
    } catch (error) {
      console.error('❌ findNextPageNum: Error setting completion status:', error);
    }
    return null;
  };

  // Performs a complete, fully-awaited segment transition with a callback pattern:
  // all async operations (CalcScript, content load, ref updates) complete before the
  // returned Promise resolves.  The goToNextPage inner loop calls this and only evaluates
  // the new segment's PreScript AFTER this resolves — ensuring navigation is fully complete
  // before any PreScript reads getPreload() or getData() on the new segment.
  const performSegmentTransition = async (
    currentState: AdminStateDto,
    nextSegmentId: string,
    nextSegmentIdx: number
  ): Promise<AdminStateDto> => {
    setIsSegmentTransitioning(true);
    try {
      // Step 1: Execute the LEAVING segment's CalcScript synchronously.
      // adminSegment holds the correct LEAVING segment here — React hasn't re-rendered yet.
      if (adminSegment) {
        const calcScript = editedCalcScript || adminSegment.calcScript || adminSegment.CalcScript || '';
        if (calcScript.trim()) {
          console.log('📊 performSegmentTransition: Executing CalcScript for leaving segment:', {
            fromSegment: currentState.segment, toSegment: nextSegmentId,
            calcScriptLength: calcScript.length,
          });
          try {
            const loopCalcUpdates = await new Promise<{ dataUpdates: Array<{name: string, value: string}>, propertyUpdates: Array<{name: string, value: string}>, preloadUpdates: Array<{fieldCode: string, value: string, languageTypeId: string}> } | null>((resolve) => {
              executeCalcScript(calcScript, currentState, (updates) => {
                resolve(updates);
              }).catch(err => { console.error('📊 performSegmentTransition: CalcScript error:', err); resolve(null); });
            });
            if (loopCalcUpdates && (loopCalcUpdates.dataUpdates.length > 0 || loopCalcUpdates.propertyUpdates.length > 0 || loopCalcUpdates.preloadUpdates.length > 0)) {
              currentState = await applyCalcScriptUpdatesSync(currentState, loopCalcUpdates);
              await adminStateStorage!.saveAdminState(currentState);
              console.log('📊 performSegmentTransition: ✅ CalcScript updates merged into currentState');
            }
          } catch (calcErr) {
            console.error('📊 performSegmentTransition: ❌ Unexpected CalcScript error:', calcErr);
          }
        }
      }

      // Step 2: Transition AdminState to the new segment at page 1, node 0.
      const updatedState: AdminStateDto = {
        ...currentState,
        segment: nextSegmentId,
        pageNum: '1',
        nodeIndex: 0
      };
      await adminStateStorage!.saveAdminState(updatedState);
      currentState = updatedState;
      setCurrentSegmentIndex(nextSegmentIdx);
      // Set adminState BEFORE loadAdminSegment so React batches both updates in the same
      // render cycle, preventing a mid-render mismatch between adminSegment and adminState.
      setAdminState(updatedState);

      // Step 3: Load the new segment's content — FULLY AWAITED.
      // processedContentWithEditsCacheRef is populated synchronously inside loadAdminSegment
      // (via processAdminContent) before its promise resolves.
      await loadAdminSegment(updatedState.segment, updatedState.languageTypeId || '1');

      // Step 4: Update segmentOverrideForNavRef with the NEW segment's data.
      // This MUST be set before executePreScript runs — executePreScript reads preloads from
      // this ref (not from the stale adminSegment React closure) to prevent getPreload() from
      // searching the PREVIOUS segment's preloads and incorrectly skipping enabled modules.
      const newSegCacheKey = `${updatedState.segment}-${updatedState.languageTypeId || '1'}`;
      segmentOverrideForNavRef.current =
        processedContentWithEditsCacheRef.current.get(newSegCacheKey) ||
        processedContentCache.current.get(newSegCacheKey) || null;

      // Step 5: Resolve first node ID for PAS/Type-5 segments.
      const newSegCached = processedContentCache.current.get(newSegCacheKey);
      if (newSegCached) {
        const newSegType = String(newSegCached.SegmentType || newSegCached.segmentType || '');
        const isNewNodeSeg = newSegType === '5' || newSegType === 'PAS';
        if (isNewNodeSeg) {
          const newNodes = newSegCached.Nodes || newSegCached.nodes;
          const firstNodeId = newNodes?.[0]?.NodeId || newNodes?.[0]?.nodeId || '';
          currentState = { ...currentState, node: firstNodeId };
        } else {
          currentState = { ...currentState, node: '' };
        }
      }

      await adminStateStorage!.saveAdminState(currentState);
      setAdminState(currentState);
      return currentState;
    } finally {
      setIsSegmentTransitioning(false);
    }
  };

  // Navigation functions for AdminSegment with AdminState support
  const goToNextPage = async (shouldAdvance: boolean = true, forceExecuteScript: boolean = false) => {
    // Guard against concurrent navigation calls.  Two simultaneous calls share storage
    // and can produce a runaway cascade where each reload picks up state advanced by
    // the other call.
    if (isNavigatingRef.current) {
      console.log('⚠️ goToNextPage: Navigation already in progress, ignoring concurrent call');
      return;
    }
    isNavigatingRef.current = true;

    // Check for any completed CalcScript updates from background processing before proceeding
    await applyPendingCalcScriptUpdates();

    // Flush any unsaved PreScript edits to adminSegment + local storage before navigating
    // Only flush when actually advancing (not in EVALUATE-only mode) to prevent re-render loops
    if (shouldAdvance && editPageLogicEnabled && preScriptDirty && adminState) {
      // Use the ref to get the current value — the state closure may be stale if React
      // batched an update between the user's edit and this async navigation call.
      savePreScriptEdits(adminState.pageNum, editedPreScriptRef.current);
    }

    const operation = shouldAdvance ? 'ADVANCE' : 'EVALUATE';
    
    if (!adminStateStorage || !adminState || !accountId || !profileId || !actualSourceId || !adminSegment) {
      console.log('❌ goToNextPage: Missing required data', {
        hasAdminStateStorage: !!adminStateStorage,
        hasAdminState: !!adminState,
        accountId,
        profileId,
        actualSourceId,
        hasAdminSegment: !!adminSegment,
      });
      // This early return bypasses the try/finally below — without resetting the ref here,
      // every subsequent call would be silently ignored by the concurrency guard above forever.
      isNavigatingRef.current = false;
      return;
    }
    
    try {
      // Check AdminSegmentDto SegmentType for navigation processing mode
      const segmentType = adminSegment.segmentType || adminSegment.SegmentType;
      // console.log('🔍 goToNextPage: SegmentType detected:', segmentType);
      
      // 'PAS' is the backend string for node-style segments (SegmentType '5') — handled below
      
      // This ensures we have the latest answer data that may have just been saved
      const freshAdminState = await adminStateStorage.loadAdminState(profileId, actualSourceId);
      if (!freshAdminState) {
        console.log('❌ goToNextPage: Failed to load fresh AdminState from storage');
        return;
      }
            
      let currentState = freshAdminState;

      // ── Ensure node string is populated for node-type segments ──────────────
      // adminState.node may be empty if the state was created before this field existed
      // or when starting on node 0 (which never goes through a NEXT_NODE: advance).
      const segTypeStr = String(adminSegment.SegmentType || adminSegment.segmentType || '');
      const isNodeSegment = segTypeStr === '5' || segTypeStr === 'PAS';
      if (isNodeSegment && !currentState.node) {
        const initNodes = adminSegment.Nodes || adminSegment.nodes;
        const initIdx = currentState.nodeIndex ?? 0;
        const initNodeId = initNodes?.[initIdx]?.NodeId || initNodes?.[initIdx]?.nodeId || '';
        if (initNodeId) {
          currentState = { ...currentState, node: initNodeId, nodeIndex: initIdx };
          await adminStateStorage.saveAdminState(currentState);
        }
      }
      // ────────────────────────────────────────────────────────────────────────

      if (shouldAdvance) {
        // First, advance to the next page number (handles segment navigation and completion)
        // Pass currentState (fresh from storage) to avoid stale React closure for nodeIndex/pageNum
        const nextPageNum = await findNextPageNum(currentState);
        if (!nextPageNum) {
          console.log('🏁 goToNextPage: No more pages/segments found, assessment completed');
          return;
        }
        
        if (nextPageNum === 'NEXT_SEGMENT') {
          // Handle segment navigation with CalcScript execution
          const currentSegmentId = adminState.segment;
          const currentSegmentIndex = availableSegments.findIndex(seg => seg === currentSegmentId);

          // For node segments: execute the leaving node's CalcScript before transitioning.
          // This covers the case where the last page of the last node exits to the next segment.
          const segTypeForExit = String(adminSegment?.SegmentType || adminSegment?.segmentType || '');
          const isNodeSegForExit = segTypeForExit === '5' || segTypeForExit === 'PAS';
          if (isNodeSegForExit && adminSegment) {
            const nodesForExit = adminSegment.Nodes || adminSegment.nodes;
            const exitNodeIdx = currentState.nodeIndex ?? 0;
            const exitNode = nodesForExit?.[exitNodeIdx];

            // PAS devage: compute developmental age scores BEFORE running CalcScript
            const exitNodeType = String((exitNode as any)?.NodeType || (exitNode as any)?.nodeType || '');
            if (exitNodeType === 'PAS' && getPasType(exitNode as AdminNodeDto) === 'devage') {
              console.log('🧮 goToNextPage: NEXT_SEGMENT exit — computing PAS devage scores for last node', (exitNode as any)?.Name);
              currentState = await computeAndStorePasDevageScores(exitNode as AdminNodeDto, currentState);
              await adminStateStorage.saveAdminState(currentState);
              setAdminState(currentState);
            }

            const exitNodeCalcScript = editedNodeCalcScriptRef.current || exitNode?.calcScript || exitNode?.CalcScript || '';
            if (exitNodeCalcScript.trim()) {
              console.log('🗺️ goToNextPage: Executing node CalcScript for last node', exitNodeIdx, 'before segment transition (blocking)');
              try {
                const exitNodeUpdates = await new Promise<{ dataUpdates: Array<{name: string, value: string}>, propertyUpdates: Array<{name: string, value: string}>, preloadUpdates: Array<{fieldCode: string, value: string, languageTypeId: string}> } | null>((resolve) => {
                  executeCalcScript(exitNodeCalcScript, currentState, (updates) => {
                    console.log('🗺️ goToNextPage: Last-node CalcScript completed before segment transition:', {
                      dataUpdates: updates.dataUpdates.length,
                      propertyUpdates: updates.propertyUpdates.length
                    });
                    resolve(updates);
                  }).catch(err => { console.error('🗺️ goToNextPage: ❌ Last-node CalcScript error:', err); resolve(null); });
                });
                if (exitNodeUpdates && (exitNodeUpdates.dataUpdates.length > 0 || exitNodeUpdates.propertyUpdates.length > 0)) {
                  currentState = await applyCalcScriptUpdatesSync(currentState, exitNodeUpdates);
                  await adminStateStorage.saveAdminState(currentState);
                  setAdminState(currentState);
                  console.log('🗺️ goToNextPage: ✅ Last-node CalcScript updates merged before segment transition');
                }
              } catch (exitCalcErr) {
                console.error('🗺️ goToNextPage: ❌ Unexpected error in last-node CalcScript:', exitCalcErr);
              }
            }
          }

          if (currentSegmentIndex >= 0 && currentSegmentIndex < availableSegments.length - 1) {
            const nextSegmentId = availableSegments[currentSegmentIndex + 1];

            // Capture the OLD segment's CalcScript NOW before loadAdminSegment replaces adminSegment
            const leavingSegmentCalcScript = editedCalcScript || adminSegment?.calcScript || adminSegment?.CalcScript || '';
            
            // Add BackTable entry for the last page of the departing segment BEFORE transitioning.
            // Without this, the segment-crossing page is never recorded in the BackTable.
            {
              const leavingPage = getCurrentPageFromSegment(currentState);
              const leavingPageTitle = leavingPage
                ? (leavingPage.name || leavingPage.Name || `Page ${currentState.pageNum}`)
                : `Page ${currentState.pageNum}`;
              const leavingEntry = {
                name: leavingPageTitle,
                value: '',
                segment: currentState.segment,
                pageNum: currentState.pageNum,
                nodeIndex: currentState.nodeIndex,
                node: currentState.node,
              };
              const lastBT = currentState.backTable?.[currentState.backTable.length - 1];
              if (!lastBT || lastBT.segment !== leavingEntry.segment || lastBT.pageNum !== leavingEntry.pageNum
                  || (lastBT.nodeIndex ?? 0) !== (leavingEntry.nodeIndex ?? 0)) {
                currentState = { ...currentState, backTable: [...(currentState.backTable ?? []), leavingEntry] };
                console.log('📝 goToNextPage: NEXT_SEGMENT — added BackTable entry for departing page:',
                  { name: leavingPageTitle, segment: leavingEntry.segment, pageNum: leavingEntry.pageNum, nodeIndex: leavingEntry.nodeIndex });
              }
            }
            
            // Set segment transition loading to prevent rendering conflicts
            setIsSegmentTransitioning(true);
            
            // Build updatedState from currentState (not adminState) so node CalcScript data updates are preserved
            const updatedState = { 
              ...currentState, 
              segment: nextSegmentId, 
              pageNum: '1',
              nodeIndex: 0
            };
            
            console.log('🗺️ goToNextPage: Creating updated state for segment transition:', {
              oldSegment: adminState.segment,
              newSegment: updatedState.segment,
              oldPageNum: adminState.pageNum,
              newPageNum: updatedState.pageNum
            });
            console.log('🗺️ goToNextPage: AdminState data BEFORE segment transition:', {
              dataCount: adminState.data?.length || 0,
              dataEntries: adminState.data?.map(d => ({ name: d.name, value: d.value, segment: d.segment, pageNum: d.pageNum })) || []
            });
            console.log('🗺️ goToNextPage: New AdminState data AFTER creating updated state:', {
              dataCount: updatedState.data?.length || 0,
              dataEntries: updatedState.data?.map(d => ({ name: d.name, value: d.value, segment: d.segment, pageNum: d.pageNum })) || []
            });
            
            // Save and update state
            await adminStateStorage.saveAdminState(updatedState);
            setAdminState(updatedState);
            setCurrentSegmentIndex(currentSegmentIndex + 1);
            
            console.log('🗺️ goToNextPage: AdminState saved and React state updated');
            
            // Load new segment content and wait for it to complete
            await loadAdminSegment(nextSegmentId, updatedState.languageTypeId || '1');
            
            // Ensure the state has the correct pageNum after segment loading
            let finalState = { 
              ...updatedState, 
              segment: nextSegmentId, 
              pageNum: '1' 
            };
            
            console.log('🗺️ goToNextPage: Final state BEFORE CalcScript updates:', {
              dataCount: finalState.data?.length || 0,
              dataEntries: finalState.data?.map(d => ({ name: d.name, value: d.value, segment: d.segment, pageNum: d.pageNum })) || []
            });
            
            console.log('🗺️ goToNextPage: Final state AFTER CalcScript updates:', {
              dataCount: finalState.data?.length || 0,
              dataEntries: finalState.data?.map(d => ({ name: d.name, value: d.value, segment: d.segment, pageNum: d.pageNum })) || []
            });
            
            await adminStateStorage.saveAdminState(finalState);
            currentState = finalState;
            setAdminState(finalState);
            
            // Clear segment transition loading after state is synchronized
            setIsSegmentTransitioning(false);
            
            console.log('✅ goToNextPage: Successfully navigated to segment', nextSegmentId, 'page', finalState.pageNum, '- final state set');
            
            // Now start leaving-segment CalcScript execution after successful navigation (avoids storage conflicts).
            // Use leavingSegmentCalcScript captured BEFORE loadAdminSegment replaced adminSegment.
            if (leavingSegmentCalcScript.trim()) {
              const calcScript = leavingSegmentCalcScript;
              
              if (calcScript.trim()) {
                console.log('📁 goToNextPage: Starting segment CalcScript execution after successful navigation:', {
                  fromSegment: currentSegmentId,
                  toSegment: nextSegmentId,
                  calcScriptLength: calcScript.length,
                  isEdited: !!editedCalcScript
                });

                // Execute CalcScript in background - do NOT await (non-blocking)
                // CalcScript state updates will use separate storage to avoid conflicts with main content loading
                executeCalcScript(calcScript, finalState, (updates) => {
                  console.log('📁 goToNextPage: CalcScript execution completed AFTER navigation:', {
                    dataUpdates: updates.dataUpdates.length,
                    propertyUpdates: updates.propertyUpdates.length,
                    preloadUpdates: updates.preloadUpdates.length
                  });
                  
                  // Store updates and set flag for main thread to apply them
                  setPendingCalcUpdates(updates);
                  setCalcScriptUpdatesReady(true);
                }).catch(error => {
                  console.error('📁 goToNextPage: ❌ Error in CalcScript execution after navigation:', error);
                });
                
                console.log('📁 goToNextPage: ✅ CalcScript started after navigation, execution in background');
              } else {
                console.log('📁 goToNextPage: No CalcScript found for segment transition');
              }
            }

            // Segment transition is fully committed. Return now so that the second while-loop
            // below does NOT execute with a stale adminSegment closure (the React closure still
            // points to the OLD segment's data). The adminSegment useEffect will fire a fresh
            // goToNextPage(false) call 50 ms later, using the correct new adminSegment closure.
            justAdvancedSegmentRef.current = true;
            console.log('📁 goToNextPage: Returning after segment transition — evaluation deferred to next call');
            return;

          } else {
            console.log('🏁 goToNextPage: No more segments available, completing assessment');
            const completedState = { ...adminState, status: '5' };
            await adminStateStorage.saveAdminState(completedState);
            setAdminState(completedState);
            return;
          }
        } else if (nextPageNum && nextPageNum.startsWith('NEXT_NODE:')) {
          // Execute node-level CalcScript for the node we are LEAVING before advancing.
          // Unlike segment transitions, no new segment is loaded here so execution is BLOCKING:
          // we await the result and merge updates directly into currentState before advancing.
          const leavingNodeIdx = currentState.nodeIndex ?? 0;
          const nodesForCalc = adminSegment?.Nodes || adminSegment?.nodes;
          const leavingNode = nodesForCalc?.[leavingNodeIdx];

          // PAS devage: compute developmental age scores BEFORE running CalcScript so
          // that CalcScript can read the freshly computed score variables.
          const leavingNodeType = String((leavingNode as any)?.NodeType || (leavingNode as any)?.nodeType || '');
          if (leavingNodeType === 'PAS' && getPasType(leavingNode as AdminNodeDto) === 'devage') {
            console.log('🧮 goToNextPage: NEXT_NODE — computing PAS devage scores for node', (leavingNode as any)?.Name);
            currentState = await computeAndStorePasDevageScores(leavingNode as AdminNodeDto, currentState);
            await adminStateStorage.saveAdminState(currentState);
            setAdminState(currentState);
          }

          const nodeCalcScriptToRun = editedNodeCalcScriptRef.current || leavingNode?.calcScript || leavingNode?.CalcScript || '';
          if (nodeCalcScriptToRun.trim()) {
            console.log('🔗 goToNextPage: Executing node CalcScript for node', leavingNodeIdx, '(blocking — applies before node advance)');
            try {
              const nodeCalcUpdates = await new Promise<{ dataUpdates: Array<{name: string, value: string}>, propertyUpdates: Array<{name: string, value: string}>, preloadUpdates: Array<{fieldCode: string, value: string, languageTypeId: string}> } | null>((resolve) => {
                executeCalcScript(nodeCalcScriptToRun, currentState, (updates) => {
                  console.log('🔗 goToNextPage: Node CalcScript execution completed:', {
                    dataUpdates: updates.dataUpdates.length,
                    propertyUpdates: updates.propertyUpdates.length
                  });
                  resolve(updates);
                }).catch(error => {
                  console.error('🔗 goToNextPage: ❌ Error in node CalcScript execution:', error);
                  resolve(null);
                });
              });

              if (nodeCalcUpdates && (nodeCalcUpdates.dataUpdates.length > 0 || nodeCalcUpdates.propertyUpdates.length > 0)) {
                currentState = await applyCalcScriptUpdatesSync(currentState, nodeCalcUpdates);
                await adminStateStorage.saveAdminState(currentState);
                setAdminState(currentState);
                console.log('🔗 goToNextPage: ✅ Node CalcScript updates merged into currentState before node advance');
              }
            } catch (calcError) {
              console.error('🔗 goToNextPage: ❌ Unexpected error applying node CalcScript:', calcError);
            }
          }

          // Advance to first page of the next node in the same segment
          const parts = nextPageNum.split(':');
          const nextNodeIdx = parseInt(parts[1], 10);
          const firstPageNum = parts[2];
          const advNodes = adminSegment?.Nodes || adminSegment?.nodes;
          const advNodeId = advNodes?.[nextNodeIdx]?.NodeId || advNodes?.[nextNodeIdx]?.nodeId || '';
          const updatedState = { ...currentState, nodeIndex: nextNodeIdx, node: advNodeId, pageNum: firstPageNum };
          await adminStateStorage.saveAdminState(updatedState);
          setAdminState(updatedState);
          currentState = updatedState;
          console.log('🔗 goToNextPage: Advanced to node', nextNodeIdx, advNodeId, 'page', firstPageNum);

          // In single-step mode, proactively load the new node-page's preScript into
          // the Page Logic editor.  Page-change effects fire asynchronously after commit;
          // doing this here ensures the editor is in sync with the new page immediately.
          if (editPageLogicEnabled) {
            const segSrc = segmentOverrideForNavRef.current || adminSegment;
            const advNodes2 = segSrc?.Nodes || (segSrc as any)?.nodes;
            const advNodePages: any[] = (advNodes2?.[nextNodeIdx]?.Pages || (advNodes2?.[nextNodeIdx] as any)?.pages) || [];
            const advFirstPage = advNodePages.find((p: any) => String(p.PageNum || p.pageNum || '') === String(firstPageNum));
            if (advFirstPage) {
              const advPreScript = advFirstPage.editedPreScript || advFirstPage.preScript || advFirstPage.PreScript || '';
              console.log('🔗 goToNextPage: Preloading Page Logic editor for node', nextNodeIdx, 'page', firstPageNum, '— preScript length:', advPreScript.length);
              preScriptInitialized.current = false; // allow PAGE-CHANGE-EFFECT to re-run cleanly
              setEditedPreScriptWithRef(advPreScript);
              setPreScriptDirty(false);
              setPreScriptExecuted(false);
            }
          }
        } else {
          // Regular page advancement within current segment
          const updatedState = { ...currentState, pageNum: nextPageNum };
          await adminStateStorage.saveAdminState(updatedState);
          setAdminState(updatedState);
          currentState = updatedState;
        }
      }
      
      // Iteratively call evaluateCurrentPage until we find a renderable page or complete
      let maxIterations = 50; // Safety limit to prevent infinite loops
      let currentIteration = 0;
      // In Page Logic edit mode, forceExecuteScript applies ONLY to the first iteration
      // (the page the user clicked Continue on). Subsequent pages must respect the edit-mode gate
      // so the loop stops at the next displayable page rather than cascading through the whole assessment.
      let effectiveForceExecute = forceExecuteScript;
      
      while (currentIteration < maxIterations) {
        // For evaluation-only mode, use current state; for advance mode, reload latest state
        if (shouldAdvance) {
          const reloadedState = await adminStateStorage.loadAdminState(profileId, actualSourceId);
          if (!reloadedState) break;
          currentState = reloadedState;
        }
        
        // Check if assessment is now complete
        if (currentState.status === '5') {
          console.log('✅ goToNextPage: Assessment completed during evaluation');
          setAdminState(currentState);
          return;
        }
        
        // Evaluate current page to get navigation instructions
        const evaluationResult = await evaluateCurrentPage(currentState, effectiveForceExecute);

        // In Page Logic edit mode, clear the force-execute flag after the first iteration so that
        // the loop does NOT auto-run PreScripts on pages the user has not explicitly continued past.
        if (editPageLogicEnabled && effectiveForceExecute) {
          effectiveForceExecute = false;
        }
        
        // Update current state from evaluation (may have been modified by PreScript)
        currentState = evaluationResult.modifiedState;

        // Apply this page's putPreload calls on top of whatever's already there — like
        // putData, a value set here persists for the rest of the assessment (across pages/
        // segments) until some later putPreload overwrites that same fieldCode again.
        if (evaluationResult.preloadUpdates) {
          evaluationResult.preloadUpdates.forEach(({ fieldCode, value }) => {
            runtimePreloadOverridesRef.current.set(fieldCode, value);
          });
        }

        
        // Check if page should be rendered
        if (evaluationResult.shouldRender) {
          
          // Clear any previous cText/enableConsent/disableBack/disableSave/enableDone/
          // renderReport from adminState first, then store the new values only if provided by
          // the current page's PreScript
          currentState = { ...currentState } as AdminStateDto & { cText?: string; enableConsent?: number; disableBack?: number; disableSave?: number; enableDone?: string; renderReport?: string; scriptExecutedForPage?: boolean };
          delete (currentState as any).cText; // Clear previous cText
          delete (currentState as any).enableConsent; // Clear previous enableConsent
          delete (currentState as any).disableBack; // Clear previous disableBack
          delete (currentState as any).disableSave; // Clear previous disableSave
          delete (currentState as any).enableDone; // Clear previous enableDone
          delete (currentState as any).renderReport; // Clear previous renderReport

          // Durable (survives to the next render, unlike the one-shot preScriptExecuted
          // state) record of whether THIS page's PreScript actually ran on this pass — lets
          // performPreloadSubstitutions show substituted tokens once the author has clicked
          // Continue in edit mode, while still showing raw tokens before that first click.
          (currentState as any).scriptExecutedForPage = !!evaluationResult.scriptExecuted;

          if (evaluationResult.cText && evaluationResult.cText.trim() !== '') {
            (currentState as any).cText = evaluationResult.cText;
          }

          if (evaluationResult.enableConsent && evaluationResult.enableConsent !== 0) {
            (currentState as any).enableConsent = evaluationResult.enableConsent;
          }

          if (evaluationResult.disableBack && evaluationResult.disableBack !== 0) {
            (currentState as any).disableBack = evaluationResult.disableBack;
          }

          if (evaluationResult.disableSave && evaluationResult.disableSave !== 0) {
            (currentState as any).disableSave = evaluationResult.disableSave;
          }

          if (evaluationResult.enableDone && evaluationResult.enableDone.trim() !== '') {
            (currentState as any).enableDone = evaluationResult.enableDone;
          }

          if (evaluationResult.renderReport && evaluationResult.renderReport.trim() !== '') {
            (currentState as any).renderReport = evaluationResult.renderReport;
          }

          // Get the current page for BackTable entry
          const currentPage = getCurrentPageFromSegment(currentState);
          if (currentPage) {
            // Add BackTable entry with adminState pageNum as source of truth
            const pageTitle = currentPage.name || currentPage.Name || `Page ${currentState.pageNum}`;
            const currentBackTableEntry = {
              name: pageTitle, // Display name for UI purposes
              value: '', // Value should be ignored per requirements
              segment: currentState.segment,
              pageNum: currentState.pageNum, // adminState pageNum is source of truth
              nodeIndex: currentState.nodeIndex,
              node: currentState.node
            };
            
            // Check if this entry already exists to avoid duplicates
            // Note: BackTable uses page names (AdminPageDto.name), Data uses mvar names (AdminQuestionDto.mvarName)
            // Duplicate check should only compare segment/pageNum, NOT names (they serve different purposes)
            const lastEntry = currentState.backTable[currentState.backTable.length - 1];
            
            if (!lastEntry || lastEntry.segment !== currentState.segment || lastEntry.pageNum !== currentState.pageNum
                || (lastEntry.nodeIndex ?? 0) !== (currentState.nodeIndex ?? 0)) {
              // Create new state object with new BackTable array to trigger React re-render
              currentState = {
                ...currentState,
                backTable: [...currentState.backTable, currentBackTableEntry]
              };
            } 
          }
          
          // Save the updated AdminState with the BackTable entry and trigger React re-render
          await adminStateStorage.saveAdminState(currentState);          
          setAdminState(currentState);
          return;
        }
        
        // Page needs navigation - use the instructions from evaluation
        // if (!shouldAdvance) {
        //   console.log('📋 goToNextPage: Evaluation-only mode - page not renderable, skipping navigation');
        //   return; // Don't navigate in evaluation-only mode
        // }
        
        // Find next page using navigation instructions
        const nextPageToAdvanceTo = findNextPageSync(currentState, evaluationResult.skipPage, evaluationResult.gotoPage, evaluationResult.skipNode);
        // console.log('🎯 goToNextPage: findNextPageSync returned:', nextPageToAdvanceTo);
        
        if (nextPageToAdvanceTo === 'NEXT_SEGMENT') {
          // If we are leaving a node segment, execute the last node's CalcScript before
          // transitioning — mirrors the NEXT_NODE: handler which fires for every other node.
          const nsSegKey = `${currentState.segment}-${currentState.languageTypeId || '1'}`;
          const nsCachedSeg = processedContentCache.current.get(nsSegKey) || segmentOverrideForNavRef.current;
          const nsNodes = nsCachedSeg?.Nodes || nsCachedSeg?.nodes || adminSegment?.Nodes || adminSegment?.nodes;
          if (nsNodes && nsNodes.length > 0) {
            const nsLeavingNodeIdx = currentState.nodeIndex ?? 0;
            const nsLeavingNode = nsNodes[nsLeavingNodeIdx];
            const nsNodeCalcScript = editedNodeCalcScriptRef.current || nsLeavingNode?.CalcScript || nsLeavingNode?.calcScript || '';
            if (nsNodeCalcScript.trim()) {
              console.log('🔗 goToNextPage (NEXT_SEGMENT): Executing last-node CalcScript for node', nsLeavingNodeIdx, '(blocking — applies before segment transition)');
              try {
                const nsNodeCalcUpdates = await new Promise<{ dataUpdates: Array<{name: string, value: string}>, propertyUpdates: Array<{name: string, value: string}>, preloadUpdates: Array<{fieldCode: string, value: string, languageTypeId: string}> } | null>((resolve) => {
                  executeCalcScript(nsNodeCalcScript, currentState, (updates) => {
                    console.log('🔗 goToNextPage (NEXT_SEGMENT): Last-node CalcScript completed:', { dataUpdates: updates.dataUpdates.length, propertyUpdates: updates.propertyUpdates.length });
                    resolve(updates);
                  }).catch(err => { console.error('🔗 goToNextPage (NEXT_SEGMENT): ❌ Error in last-node CalcScript:', err); resolve(null); });
                });
                if (nsNodeCalcUpdates && (nsNodeCalcUpdates.dataUpdates.length > 0 || nsNodeCalcUpdates.propertyUpdates.length > 0)) {
                  currentState = await applyCalcScriptUpdatesSync(currentState, nsNodeCalcUpdates);
                  await adminStateStorage.saveAdminState(currentState);
                  console.log('🔗 goToNextPage (NEXT_SEGMENT): ✅ Last-node CalcScript updates merged into currentState');
                }
              } catch (calcErr) {
                console.error('🔗 goToNextPage (NEXT_SEGMENT): ❌ Unexpected error applying last-node CalcScript:', calcErr);
              }
            }
          }

          // Find current segment index in availableSegments array
          const currentSegmentId = currentState.segment;
          const currentSegmentIndex = availableSegments.findIndex(seg => seg === currentSegmentId);
          
          if (currentSegmentIndex >= 0 && currentSegmentIndex < availableSegments.length - 1) {
            const nextSegmentId = availableSegments[currentSegmentIndex + 1];
            try {
              // Fully await the segment transition. performSegmentTransition only resolves when
              // the new segment content is loaded and segmentOverrideForNavRef.current holds the
              // new segment's data. The next loop iteration evaluates PreScript only AFTER this.
              currentState = await performSegmentTransition(currentState, nextSegmentId, currentSegmentIndex + 1);
            } catch (error) {
              console.error('❌ goToNextPage: Error during segment transition:', error);
              break;
            }
          } else {
            // No more segments available, complete assessment
            console.log('🏁 goToNextPage: No more segments, completing assessment');
            const completedState = { ...currentState, status: '5' };
            await adminStateStorage.saveAdminState(completedState);
            setAdminState(completedState);
            return;
          }
        } else if (nextPageToAdvanceTo.startsWith('NEXT_NODE:')) {
          // Advance to first page of next node within current segment
          const parts = nextPageToAdvanceTo.split(':');
          const nextNodeIdx = parseInt(parts[1], 10);
          const firstPageNum = parts[2];
          // Resolve nodes from the synchronously-updated processedContentCache rather than the
          // stale adminSegment React closure (which may still refer to a previous segment when
          // a NEXT_SEGMENT transition happened earlier in this same goToNextPage loop run).
          const loopNodeSegKey = `${currentState.segment}-${currentState.languageTypeId || '1'}`;
          const cachedLoopSeg = processedContentCache.current.get(loopNodeSegKey) || segmentOverrideForNavRef.current;
          const loopNodes = (cachedLoopSeg?.Nodes || cachedLoopSeg?.nodes) || adminSegment?.Nodes || adminSegment?.nodes;

          // Execute leaving node's CalcScript before advancing (mirrors Part-1 NEXT_NODE handler).
          // editedNodeCalcScriptRef.current is still pointing to the leaving node's script here
          // because setAdminState has not been called yet in this loop iteration, so the
          // nodeCalcScript useEffect has not had a chance to fire for the NEW node.
          const loopLeavingNodeIdx = currentState.nodeIndex ?? 0;
          const loopLeavingNode = loopNodes?.[loopLeavingNodeIdx];
          const loopNodeCalcScript = editedNodeCalcScriptRef.current || loopLeavingNode?.CalcScript || loopLeavingNode?.calcScript || '';
          if (loopNodeCalcScript.trim()) {
            console.log('🔗 goToNextPage (loop): Executing node CalcScript for node', loopLeavingNodeIdx, '(blocking — applies before node advance)');
            try {
              const loopNodeCalcUpdates = await new Promise<{ dataUpdates: Array<{name: string, value: string}>, propertyUpdates: Array<{name: string, value: string}>, preloadUpdates: Array<{fieldCode: string, value: string, languageTypeId: string}> } | null>((resolve) => {
                executeCalcScript(loopNodeCalcScript, currentState, (updates) => {
                  console.log('🔗 goToNextPage (loop): Node CalcScript execution completed:', {
                    dataUpdates: updates.dataUpdates.length,
                    propertyUpdates: updates.propertyUpdates.length
                  });
                  resolve(updates);
                }).catch(error => {
                  console.error('🔗 goToNextPage (loop): ❌ Error in node CalcScript execution:', error);
                  resolve(null);
                });
              });
              if (loopNodeCalcUpdates && (loopNodeCalcUpdates.dataUpdates.length > 0 || loopNodeCalcUpdates.propertyUpdates.length > 0)) {
                currentState = await applyCalcScriptUpdatesSync(currentState, loopNodeCalcUpdates);
                await adminStateStorage.saveAdminState(currentState);
                console.log('🔗 goToNextPage (loop): ✅ Node CalcScript updates merged into currentState before node advance');
              }
            } catch (calcError) {
              console.error('🔗 goToNextPage (loop): ❌ Unexpected error applying node CalcScript:', calcError);
            }
          }

          const loopNodeId = loopNodes?.[nextNodeIdx]?.NodeId || loopNodes?.[nextNodeIdx]?.nodeId || '';
          currentState = { ...currentState, nodeIndex: nextNodeIdx, node: loopNodeId, pageNum: firstPageNum };
          await adminStateStorage.saveAdminState(currentState);
        } else {
          // Advance to specific page within current segment (or current node for type 5)
          currentState = { ...currentState, pageNum: nextPageToAdvanceTo };
          await adminStateStorage.saveAdminState(currentState);
        }
        
        currentIteration++;
        // console.log('🔄 goToNextPage: Iteration', currentIteration, '- continuing evaluation...');
      }
      
      // If we hit max iterations, something may be wrong
      if (currentIteration >= maxIterations) {
        console.error('❌ goToNextPage: Hit maximum iterations, stopping to prevent infinite loop');
      }
      
    } catch (error) {
      console.error('❌ Failed to navigate to next page:', error);
    } finally {
      // performSegmentTransition (Step 4, above) sets this ref to bridge the gap between
      // processedContentCache being updated synchronously and adminSegment's React state
      // re-render landing — but nothing was clearing it afterward for the forward-navigation
      // path (unlike goToPreviousPage, which sets *and* clears it within itself). Left set,
      // getCurrentPageFromSegment() keeps preferring this point-in-time snapshot of the
      // segment over the live adminSegment state for the rest of the session, so any later
      // in-memory edit (savePreScriptEdits, savePageLogicToBackend's dirty-clearing, etc.) —
      // which only ever updates adminSegment — becomes invisible to it. By the time this
      // function returns, adminSegment has had every opportunity to re-render, so it's safe
      // to drop back to it as the sole source of truth.
      segmentOverrideForNavRef.current = null;
      isNavigatingRef.current = false;
    }
  };

  const goToPreviousPage = async () => {
    // Check for any completed CalcScript updates from background processing before proceeding
    await applyPendingCalcScriptUpdates();

    // Flush any unsaved PreScript edits to adminSegment + local storage before navigating
    if (editPageLogicEnabled && preScriptDirty && adminState) {
      savePreScriptEdits(adminState.pageNum, editedPreScriptRef.current);
    }

    if (!adminStateStorage || !adminState || !accountId || !profileId || !sourceId) return;
    
    try {
      // Simplified BackTable processing - AdminState is sole source of truth
      if (!adminState.backTable || adminState.backTable.length <= 1) {
        console.log('🔄 goToPreviousPage: Only one item in BackTable, returning with no action');
        return;
      }
      
      // More than one item - load segment and pagenum from BackTable into AdminState
      const targetEntry = adminState.backTable[adminState.backTable.length - 2]; // Get previous page
      const entryToRemove = adminState.backTable[adminState.backTable.length - 1]; // Entry being removed
      
      // Remove data entries that match the BackTable entry being removed.
      // For node segments, also match on nodeIndex so that page N of node 1 does not
      // accidentally delete page N of node 0 (same segment + pageNum, different node).
      const removedNames = new Set<string>();
      const filteredData = (adminState.data ?? []).filter(dataEntry => {
        const segmentMatch = dataEntry.segment === entryToRemove.segment;
        const pageMatch    = dataEntry.pageNum  === entryToRemove.pageNum;
        const nodeMatch    =
          entryToRemove.nodeIndex !== undefined
            ? dataEntry.nodeIndex === entryToRemove.nodeIndex
            : true; // flat segment — no nodeIndex to distinguish
        const shouldRemove = segmentMatch && pageMatch && nodeMatch;
        if (shouldRemove) removedNames.add(dataEntry.name);
        return !shouldRemove;
      });

      // handleAnswerChange mirrors every answer into adminState.prefill too ("for consistency
      // and debugging" — see updatePrefillData). Prefill entries carry no segment/pageNum/
      // nodeIndex of their own so they can't be filtered the same way above, but they share the
      // same name as their data-array counterpart. Without this, getAnswerData's fallback to
      // prefill (when a name is missing from data) resurrects whatever value was here before
      // this page was backed out of — a control reading it via getAnswerValue would still show
      // the "removed" answer, so re-selecting the same value never fires an onChange (nothing
      // looks changed) and the data-array entry this was meant to clear never gets restored.
      // Exactly the bug the <<dt:...>> date control hit after Back removed its data entries.
      const filteredPrefill = (adminState.prefill ?? []).filter(p => !removedNames.has(p.name));

      // Update AdminState with values from BackTable
      const updatedState = {
        ...adminState,
        segment: targetEntry.segment,
        pageNum: targetEntry.pageNum,
        nodeIndex: targetEntry.nodeIndex,
        node: targetEntry.node ?? '',
        // Remove data entries from the page we're leaving
        data: filteredData,
        // Keep prefill in sync with data (see comment above) so a removed answer can't
        // reappear via getAnswerData's prefill fallback.
        prefill: filteredPrefill,
        // Remove last item from BackTable
        backTable: adminState.backTable.slice(0, -1)
      };
      
      // If segment changed, load it
      if (targetEntry.segment !== adminState.segment) {
        console.log('🔄 goToPreviousPage: Loading segment:', targetEntry.segment);
        const newSegmentIndex = availableSegments.findIndex(seg => seg === targetEntry.segment);
        if (newSegmentIndex >= 0) {
          setCurrentSegmentIndex(newSegmentIndex);
        }
        await loadAdminSegment(targetEntry.segment, adminState.languageTypeId || '1');

        // processAdminContent updates processedContentCache synchronously but setAdminSegment
        // is a React state update — adminSegment closure is still the old segment here.
        // Populate the override ref with the with-edits version so PreScript edits carry through.
        const prevCacheKey = `${targetEntry.segment}-${adminState.languageTypeId || '1'}`;
        segmentOverrideForNavRef.current = processedContentWithEditsCacheRef.current.get(prevCacheKey) || processedContentCache.current.get(prevCacheKey) || null;
      }
      
      // Save and update AdminState (sole source of truth)
      await adminStateStorage.saveAdminState(updatedState);
      setAdminState(updatedState);

      // Evaluate the restored page's PreScript — if it produces skipPage/gotoPage the
      // user is re-forwarded past it rather than left stranded on an auto-skip page.
      await goToNextPage(false);
      segmentOverrideForNavRef.current = null; // clear after evaluation is done
    } catch (error) {
      console.error('❌ Failed to navigate to previous page:', error);
    }
  };

  // New segment navigation functions
  const goToNextSegment = async () => {
    // Check for any completed CalcScript updates from background processing before proceeding
    await applyPendingCalcScriptUpdates();
    
    if (!adminStateStorage || !adminState) return;
    
    // Find current segment index in availableSegments array
    const currentSegmentId = adminState.segment;
    const currentSegmentIndex = availableSegments.findIndex(seg => seg === currentSegmentId);
    
    if (currentSegmentIndex >= 0 && currentSegmentIndex < availableSegments.length - 1) {
      // Get the next segment ID from availableSegments array
      const nextSegmentId = availableSegments[currentSegmentIndex + 1];
      
      // Create updated state with next segment ID and reset to page 1
      const updatedState = { 
        ...adminState, 
        segment: nextSegmentId, 
        pageNum: '1' 
      };
      
      // Save and update state
      await adminStateStorage.saveAdminState(updatedState);
      setAdminState(updatedState);
      setCurrentSegmentIndex(currentSegmentIndex + 1);
      // AdminState pageNum will be updated via the navigation flow
      
      // Update local AdminState to reflect the new segment and page
      setAdminState(updatedState);
      
      // Load new segment content if needed
      await loadSegmentContent(updatedState.segment);
      
      console.log('🔄 Navigated to next segment:', {
        segment: updatedState.segment,
        pageNum: updatedState.pageNum
      });
    }
  };

  const goToPreviousSegment = async () => {
    // Check for any completed CalcScript updates from background processing before proceeding
    await applyPendingCalcScriptUpdates();
    
    if (!adminStateStorage || !adminState) return;
    
    const updatedState = await adminStateStorage.navigateToPreviousSegment(
      accountId,
      profileId,
      actualSourceId!
    );
    
    if (updatedState) {
      setAdminState(updatedState);
      const newSegmentIndex = parseInt(updatedState.segment) - 1;
      setCurrentSegmentIndex(newSegmentIndex);
      // AdminState pageNum will be updated via the navigation flow
      
      // Update local AdminState to reflect the previous segment and page
      setAdminState(updatedState);
      
      // Load previous segment content if needed
      await loadSegmentContent(updatedState.segment);
      
      console.log('🔄 Navigated to previous segment:', {
        segment: updatedState.segment,
        pageNum: updatedState.pageNum
      });
    }
  };

  // Load segment content based on segment number
  const loadSegmentContent = async (segmentNumber: string) => {
    // For now, we'll use the same assessment ID
    // In a full implementation, this would load different segment content
    console.log(`🔄 Loading segment ${segmentNumber} content...`);
    
    // This is where you would load the specific AdminSegment for the segment
    // For now, we'll keep using the same segment
  };

  // Answer management functions
  const handleAnswerChange = async (questionId: string, mvarName: string, value: any, isMultiple: boolean = false) => {
    // Check for any completed CalcScript updates from background processing before proceeding
    await applyPendingCalcScriptUpdates();
    
    if (!adminStateStorage || !adminState) return;

    try {
      // Get the actual page number from current AdminPageDto
      const currentAdminPage = getCurrentPageFromSegment();
      const actualPageNum = currentAdminPage ? 
        (currentAdminPage.pageNum || currentAdminPage.PageNum || adminState.pageNum) : 
        adminState.pageNum;
      
      // Handle multiple choice answers (checkboxes)
      // Storage format: one admindata entry per answer option, key = mvarName_dataValue
      // Checked → value = dataValue; Unchecked → value = ''
      if (isMultiple) {
        const compoundKey = `${mvarName}_${String(value)}`;
        const currentVal = adminStateStorage.getAnswerData(adminState, compoundKey, adminState.segment);
        // Toggle: if the compound key already holds this dataValue, uncheck (store ''); otherwise check (store value)
        const newValue = (currentVal === String(value)) ? '' : String(value);
        
        await adminStateStorage.updateAnswerData(
          accountId,
          profileId,
          actualSourceId!,
          compoundKey,
          newValue,
          adminState.segment,
          actualPageNum,
          adminState.nodeIndex
        );
        
        // Also update prefill array for consistency and debugging
        await adminStateStorage.updatePrefillData(
          accountId,
          profileId,
          actualSourceId!,
          compoundKey,
          newValue
        );
        
        // Refresh local state to show updated data in debug info
        const updatedState = await adminStateStorage.loadAdminState(profileId, actualSourceId!);
        if (updatedState) {
          setAdminState(updatedState);
        }
      } else {
        // Handle single choice, text, date, memo answers
        await adminStateStorage.updateAnswerData(
          accountId,
          profileId,
          actualSourceId!,
          mvarName,
          String(value),
          adminState.segment,
          actualPageNum,
          adminState.nodeIndex
        );
        
        // Also update prefill array for consistency and debugging
        await adminStateStorage.updatePrefillData(
          accountId,
          profileId,
          actualSourceId!,
          mvarName,
          String(value)
        );
        
        // Refresh local state to show updated data in debug info
        const updatedState = await adminStateStorage.loadAdminState(profileId, actualSourceId!);
        if (updatedState) {
          setAdminState(updatedState);
        }
      }
      
      // Don't refresh AdminState here - let goToNextPage handle navigation and state updates

    } catch (error) {
      console.error('❌ Failed to save answer:', error);
    }
  };

  // Consent gate (enableConsent PreScript flag) Agree/Exit handler — records APPM_Disclaimer
  // ('1' = Agree, '2' = Exit) into AdminState.data the same way a normal single-answer question
  // does, then advances exactly like Continue would.
  const handleConsentAnswer = async (disclaimerValue: '1' | '2') => {
    await handleAnswerChange('appm_disclaimer', 'APPM_Disclaimer', disclaimerValue, false);
    await goToNextPage();
  };

  const getAnswerValue = (mvarName: string) => {
    if (adminState && adminStateStorage) {
      const storedValue = adminStateStorage.getAnswerData(adminState, mvarName, adminState.segment);
      if (storedValue) return storedValue;
    }
    return segmentAnswers[mvarName];
  };

  const isAnswerSelected = (mvarName: string, value: any) => {
    // New format: compound key mvarName_dataValue stores the dataValue when checked, '' when unchecked
    if (adminState && adminStateStorage) {
      const compoundKey = `${mvarName}_${String(value)}`;
      const compoundVal = adminStateStorage.getAnswerData(adminState, compoundKey, adminState.segment);
      if (compoundVal !== null) {
        // Key exists in new compound-key format — non-empty value matching dataValue means checked
        return compoundVal === String(value);
      }
    }
    // Fallback: old comma-delimited format (backward compat with previously stored data)
    const currentValue = getAnswerValue(mvarName);
    if (typeof currentValue === 'string' && currentValue.includes(',')) {
      return currentValue.split(',').includes(String(value));
    }
    return currentValue === String(value);
  };

  // Helper function to check if current page uses submit button mode (single answer questions)
  const currentPageUsesSubmitButtons = () => {
    const currentAdminPage = getCurrentPageFromSegment();
    if (!currentAdminPage) return false;
    
    const questions = currentAdminPage.questions || currentAdminPage.Questions;
    if (!questions || !Array.isArray(questions)) return false;
    
    // Check if any question on this page is single answer type (mvarTypeId = 1)
    return questions.some((questionItem: any) => {
      const question = questionItem.question || questionItem.Question;
      if (!question) return false;
      const mvarTypeId = question.MvarTypeId || question.mvarTypeId;
      return parseInt(mvarTypeId || '1') === 1;
    });
  };

  // Helper function to check if Next button should be hidden
  // Only hide when: 1) AdminPageDto Type >= 3 AND 2) MvarTypeId = 1 (single answer) AND 3) AdminHtmlAnswerDto array size > 0
  const shouldHideNextButton = () => {
    const currentAdminPage = getCurrentPageFromSegment();
    if (!currentAdminPage) {
      return false;
    }
    
    // Check if this is a question page (Type >= 3)
    const pageType = currentAdminPage.type || currentAdminPage.Type;
    const pageTypeNum = parseInt(pageType || '1');
    if (pageTypeNum < 3) {
      return false;
    }
    
    const questions = currentAdminPage.questions || currentAdminPage.Questions;
    if (!questions || !Array.isArray(questions)) {
      return false;
    }
    

    
    const shouldHide = questions.some((questionItem: any) => {
      const question = questionItem.question || questionItem.Question;
      if (!question) {
        return false;
      }
      
      // Check both camelCase and PascalCase for MvarTypeId (server sends PascalCase)
      const mvarTypeId = question.MvarTypeId || question.mvarTypeId;
      
      // Check for answers in multiple possible locations based on actual data structure
      // From the rendering code, htmlQuestions are stored as question.htmlQuestion or question.HtmlQuestion
      const htmlQuestions = question.htmlQuestion || question.HtmlQuestion || [];
      let hasAnswersInAnyHtmlQuestion = false;
      

      
      if (Array.isArray(htmlQuestions) && htmlQuestions.length > 0) {
        hasAnswersInAnyHtmlQuestion = htmlQuestions.some((htmlQuestion: any) => {
          const htmlAnswers = htmlQuestion.htmlAnswer || htmlQuestion.HtmlAnswer || [];

          return Array.isArray(htmlAnswers) && htmlAnswers.length > 0;
        });
      } else {
        // Fallback to direct answer checking
        const answers = question.answers || question.Answers || 
                       questionItem.htmlAnswer || questionItem.HtmlAnswer ||
                       [];
        hasAnswersInAnyHtmlQuestion = answers && Array.isArray(answers) && answers.length > 0;
        console.log('🔍 Using fallback answer checking:', {
          answersCount: Array.isArray(answers) ? answers.length : 0,
          hasAnswersInAnyHtmlQuestion
        });
      }
      
      const typeId = parseInt(mvarTypeId || '1');
      
      // Only hide Next button if MvarTypeId = 1 AND has answers available
      return typeId === 1 && hasAnswersInAnyHtmlQuestion;
    });
    
    return shouldHide;
  };

  // enableConsent PreScript flag (pageType 1 only) — Agree/Exit answer set replaces Continue.
  const isConsentGateActive = () => {
    const currentAdminPage = getCurrentPageFromSegment();
    if (!currentAdminPage) {
      return false;
    }
    const pageType = currentAdminPage.type || currentAdminPage.Type;
    const pageTypeNum = parseInt(pageType || '1');
    return pageTypeNum === 1 && !!(adminState as any)?.enableConsent;
  };

  // disableBack/disableSave PreScript flags — hide (while preserving footer layout spacing)
  // the Back arrow / 'Save and finish later' footer controls, respectively.
  const isBackButtonHiddenByScript = () => !!(adminState as any)?.disableBack;
  const isSaveLinkHiddenByScript = () => !!(adminState as any)?.disableSave;

  // enableDone PreScript flag — the footer Done button stays disabled on
  // isLastPageOfLastSegment() pages until close-segment logic explicitly enables it with a
  // label (e.g. 'Submit', 'Finish', 'Exit Now'). Returns the configured label, or '' if unset.
  const isDoneEnabledByScript = () => (adminState as any)?.enableDone || '';

  // Helper functions to check navigation availability
  // Helper function to check if we're on the last page of the last segment
  const isLastPageOfLastSegment = useCallback(() => {
    if (!adminSegment || !adminState) return false;
    const segType = String(adminSegment.segmentType || adminSegment.SegmentType || '');
    const isNodeSeg = segType === '5' || segType === 'PAS';

    // Check if we're on the last segment
    const currentSegmentIndex = availableSegments.findIndex(seg => seg === adminState.segment);
    const isLastSegment = currentSegmentIndex === availableSegments.length - 1;
    if (!isLastSegment) return false;

    if (isNodeSeg) {
      const nodes = adminSegment.Nodes || adminSegment.nodes || [];
      const nodeIdx = adminState.nodeIndex ?? 0;
      const isLastNode = nodeIdx >= nodes.length - 1;
      if (!isLastNode) return false;
      const nodePages = nodes[nodeIdx]?.Pages || nodes[nodeIdx]?.pages || [];
      const currentIndex = nodePages.findIndex((p: any) => (p.pageNum || p.PageNum) === adminState.pageNum);
      for (let i = currentIndex + 1; i < nodePages.length; i++) {
        if (parseInt(nodePages[i].type || nodePages[i].Type || '1') >= 3) return false;
      }
      return true;
    }

    const pages = adminSegment.pages || adminSegment.Pages || [];
    const currentIndex = pages.findIndex((p: any) => (p.pageNum || p.PageNum) === adminState.pageNum);
    for (let i = currentIndex + 1; i < pages.length; i++) {
      if (parseInt(pages[i].type || pages[i].Type || '1') >= 3) return false;
    }
    return true;
  }, [adminSegment, adminState, availableSegments]);

  const hasNextQuestionPage = useCallback(() => {
    if (!adminSegment || !adminState) return false;
    const segType = String(adminSegment.segmentType || adminSegment.SegmentType || '');
    const isNodeSeg = segType === '5' || segType === 'PAS';

    const currentSegmentIndex = availableSegments.findIndex(seg => seg === adminState.segment);
    const hasNextSegment = currentSegmentIndex >= 0 && currentSegmentIndex < availableSegments.length - 1;

    if (isNodeSeg) {
      const nodes = adminSegment.Nodes || adminSegment.nodes || [];
      const nodeIdx = adminState.nodeIndex ?? 0;
      const nodePages = nodes[nodeIdx]?.Pages || nodes[nodeIdx]?.pages || [];
      const currentIndex = nodePages.findIndex((p: any) => (p.pageNum || p.PageNum) === adminState.pageNum);
      for (let i = currentIndex + 1; i < nodePages.length; i++) {
        if (parseInt(nodePages[i].type || nodePages[i].Type || '1') >= 3) return true;
      }
      // More nodes in this segment?
      if (nodeIdx < nodes.length - 1) return true;
      return hasNextSegment;
    }

    const pages = adminSegment.pages || adminSegment.Pages || [];
    const currentIndex = pages.findIndex((p: any) => (p.pageNum || p.PageNum) === adminState.pageNum);
    for (let i = currentIndex + 1; i < pages.length; i++) {
      if (parseInt(pages[i].type || pages[i].Type || '1') >= 3) return true;
    }
    return hasNextSegment;
  }, [adminSegment, adminState, availableSegments]);

  const hasPreviousQuestionPage = useCallback(() => {
    if (!adminState) return false;
    
    // Check if there are any BackTable entries (navigation history)
    // If user has navigated through pages, Previous should be available
    return adminState.backTable && adminState.backTable.length > 0;
  }, [adminState]);

  // New helper functions for segment navigation
  const canGoToNextSegment = useCallback(() => {
    if (!adminState) return false;
    const currentSegmentId = adminState.segment;
    const currentSegmentIndex = availableSegments.findIndex(seg => seg === currentSegmentId);
    return currentSegmentIndex >= 0 && currentSegmentIndex < availableSegments.length - 1;
  }, [adminState, availableSegments]);

  const canGoToPreviousSegment = useCallback(() => {
    if (!adminState) return false;
    const currentSegmentId = adminState.segment;
    const currentSegmentIndex = availableSegments.findIndex(seg => seg === currentSegmentId);
    return currentSegmentIndex > 0;
  }, [adminState, availableSegments]);

  const getNavigationInfo = (): AdminSegmentNavigation | null => {
    if (!adminState || !adminSegment) return null;
    
    const pages = adminSegment.pages || adminSegment.Pages;
    const totalPagesInSegment = pages ? pages.length : 0;
    
    if (adminStateStorage) {
      return adminStateStorage.getNavigationInfo(
        adminState,
        availableSegments.length,
        totalPagesInSegment
      );
    }
    
    return null;
  };

  // Log AdminSegment page sequence for debugging
  const logAdminSegmentPageSequence = (adminSegment: any) => {
    console.log('📄 AdminView: Page Sequence Debug Log');
    console.log('='.repeat(50));
    
    // Handle both camelCase and PascalCase properties
    const title = adminSegment.title || adminSegment.Title;
    const surveybId = adminSegment.surveybId || adminSegment.SurveybId;
    const languageTypeId = adminSegment.languageTypeId || adminSegment.LanguageTypeId;
    const generatedAt = adminSegment.generatedAt || adminSegment.GeneratedAt;
    const pages = adminSegment.pages || adminSegment.Pages;

    if (pages && Array.isArray(pages)) {
      pages.forEach((page: any, index: number) => {
        // Handle both camelCase and PascalCase properties
        const pageName = page.name || page.Name;
        const pageNum = page.pageNum || page.PageNum;
        const pageType = page.type || page.Type;
        const questions = page.questions || page.Questions;
        const questionsCount = questions?.length || 0;
        
        // Page info available in debug header
        
        if (questions && questionsCount > 0) {
          questions.forEach((questionItem: any, qIndex: number) => {
            const question = questionItem.question || questionItem.Question;
            if (question) {
              const qListNum = questionItem.qListNum || questionItem.QListNum;
              const mvarName = question.mvarName || question.MvarName;
              const htmlQuestion = question.htmlQuestion || question.HtmlQuestion;
              const htmlQuestionsCount = htmlQuestion?.length || 0;
              
              // Question details available in debug header
              
              if (htmlQuestion && htmlQuestionsCount > 0) {
                htmlQuestion.forEach((htmlQ: any, hIndex: number) => {
                  const qText = htmlQ.qText || htmlQ.QText;
                  const htmlAnswer = htmlQ.htmlAnswer || htmlQ.HtmlAnswer;
                  const htmlUvarAns = htmlQ.htmlUvarAns || htmlQ.HtmlUvarAns;
                  const answersCount = (htmlAnswer?.length || 0) + (htmlUvarAns?.length || 0);
                  const questionText = qText || '';
                  const truncatedText = questionText.length > 50 ? questionText.substring(0, 50) + '...' : questionText;

                });
              }
            }
          });
        }
        console.log('');
      });
    } else {
      console.log('❌ No pages found in AdminSegment');
    }
    
    console.log('='.repeat(50));
  };

  // Convert AdminSegment JSON format to Tables format
  const convertAdminSegmentToTables = (adminSegmentData: any) => {
    // Handle both camelCase and PascalCase properties
    const title = adminSegmentData.title || adminSegmentData.Title;
    const surveybId = adminSegmentData.surveybId || adminSegmentData.SurveybId;
    const pages = adminSegmentData.pages || adminSegmentData.Pages;
    
    console.log('🔄 convertAdminSegmentToTables: Converting AdminSegment data...', {
      title: title,
      surveybId: surveybId,
      pagesCount: pages?.length || 0
    });
    
    // If no pages, return empty tables to avoid processing
    if (!pages || !Array.isArray(pages) || pages.length === 0) {
      console.log('❌ convertAdminSegmentToTables: No pages found, returning empty tables');
      return { Tables: [] };
    }
    
    const tables: any[] = [];
    const htmlPages: any[] = [];
    const htmlQuestions: any[] = [];
    const htmlAnswers: any[] = [];
    const htmlQuestionList: any[] = [];
    
    // Process Pages from AdminSegment
    if (pages && Array.isArray(pages)) {
      pages.forEach((page: any, pageIndex: number) => {
        // Handle both camelCase and PascalCase properties
        const pageName = page.name || page.Name;
        const pageNum = page.pageNum || page.PageNum;
        const pageType = page.type || page.Type;
        const questions = page.questions || page.Questions;
        
        // Add page to htmlpage table
        const pageId = `page_${pageIndex + 1}`;
        htmlPages.push([
          pageId,           // htmlpage_id
          pageName || `Page ${pageNum}`, // htmlpage_title
          pageNum || (pageIndex + 1).toString(), // htmlpage_num
          pageType || "1"  // htmlpage_type
        ]);
        
        // Process Questions in this page
        if (questions && Array.isArray(questions)) {
          questions.forEach((questionItem: any, questionIndex: number) => {
            const question = questionItem.question;
            if (question) {
              const questionId = `question_${pageIndex}_${questionIndex}`;
              
              // Process HTML questions if they exist
              if (question.htmlQuestion && Array.isArray(question.htmlQuestion)) {
                question.htmlQuestion.forEach((htmlQuestion: AdminHtmlQuestionDto, htmlIndex: number) => {
                  const htmlQuestionId = `htmlquestion_${pageIndex}_${questionIndex}_${htmlIndex}`;
                  
                  // Add question to htmlquestion table
                  htmlQuestions.push([
                    htmlQuestionId,                    // htmlquestion_id
                    question.mvarName || "",           // htmlquestion_question_id
                    question.mvarTypeId || "1",        // htmlquestion_answertype_id
                    htmlQuestion.qText || "",          // htmlquestion_text
                    question.mvarName || ""            // htmlquestion_name
                  ]);
                  
                  // Add to question list (links questions to pages)
                  htmlQuestionList.push([
                    `list_${pageIndex}_${questionIndex}_${htmlIndex}`, // htmlquestionlist_id
                    pageId,                                           // htmlquestionlist_htmlpage_id
                    htmlQuestionId,                                   // htmlquestionlist_htmlquestion_id
                    (questionItem.qListNum || questionIndex + 1).toString() // htmlquestionlist_listnumber
                  ]);
                  
                  // Process actual HTML answers
                  if (htmlQuestion.htmlAnswer && Array.isArray(htmlQuestion.htmlAnswer)) {
                    htmlQuestion.htmlAnswer.forEach((answer: AdminHtmlAnswerDto, answerIndex: number) => {
                      htmlAnswers.push([
                        `answer_${pageIndex}_${questionIndex}_${htmlIndex}_${answerIndex}`, // htmlanswer_id
                        htmlQuestionId,                                                     // htmlanswer_htmlquestion_id
                        answer.aText || "",                                                 // htmlanswer_text
                        answer.keyValue || (answerIndex + 1).toString()                     // htmlanswer_answer_id
                      ]);
                    });
                  }
                  
                  // Process HTML uvar answers if they exist
                  if (htmlQuestion.htmlUvarAns && Array.isArray(htmlQuestion.htmlUvarAns)) {
                    htmlQuestion.htmlUvarAns.forEach((uvarAnswer: AdminHtmlAnswerDto, uvarIndex: number) => {
                      htmlAnswers.push([
                        `uvaranswer_${pageIndex}_${questionIndex}_${htmlIndex}_${uvarIndex}`, // htmlanswer_id
                        htmlQuestionId,                                                      // htmlanswer_htmlquestion_id
                        uvarAnswer.aText || "",                                              // htmlanswer_text
                        uvarAnswer.keyValue || `uvar_${uvarIndex + 1}`                       // htmlanswer_answer_id
                      ]);
                    });
                  }
                });
              } else {
                // Fallback for questions without htmlQuestion array
                htmlQuestions.push([
                  questionId,                    // htmlquestion_id
                  question.mvarName || "",       // htmlquestion_question_id
                  question.mvarTypeId || "1",    // htmlquestion_answertype_id
                  question.mvarName || "",       // htmlquestion_text
                  question.mvarName || ""        // htmlquestion_name
                ]);
                
                // Add to question list (links questions to pages)
                htmlQuestionList.push([
                  `list_${pageIndex}_${questionIndex}`, // htmlquestionlist_id
                  pageId,                               // htmlquestionlist_htmlpage_id
                  questionId,                           // htmlquestionlist_htmlquestion_id
                  (questionItem.qListNum || questionIndex + 1).toString() // htmlquestionlist_listnumber
                ]);
                
                // Add default answers for questions without specific answers
                ['Yes', 'No'].forEach((answerText, answerIndex) => {
                  htmlAnswers.push([
                    `answer_${pageIndex}_${questionIndex}_${answerIndex}`, // htmlanswer_id
                    questionId,                                           // htmlanswer_htmlquestion_id
                    answerText,                                          // htmlanswer_text
                    (answerIndex + 1).toString()                         // htmlanswer_answer_id
                  ]);
                });
              }
            }
          });
        }
      });
    }
    
    // Create table structures
    tables.push({
      Name: 'htmlpage',
      PrimaryKey: 'htmlpage_id',
      Schema: [
        { Name: 'htmlpage_id', Type: 'String' },
        { Name: 'htmlpage_title', Type: 'String' },
        { Name: 'htmlpage_num', Type: 'String' },
        { Name: 'htmlpage_type', Type: 'String' }
      ],
      Data: {
        Columns: ['htmlpage_id', 'htmlpage_title', 'htmlpage_num', 'htmlpage_type'],
        Rows: htmlPages
      }
    });
    
    tables.push({
      Name: 'htmlquestion',
      PrimaryKey: 'htmlquestion_id', 
      Schema: [
        { Name: 'htmlquestion_id', Type: 'String' },
        { Name: 'htmlquestion_question_id', Type: 'String' },
        { Name: 'htmlquestion_answertype_id', Type: 'String' },
        { Name: 'htmlquestion_text', Type: 'String' },
        { Name: 'htmlquestion_name', Type: 'String' }
      ],
      Data: {
        Columns: ['htmlquestion_id', 'htmlquestion_question_id', 'htmlquestion_answertype_id', 'htmlquestion_text', 'htmlquestion_name'],
        Rows: htmlQuestions
      }
    });
    
    tables.push({
      Name: 'htmlanswer',
      PrimaryKey: 'htmlanswer_id',
      Schema: [
        { Name: 'htmlanswer_id', Type: 'String' },
        { Name: 'htmlanswer_htmlquestion_id', Type: 'String' },
        { Name: 'htmlanswer_text', Type: 'String' },
        { Name: 'htmlanswer_answer_id', Type: 'String' }
      ],
      Data: {
        Columns: ['htmlanswer_id', 'htmlanswer_htmlquestion_id', 'htmlanswer_text', 'htmlanswer_answer_id'],
        Rows: htmlAnswers
      }
    });
    
    tables.push({
      Name: 'htmlquestionlist',
      PrimaryKey: 'htmlquestionlist_id',
      Schema: [
        { Name: 'htmlquestionlist_id', Type: 'String' },
        { Name: 'htmlquestionlist_htmlpage_id', Type: 'String' },
        { Name: 'htmlquestionlist_htmlquestion_id', Type: 'String' },
        { Name: 'htmlquestionlist_listnumber', Type: 'String' }
      ],
      Data: {
        Columns: ['htmlquestionlist_id', 'htmlquestionlist_htmlpage_id', 'htmlquestionlist_htmlquestion_id', 'htmlquestionlist_listnumber'],
        Rows: htmlQuestionList
      }
    });
    
    console.log('🔄 convertAdminSegmentToTables: Created tables:', {
      pages: htmlPages.length,
      questions: htmlQuestions.length,
      answers: htmlAnswers.length,
      questionList: htmlQuestionList.length
    });
    
    return { Tables: tables };
  };

  // Extract function to process admin content
  const processAdminContent = async (adminContent: ContentRecord) => {
    
    // Check cache first to avoid redundant processing
    const cacheKey = `${adminContent.contentTypeId}-${adminContent.languageTypeId}`;
    const hasCachedContent = processedContentCache.current.has(cacheKey);
    
    if (hasCachedContent) {
      const cachedResult = processedContentCache.current.get(cacheKey);
      // Validate cache: detect old Nodes→Pages mutation (NodeDto objects have no PageNum/HtmPageId)
      const cachedPages = cachedResult?.Pages || cachedResult?.pages;
      const cacheIsCorrupted = Array.isArray(cachedPages) && cachedPages.length > 0 &&
        cachedPages[0] && !cachedPages[0].PageNum && !cachedPages[0].pageNum && !cachedPages[0].HtmPageId && !cachedPages[0].htmPageId;
      if (cacheIsCorrupted) {
        // Stale/corrupted cache — invalidate and fall through to reprocess
        processedContentCache.current.delete(cacheKey);
      } else {
        try {
          const savedEdits = await loadPreScriptEditsFromStorage();
          const adminSegmentWithEdits = applyPreScriptEditsToAdminSegment(cachedResult, savedEdits, adminContent.contentTypeId);
          processedContentWithEditsCacheRef.current.set(cacheKey, adminSegmentWithEdits);
          setAdminSegment(adminSegmentWithEdits);
        } catch (error) {
          console.error('💾 processAdminContent: Error loading saved edits for cached content:', error);
          setAdminSegment(cachedResult);
        }
        setIsContentLoading(false);
        return;
      }
    }
    

    if (adminContent?.tableData) {
       // Extract tables from the AdminSegment content
      const tables: Record<string, QuickAccessTable> = {};
      
      // Parse the content body if it contains encoded table data
      try {
        let tablesData;
        
        // Handle string tableData (JSON string from API)
        let parsedData = adminContent.tableData;
        if (typeof adminContent.tableData === 'string') {
          parsedData = JSON.parse(adminContent.tableData);
        }
        
        // Check if this is an AdminSegmentDTO with Tables array directly
        if ((parsedData as any).Tables && Array.isArray((parsedData as any).Tables)) {
          
          // Check if this has a content table with content_body
          const contentTable = (parsedData as any).Tables.find((t: any) => t.Name === 'content');
          
          if (contentTable && contentTable.Data?.Columns?.includes('content_body')) {
            const contentRow = contentTable.Data.Rows[0];
            const contentBodyIndex = contentTable.Data.Columns.indexOf('content_body');
            const encodedContent = contentRow[contentBodyIndex];
            
            // Decode base64 and parse JSON
            const decodedContent = atob(encodedContent);
            const adminSegmentData = JSON.parse(decodedContent);
            
            // 🐛 DEBUG: Report CalcScript when AdminSegmentDto is parsed from base64 content_body
            console.log('🔍 AdminView.processAdminContent: Parsed AdminSegmentDto CalcScript:', {
              hasCalcScript: !!(adminSegmentData.CalcScript || adminSegmentData.calcScript),
              calcScriptLength: (adminSegmentData.CalcScript || adminSegmentData.calcScript || '').length,
              calcScriptPreview: (adminSegmentData.CalcScript || adminSegmentData.calcScript || '').substring(0, 200) + (((adminSegmentData.CalcScript || adminSegmentData.calcScript || '').length > 200) ? '...' : ''),
              segmentTitle: adminSegmentData.Title || adminSegmentData.title,
              segmentId: adminSegmentData.SurveybId || adminSegmentData.surveybId
            });
            
            // We have AdminSegment data - use it directly instead of converting to tables
            const pages = adminSegmentData.pages || adminSegmentData.Pages;
            const segTypeP1 = String(adminSegmentData.SegmentType || adminSegmentData.segmentType || '');
            const isNodeSegP1 = segTypeP1 === '5' || segTypeP1 === 'PAS';
            const nodesP1 = adminSegmentData.Nodes || adminSegmentData.nodes;
            const firstNodePagesP1: any[] = isNodeSegP1 ? ((nodesP1?.[0]?.Pages || nodesP1?.[0]?.pages) ?? []) : [];
            const hasContentP1 = isNodeSegP1
              ? (Array.isArray(nodesP1) && nodesP1.length > 0 && firstNodePagesP1.length > 0)
              : (Array.isArray(pages) && pages.length > 0);
            const _allKeys1 = Object.keys(adminSegmentData || {});
            const _arrayCandidates1 = _allKeys1.filter(k => Array.isArray((adminSegmentData as any)[k])).map(k => `${k}[${(adminSegmentData as any)[k].length}]`);
            console.log('🔍 processAdminContent (tables path) pages check:', {
              topLevelKeys: _allKeys1.join(', '),
              arrayCandidates: _arrayCandidates1.join(', '),
              pagesLower: Array.isArray(adminSegmentData.pages) ? `array[${adminSegmentData.pages.length}]` : adminSegmentData.pages,
              pagesUpper: Array.isArray(adminSegmentData.Pages) ? `array[${adminSegmentData.Pages.length}]` : adminSegmentData.Pages,
              pagesType: typeof pages,
              isArray: Array.isArray(pages),
              length: Array.isArray(pages) ? pages.length : 'n/a'
            });
            if (hasContentP1) {
              // Load saved PreScript edits from storage and apply them to the AdminSegment BEFORE setting state
              try {
                const savedEdits = await loadPreScriptEditsFromStorage();
                const adminSegmentWithEdits = applyPreScriptEditsToAdminSegment(adminSegmentData, savedEdits, adminContent.contentTypeId);

                // Set the AdminSegment data only once with applied edits
                setAdminSegment(adminSegmentWithEdits);

                // Cache the processed result (without edits to keep fresh content clean)
                processedContentCache.current.set(cacheKey, adminSegmentData);
                // Cache the with-edits version for segmentOverrideForNavRef
                processedContentWithEditsCacheRef.current.set(cacheKey, adminSegmentWithEdits);
              } catch (error) {
                console.error('💾 processAdminContent: Error loading saved edits, using AdminSegment without edits:', error);
                setAdminSegment(adminSegmentData);
                processedContentCache.current.set(cacheKey, adminSegmentData);
              }

              // For node segments, skip flat-page scanning (pages are inside Nodes[].Pages[])
              // Node PageNums are backend-assigned starting at 1 — no correction needed.
              if (isNodeSegP1) {
                setIsContentLoading(false);
                return;
              }
              
              // Find first page with type >= 3 (question pages) and check for HtmlQuestion data
              let startPageIndex = 0;
              let foundHtmlQuestions = false;
              
              for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const pageType = parseInt(page.type || page.Type || "1");
                const questions = page.questions || page.Questions || [];
                
                // Check if any questions have HtmlQuestion data
                const htmlQuestionCounts = questions.map((q: any) => {
                  const question = q.question || q.Question;
                  const htmlQuestion = question?.HtmlQuestion || question?.htmlQuestion;
                  return {
                    hasQuestion: !!question,
                    htmlQuestionCount: Array.isArray(htmlQuestion) ? htmlQuestion.length : (htmlQuestion === null ? 'null' : 'undefined'),
                    htmlQuestionSample: Array.isArray(htmlQuestion) && htmlQuestion.length > 0 ? htmlQuestion[0] : htmlQuestion
                  };
                });
                
                if (pageType >= 3) {
                  if (startPageIndex === 0 || startPageIndex === i) {
                    startPageIndex = i;
                  }
                  
                  // Check if this page has any non-null HtmlQuestion arrays
                  const hasHtmlData = questions.some((q: any) => {
                    const question = q.question || q.Question;
                    const htmlQuestion = question?.HtmlQuestion || question?.htmlQuestion;
                    return Array.isArray(htmlQuestion) && htmlQuestion.length > 0;
                  });
                  
                  if (hasHtmlData && !foundHtmlQuestions) {
                    startPageIndex = i;
                    foundHtmlQuestions = true;
                    break;
                  } 
                }
              }
              
              // Trigger page evaluation after AdminSegment is loaded
              if (adminState) {
                setTimeout(() => {
                  // Page evaluation will be triggered by AdminState pageNum change
                }, 100); // Small delay to ensure state is settled
              }
              
              // Skip table processing entirely
              setIsContentLoading(false);
              return;
            } else {
              tablesData = parsedData;
            }
          } else {
            tablesData = parsedData;
          }
        }
        // Check if this is a single table structure
        else if ((parsedData as any).Name && (parsedData as any).Data) {
  
          
          // Check if this single table is a content table with content_body
          if ((parsedData as any).Name === 'content' && (parsedData as any).Data?.Columns?.includes('content_body')) {
  
            const contentRow = (parsedData as any).Data.Rows[0];
            const contentBodyIndex = (parsedData as any).Data.Columns.indexOf('content_body');
            const encodedContent = contentRow[contentBodyIndex];
             
            // Decode base64 and parse JSON
            const decodedContent = atob(encodedContent);
           
            const adminSegmentData = JSON.parse(decodedContent);
            
            // SegmentType '5'/'PAS' = node-style (Nodes hierarchy), '4' = page/branching-style (Pages flat)
            const segType1 = String(adminSegmentData.SegmentType || adminSegmentData.segmentType || '');
            const isNodeSeg1 = segType1 === '5' || segType1 === 'PAS';
            if (!isNodeSeg1 && segType1 !== '4') {
              // Unknown/legacy: if Pages empty but Nodes has items, flatten Nodes->Pages
              if ((!adminSegmentData.Pages || adminSegmentData.Pages.length === 0) &&
                  (!adminSegmentData.pages || adminSegmentData.pages.length === 0)) {
                const nodes = adminSegmentData.Nodes || adminSegmentData.nodes;
                if (nodes && Array.isArray(nodes) && nodes.length > 0) {
                  adminSegmentData.Pages = nodes;
                }
              }
            }
            // For node segments (type 5), hasContent is derived from first node's pages
            const pages1 = adminSegmentData.pages || adminSegmentData.Pages;
            const nodes1 = adminSegmentData.Nodes || adminSegmentData.nodes;
            const firstNodePages1 = isNodeSeg1 ? ((nodes1?.[0]?.Pages || nodes1?.[0]?.pages) ?? []) : [];
            const hasContent1 = isNodeSeg1
              ? (Array.isArray(nodes1) && nodes1.length > 0 && firstNodePages1.length > 0)
              : (Array.isArray(pages1) && pages1.length > 0);

            if (hasContent1) {
              
              // Load saved PreScript edits from storage and apply them to the AdminSegment BEFORE setting state
              try {
                const savedEdits = await loadPreScriptEditsFromStorage();
                const adminSegmentWithEdits = applyPreScriptEditsToAdminSegment(adminSegmentData, savedEdits, adminContent.contentTypeId);
                setAdminSegment(adminSegmentWithEdits);
                processedContentCache.current.set(cacheKey, adminSegmentData);
                processedContentWithEditsCacheRef.current.set(cacheKey, adminSegmentWithEdits);
              } catch (error) {
                console.error('💾 processAdminContent: Error loading saved edits:', error);
                setAdminSegment(adminSegmentData);
                processedContentCache.current.set(cacheKey, adminSegmentData);
              }
              
              // skip table processing entirely
              setIsContentLoading(false);
              return;
            } 
          }
          
          tablesData = { Tables: [parsedData] };
        }
        // Default fallback
        else {
          console.log('� processAdminContent: Taking path 3 - Default fallback structure detection');
          console.log('🔄 processAdminContent: parsedData structure:', {
            hasTablesProperty: !!(parsedData as any).Tables,
            hasNameProperty: !!(parsedData as any).Name,
            hasDataProperty: !!(parsedData as any).Data,
            rootKeys: Object.keys(parsedData || {}),
            sampleData: parsedData
          });
          tablesData = (parsedData as any).Tables ? parsedData : { Tables: [parsedData] };
          console.log('💾 processAdminContent: Created tablesData for fallback:', {
            hasTablesProperty: !!(tablesData as any).Tables,
            tablesCount: (tablesData as any).Tables?.length || 0
          });
        }

        // Only continue with table processing if we didn't find AdminSegment data
        {
          // Type guard to check if tablesData has Tables property
          const hasTablesProperty = (data: any): data is { Tables: any[] } => {
            return data && Array.isArray(data.Tables);
          };

          // Check if we can still extract AdminSegment from the fallback structure
          if (hasTablesProperty(tablesData)) {
            const contentTable = tablesData.Tables.find((t: any) => t.Name === 'content');
            
            if (contentTable && contentTable.Data?.Columns?.includes('content_body')) {
              try {
                const contentRow = contentTable.Data.Rows[0];
                const contentBodyIndex = contentTable.Data.Columns.indexOf('content_body');
                const encodedContent = contentRow[contentBodyIndex];
                const decodedContent = atob(encodedContent);
                const adminSegmentData = JSON.parse(decodedContent);
                
                // 🐛 DEBUG: Report CalcScript when AdminSegmentDto is parsed from base64 content_body
                console.log('🔍 AdminView.processAdminContent (fallback): Parsed AdminSegmentDto CalcScript:', {
                  hasCalcScript: !!(adminSegmentData.CalcScript || adminSegmentData.calcScript),
                  calcScriptLength: (adminSegmentData.CalcScript || adminSegmentData.calcScript || '').length,
                  calcScriptPreview: (adminSegmentData.CalcScript || adminSegmentData.calcScript || '').substring(0, 200) + (((adminSegmentData.CalcScript || adminSegmentData.calcScript || '').length > 200) ? '...' : ''),
                  segmentTitle: adminSegmentData.Title || adminSegmentData.title,
                  segmentId: adminSegmentData.SurveybId || adminSegmentData.surveybId
                });
                
                // SegmentType '5'/'PAS' = node-style (use Nodes), '4' = page/branching-style (use Pages)
                const segType2 = String(adminSegmentData.SegmentType || adminSegmentData.segmentType || '');
                const isNodeSeg2raw = segType2 === '5' || segType2 === 'PAS';
                if (isNodeSeg2raw) {
                  // do NOT copy - for node-style we keep Nodes intact for hierarchical navigation
                } else if (segType2 !== '4') {
                  // Unknown/legacy: if Pages empty but Nodes has items, flatten Nodes->Pages
                  if ((!adminSegmentData.Pages || adminSegmentData.Pages.length === 0) &&
                      (!adminSegmentData.pages || adminSegmentData.pages.length === 0)) {
                    const nodes = adminSegmentData.Nodes || adminSegmentData.nodes;
                    if (nodes && Array.isArray(nodes) && nodes.length > 0) {
                      adminSegmentData.Pages = nodes;
                    }
                  }
                }
                const isNodeSeg2 = isNodeSeg2raw;
                const pages2 = adminSegmentData.pages || adminSegmentData.Pages;
                const nodes2 = adminSegmentData.Nodes || adminSegmentData.nodes;
                const firstNodePages2 = isNodeSeg2 ? ((nodes2?.[0]?.Pages || nodes2?.[0]?.pages) ?? []) : [];
                const hasContent2 = isNodeSeg2
                  ? (Array.isArray(nodes2) && nodes2.length > 0 && firstNodePages2.length > 0)
                  : (Array.isArray(pages2) && pages2.length > 0);
                if (hasContent2) {
                  
                  // Load saved PreScript edits from storage and apply them to the AdminSegment
                  try {
                    const savedEdits = await loadPreScriptEditsFromStorage();
                    const adminSegmentWithEdits = applyPreScriptEditsToAdminSegment(adminSegmentData, savedEdits, adminContent.contentTypeId);
                    setAdminSegment(adminSegmentWithEdits);
                    processedContentCache.current.set(cacheKey, adminSegmentData);
                    processedContentWithEditsCacheRef.current.set(cacheKey, adminSegmentWithEdits);

                    setIsContentLoading(false);
                    return; // Exit early since we have AdminSegment data
                  } catch (error) {
                    console.error('💾 processAdminContent: Error loading saved edits for fallback:', error);
                    setAdminSegment(adminSegmentData);
                    processedContentCache.current.set(cacheKey, adminSegmentData);
                    setIsContentLoading(false);
                    return;
                  }
                }
              } catch (error) {
                console.error('💾 processAdminContent: Failed to extract AdminSegment from fallback structure:', error);
              }
            }
          }

          // Build quick access tables (legacy approach)
          if (hasTablesProperty(tablesData)) {
            tablesData.Tables.forEach((table: any) => {
              switch (table.Name) {
                case 'htmlpage':
                  tables.pages = new QuickAccessTable(table);
                  break;
                case 'htmlquestion':
                  tables.questions = new QuickAccessTable(table);
                  break;
                case 'htmlanswer':
                  tables.answers = new QuickAccessTable(table);
                  break;
                case 'htmlquestionlist':
                  tables.questionList = new QuickAccessTable(table);
                  break;
                case 'content':
                  // Already processed for AdminSegment
                  break;
                default:
                  console.log('⚠️ Unknown table type:', table.Name);
              }
            });
          }
        } 
       setContentTables(tables);
       setIsContentLoading(false);
      } catch (error) {
        console.error('❌ Error parsing AdminSegment content:', error);
        console.error('❌ Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        });
        setIsContentLoading(false);
      }
    } else {
      console.log('⚠️ No tableData found in adminContent');
      setIsContentLoading(false);
    }
  };

  // PageBuilder function - builds current page based on adminState
  const PageBuilder = useCallback(() => {
    if (!contentTables.pages || !adminState) return null;

    // Get current page
    const pageData = contentTables.pages.get(adminState.pageNum.toString());
    if (!pageData) return null;

    const page: HtmlPage = {
      htmlpage_id: pageData.htmlpage_id,
      htmlpage_title: pageData.htmlpage_title,
      htmlpage_num: parseInt(pageData.htmlpage_num),
      htmlpage_type: parseInt(pageData.htmlpage_type),
    };

    setCurrentPage(page);

    // Get questions for this page
    if (contentTables.questionList && contentTables.questions) {
      const questionListItems: any[] = [];
      
      // Find questions linked to this page
      contentTables.questionList.getAllKeys().forEach(key => {
        const listItem = contentTables.questionList!.get(key);
        if (listItem?.htmlquestionlist_htmlpage_id === page.htmlpage_id) {
          questionListItems.push(listItem);
        }
      });

      // Sort by list number and get question details
      const pageQuestions: HtmlQuestion[] = [];
      questionListItems
        .sort((a, b) => parseInt(a.htmlquestionlist_listnumber) - parseInt(b.htmlquestionlist_listnumber))
        .forEach(listItem => {
          const questionData = contentTables.questions!.get(listItem.htmlquestionlist_htmlquestion_id);
          if (questionData) {
            pageQuestions.push({
              htmlquestion_id: questionData.htmlquestion_id,
              htmlquestion_question_id: questionData.htmlquestion_question_id,
              htmlquestion_answertype_id: questionData.htmlquestion_answertype_id,
              htmlquestion_text: questionData.htmlquestion_text,
              htmlquestion_name: questionData.htmlquestion_name,
            });
          }
        });

      setCurrentQuestions(pageQuestions);

      // Get answers for each question
      const answersMap: Record<string, HtmlAnswer[]> = {};
      pageQuestions.forEach(question => {
        const questionAnswers: HtmlAnswer[] = [];
        
        if (contentTables.answers) {
          contentTables.answers.getAllKeys().forEach(key => {
            const answerData = contentTables.answers!.get(key);
            if (answerData?.htmlanswer_htmlquestion_id === question.htmlquestion_id) {
              questionAnswers.push({
                htmlanswer_id: answerData.htmlanswer_id,
                htmlanswer_htmlquestion_id: answerData.htmlanswer_htmlquestion_id,
                htmlanswer_text: answerData.htmlanswer_text,
                htmlanswer_answer_id: answerData.htmlanswer_answer_id,
              });
            }
          });
        }
        
        answersMap[question.htmlquestion_id] = questionAnswers;
      });

      setQuestionAnswers(answersMap);
    }
  }, [contentTables, adminState?.pageNum]);

  // Handle assessment completion
  const handleComplete = async () => {
    if (!adminStateStorage || !adminState) return;
    
    try {
      const completedState = await adminStateStorage.completeAssessment(
        accountId,
        profileId,
        actualSourceId!
      );
      
      if (completedState) {
        setAdminState(completedState);
        await handleDoneNavigation(); // Call handleDoneNavigation instead of onComplete
        console.log('✅ Assessment completed successfully');
      }
    } catch (error) {
      console.error('❌ Failed to complete assessment:', error);
    }
  };

  // Handle Next/Done button click
  const handleNextOrDone = async () => {
    if (isLastPageOfLastSegment() && isDoneEnabledByScript()) {
      // Guard against double-tap re-entrancy — once latched, this whole Done path (local
      // completion + the background backend submission it kicks off) runs exactly once.
      if (doneClickLatchRef.current) {
        console.log('🏁 handleNextOrDone: Done already processing — ignoring duplicate tap');
        return;
      }
      doneClickLatchRef.current = true;
      setIsDoneProcessing(true);

      // We're on the last page of the last segment - complete the assessment
      console.log('🏁 Assessment completion triggered - setting status to COMPLETED');

      if (adminStateStorage && adminState) {
        try {
          const completedState = { ...adminState, status: 'COMPLETED' };
          await adminStateStorage.saveAdminState(completedState);
          setAdminState(completedState);
          console.log('✅ Assessment marked as complete (status: COMPLETED)');

          // Call handleDoneNavigation for final processing
          await handleDoneNavigation();
        } catch (error) {
          console.error('❌ Failed to complete assessment:', error);
          // Nothing has been submitted yet at this point — safe to let the respondent retry.
          doneClickLatchRef.current = false;
          setIsDoneProcessing(false);
        }
      } else {
        doneClickLatchRef.current = false;
        setIsDoneProcessing(false);
      }
    } else {
      // Normal navigation to next page
      await goToNextPage();
    }
  };

  // NextQuestion function - handles navigation and branching logic
  const NextQuestion = useCallback(async (selectedAnswerId?: string, questionId?: string) => {
    if (!adminStateStorage || !adminState) return;

    try {
      // Save answer if provided
      if (selectedAnswerId && questionId) {
        await adminStateStorage.updateAnswerData(
          adminState.profileId,
          adminState.sourceId,
          questionId, // mvarName
          selectedAnswerId, // value
          adminState.segment,
          adminState.pageNum
        );
        
        // Reload AdminState to get updated data
        const updatedState = await adminStateStorage.loadAdminState(
          adminState.profileId,
          adminState.sourceId
        );
        if (updatedState) {
          setAdminState(updatedState);
        }
      }

      // Navigate to next page or next segment
      const totalPagesInSegment = contentTables.pages?.size || 1;
      
      const nextState = await adminStateStorage.navigateToNextPage(
        adminState.profileId,
        adminState.sourceId,
        totalPagesInSegment
      );
      
      if (nextState) {
        setAdminState(nextState);
        
        // Check if assessment is now complete
        if (nextState.status === 'COMPLETED') {
          handleComplete();
        }
      }
    } catch (error) {
      console.error('❌ Failed to navigate to next question:', error);
    }
  }, [adminStateStorage, adminState, handleComplete]);

  // Previous question navigation
  const PreviousQuestion = useCallback(async () => {
    if (!adminStateStorage || !adminState) return;

    try {
      const currentPage = parseInt(adminState.pageNum) || 1;
      
      if (currentPage > 1) {
        const previousState = await adminStateStorage.navigateToPreviousPage(
          adminState.profileId,
          adminState.sourceId
        );
        
        if (previousState) {
          setAdminState(previousState);
        }
      }
    } catch (error) {
      console.error('❌ Failed to navigate to previous question:', error);
    }
  }, [adminStateStorage, adminState]);

  // Handle answer selection
  const handleAnswerSelect = (questionId: string, answerId: string) => {
    NextQuestion(answerId, questionId);
  };

  // Build page when state changes
  useEffect(() => {
    PageBuilder();
  }, [PageBuilder]);

  const doSaveAndExit = async () => {
    if (adminStateStorage && adminState) {
      try {
        console.log('💾 Save & Finish Later - preserving current progress:', {
          pageNum: adminState.pageNum,
          backTableLength: adminState.backTable?.length || 0,
          dataLength: adminState.data?.length || 0,
          status: adminState.status
        });
        await adminStateStorage.saveAdminState(adminState);
        console.log('✅ Progress saved for later continuation');
      } catch (error) {
        console.error('❌ Failed to save progress on exit:', error);
      }
    }
    onCancel?.();
  };

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  // Loading state - check if AdminState is ready instead of content

  
  // Show loading if we don't have AdminState ready yet
  if (!adminState || !adminStateStorage) {
    return (
      <View style={styles.container}>
        {renderSkinHeader()}
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Preparing assessment...</Text>
        </View>
      </View>
    );
  }

  // AdminStateDto-based assessment interface (when skipContentAutoLoad is true)
  // Check if we have actual AdminSegment content available
  if (skipContentAutoLoad && adminState && adminStateStorage) {
    // If we have AdminSegment content (questions, pages, etc.), use normal flow
    // Handle both camelCase (pages) and PascalCase (Pages) properties
    const adminPages = adminSegment?.pages || adminSegment?.Pages;
    const adminNodes = adminSegment?.nodes || adminSegment?.Nodes;
    const segTypeCheck = String(adminSegment?.segmentType || adminSegment?.SegmentType || '');
    const isNodeSeg = segTypeCheck === '5' || segTypeCheck === 'PAS';
    const hasContent = adminSegment && (
      (adminPages && Array.isArray(adminPages) && adminPages.length > 0) ||
      (isNodeSeg && adminNodes && Array.isArray(adminNodes) && adminNodes.length > 0)
    );
    if (hasContent) {
  
      // Continue to normal AdminSegment-based rendering below
    } else if (isContentLoading) {
      // Show loading while loadAdminSegment is running
      return (
        <View style={styles.container}>
          {renderSkinHeader()}
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading assessment content...</Text>
          </View>
        </View>
      );
    } else {
      // Check if loadAdminSegment has been triggered yet - if not, show loading instead of "no content found"
      // This prevents race condition where render happens before loadAdminSegment completes
      const debugAdminPages = adminSegment?.pages || adminSegment?.Pages;

      return (
        <View style={styles.container}>
          {renderSkinHeader()}
          <View style={styles.completionContainer}>
            <Text style={styles.completionTitle}>⚠️ No Questions Found</Text>
            <Text style={styles.completionText}>
              The assessment content could not be loaded. Please try using the debug controls to download the content, or contact support.
            </Text>
            <TouchableOpacity style={styles.completionButton} onPress={onCancel}>
              <Text style={styles.completionButtonText}>Return</Text>
            </TouchableOpacity>
          </View>
          {/* {showDebugInfo && renderDebugControls()} */}
        </View>
      );
    }
  }

  // Completion state
  if (adminState?.status === 'COMPLETED' || adminState?.status === '5') {
    return (
      <View style={styles.container}>
        {renderSkinHeader()}

        {/* Assessment Controls */}
        <View style={styles.assessmentControls}>
          <View style={styles.statusContainer}>
            <Text style={styles.statusText}>🎉 Assessment Complete!</Text>
          </View>
        </View>
        
        <View style={styles.completionContainer}>
          <Text style={styles.completionTitle}>Assessment Complete! ✅</Text>
          <Text style={styles.completionText}>
            Thank you for completing the assessment. Your responses have been recorded.
          </Text>
          <TouchableOpacity style={styles.completionButton} onPress={onCancel}>
            <Text style={styles.completionButtonText}>Return</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Get current page from AdminSegment using AdminState.pageNum (AdminState-driven)
  const getCurrentAdminPage = () => {
    // Delegate to getCurrentPageFromSegment which handles both flat (type 4) and node (type 5) segments
    return getCurrentPageFromSegment();
  };

  // Render answers based on MvarTypeId
  const renderAnswersByType = (mvarName: string, mvarTypeId: string, htmlAnswers: any[], htmlUvarAnswers: any[], qIndex: number, hIndex: number, currentAdminPage: any) => {
    const typeId = parseInt(mvarTypeId || '1');
    const allAnswers = [...htmlAnswers, ...htmlUvarAnswers];

    // Rendering answers - logged above with question details

    switch (typeId) {
      case 1: { // Single answer — layout resolved via overrideQLayout (falls back to the
                // skinType-driven answerLayout default); see getAnswerLayoutMode.
        const layoutMode = getAnswerLayoutMode(currentAdminPage);
        const answersContainerStyle =
          layoutMode === 'horizontal-button' ? styles.answersContainerHorizontal :
          layoutMode === 'vertical-button' ? styles.answersContainerButtonVertical :
          styles.answersContainer;
        return (
          <View style={answersContainerStyle}>
            {allAnswers.map((answer, aIndex) => {
              const aText = answer.aText || answer.AText || '';
              const dataValue = answer.dataValue || answer.DataValue || aIndex.toString();
              const keyValue = answer.keyValue || answer.KeyValue || (aIndex + 1).toString();
              const isSelected = isAnswerSelected(mvarName, dataValue);

              const onSelect = async () => {
                await handleAnswerChange(`q_${qIndex}_${hIndex}`, mvarName, dataValue, false);
                await goToNextPage();
              };

              if (layoutMode === '2-column') {
                // Two-column row: left = numeric keyValue badge, right = aText label.
                // Tapping either column stores dataValue and auto-advances.
                return (
                  <TouchableOpacity
                    key={`ans_${qIndex}_${hIndex}_${aIndex}`}
                    style={[styles.twoColAnswerRow, isSelected && styles.twoColAnswerRowSelected,
                      { maxWidth: APP_CONSTANTS.ANSWER_ROW_MAX_WIDTH }]}
                    onPress={onSelect}
                  >
                    <View style={[styles.twoColKeyCell, isSelected && styles.twoColKeyCellSelected,
                      { backgroundColor: isSelected ? '#27ae60' : answerButtonColor }]}>
                      <Text style={[styles.twoColKeyText, isSelected && styles.twoColKeyTextSelected]}>
                        {keyValue}
                      </Text>
                    </View>
                    <View style={styles.twoColTextCell}>
                      <Text style={[styles.twoColAnswerText, isSelected && styles.twoColAnswerTextSelected]}>
                        {performPreloadSubstitutions(aText).replace(/<[^>]*>/g, '')}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              }

              // 'vertical-button' / 'horizontal-button': rounded panel button — matches the
              // reference <input type="submit"> markup (light gray, subtle box-shadow, ~20px
              // text), sized to its own content (minWidth as a floor) so the label never
              // wraps. Selected state is called out via the text (bold + slightly larger)
              // rather than a background change.
              return (
                <TouchableOpacity
                  key={`ans_${qIndex}_${hIndex}_${aIndex}`}
                  style={styles.panelButton}
                  onPress={onSelect}
                >
                  <Text
                    style={[styles.panelButtonText, isSelected && styles.panelButtonTextSelected]}
                  >
                    {performPreloadSubstitutions(aText).replace(/<[^>]*>/g, '')}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        );
      }

      case 2: // Multiple answers (checkboxes) — stores dataValue
        return (
          <View style={styles.answersContainer}>
            {allAnswers.map((answer, aIndex) => {
              const aText = answer.aText || answer.AText || '';
              const dataValue = answer.dataValue || answer.DataValue || aIndex.toString();
              const isSelected = isAnswerSelected(mvarName, dataValue);
              
              return (
                <TouchableOpacity
                  key={`ans_${qIndex}_${hIndex}_${aIndex}`}
                  style={styles.multipleAnswerButton}
                  onPress={() => handleAnswerChange(`q_${qIndex}_${hIndex}`, mvarName, dataValue, true)}
                >
                  <View style={styles.checkboxContainer}>
                    <View style={[styles.checkboxOuter, isSelected && styles.checkboxSelected]}>
                      {isSelected && (
                        <Text style={styles.checkboxInner}>✓</Text>
                      )}
                    </View>
                    <Text style={styles.multipleAnswerText}>
                      {performPreloadSubstitutions(aText).replace(/<[^>]*>/g, '')}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        );
        
      case 4: // Text field
        return (
          <View style={styles.answersContainer}>
            <TextInput
              style={styles.textInput}
              placeholder={performPreloadSubstitutions("Enter your answer")}
              value={getAnswerValue(mvarName) || ''}
              onChangeText={(text) => handleAnswerChange(`q_${qIndex}_${hIndex}`, mvarName, text, false)}
              multiline={false}
            />
          </View>
        );
        
      case 5: // Date picker
        // Alert.prompt is iOS-only — it silently does nothing on web/Android, which
        // makes any date question unanswerable there. Web gets a real <input type="date">;
        // iOS keeps the existing Alert.prompt (unchanged behavior); Android/other native
        // platforms fall back to a plain YYYY-MM-DD text field rather than a dead button.
        if (Platform.OS === 'web') {
          return (
            <View style={styles.answersContainer}>
              {React.createElement('input', {
                type: 'date',
                value: getAnswerValue(mvarName) || '',
                onChange: (event: any) => {
                  const text = String(event?.target?.value ?? '');
                  if (text) {
                    handleAnswerChange(`q_${qIndex}_${hIndex}`, mvarName, text, false);
                  }
                },
                style: styles.textInput,
              })}
            </View>
          );
        }

        if (Platform.OS === 'ios') {
          return (
            <View style={styles.answersContainer}>
              <TouchableOpacity
                style={styles.dateButton}
                onPress={() => {
                  const currentDate = getAnswerValue(mvarName) || new Date().toISOString().split('T')[0];
                  Alert.prompt(
                    'Enter Date',
                    'Please enter date (YYYY-MM-DD):',
                    (text) => {
                      if (text) {
                        handleAnswerChange(`q_${qIndex}_${hIndex}`, mvarName, text, false);
                      }
                    },
                    'plain-text',
                    currentDate
                  );
                }}
              >
                <Text style={styles.dateButtonText}>
                  {getAnswerValue(mvarName) || '📅 Select Date'}
                </Text>
              </TouchableOpacity>
            </View>
          );
        }

        return (
          <View style={styles.answersContainer}>
            <TextInput
              style={styles.textInput}
              placeholder="YYYY-MM-DD"
              value={getAnswerValue(mvarName) || ''}
              onChangeText={(text) => handleAnswerChange(`q_${qIndex}_${hIndex}`, mvarName, text, false)}
              maxLength={10}
            />
          </View>
        );
        
      case 6: // Memo field (textarea)
        return (
          <View style={styles.answersContainer}>
            <TextInput
              style={styles.memoInput}
              placeholder={performPreloadSubstitutions("Enter your detailed response")}
              value={getAnswerValue(mvarName) || ''}
              onChangeText={(text) => handleAnswerChange(`q_${qIndex}_${hIndex}`, mvarName, text, false)}
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>
        );
        
      default:
        return (
          <View style={styles.answersContainer}>
            <Text style={styles.unsupportedText}>
              Unsupported answer type: {typeId}
            </Text>
          </View>
        );
    }
  };

  // Parses the args following "dt:" in a
  // <<dt:baseName:years:approx:blank:preselect:styleOverride>> token. Takes the RAW
  // argsAfterFn string (not pre-split on ':') and only splits the first 5 fields via regex —
  // everything after the 5th colon is captured verbatim as the optional style-override field,
  // so a value like "font-size:20px;color:#333" (which itself contains colons) isn't mistaken
  // for extra positional fields.
  const parseDateControlToken = (argsAfterFn: string): DateControlToken | null => {
    const m = argsAfterFn.match(/^([^:]*):([^:]*):([^:]*):([^:]*):([^:]*)(?::(.*))?$/s);
    if (!m) return null;
    const [, baseName, yearsRaw, approxRaw, blankRaw, preselectRaw, styleRaw] = m;
    if (!baseName || !baseName.trim()) return null;
    return {
      fn: 'dt',
      baseName: baseName.trim(),
      yearsParam: parseInt(yearsRaw, 10) || 0,
      approxFlg: String(approxRaw ?? '').trim().toLowerCase() === 'true',
      showBlankLabels: String(blankRaw ?? '').trim().toLowerCase() === 'true',
      attemptPreselect: String(preselectRaw ?? '').trim().toLowerCase() === 'true',
      styleOverride: parseInlineStyleOverride(styleRaw),
    };
  };

  type TextOrControlSegment =
    | { type: 'html'; content: string }
    | { type: 'control'; token: DateControlToken };

  // Splits question text on recognized <<fn:...>> insertion-function tokens (see
  // INSERTION_FUNCTION_NAMES) into an ordered list of plain HTML segments and parsed
  // control tokens, so renderHtmlText can interleave live controls (e.g. date dropdowns)
  // with the surrounding HTML instead of treating the whole string as one opaque blob.
  const splitInsertionFunctionSegments = (text: string): TextOrControlSegment[] => {
    const regex = /<<([a-zA-Z]+):([^>]+)>>/g;
    const segments: TextOrControlSegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const [full, fn, argsStr] = match;
      const fnLower = fn.trim().toLowerCase();
      if (!INSERTION_FUNCTION_NAMES.has(fnLower)) continue; // not an insertion function — leave for normal HTML rendering

      if (match.index > lastIndex) {
        segments.push({ type: 'html', content: text.slice(lastIndex, match.index) });
      }
      const token = fnLower === 'dt' ? parseDateControlToken(argsStr) : null;
      segments.push(token ? { type: 'control', token } : { type: 'html', content: full });
      lastIndex = match.index + full.length;
    }
    if (lastIndex < text.length) {
      segments.push({ type: 'html', content: text.slice(lastIndex) });
    }
    return segments.length > 0 ? segments : [{ type: 'html', content: text }];
  };

  // 'dt' insertion function — renders baseNameday/baseNamemon/baseNameyr dropdowns
  // (baseNameday omitted when approxFlg) plus a combined baseName field, each saved as a
  // normal MVar-style answer via handleAnswerChange, matching every other answer control
  // on the page (shows up in the AdminStateDto Data Array debug view, PreScript-readable
  // via getData(), etc.) — see DateControlToken for the token syntax this comes from.
  const renderDateControl = (token: DateControlToken) => {
    const { baseName, yearsParam, approxFlg, showBlankLabels, attemptPreselect, styleOverride: fullStyleOverride } = token;
    // justify-content in the style field positions the whole month/day/year row (e.g.
    // "justify-content:center") rather than styling each dropdown. The control is a live
    // element rendered BETWEEN the question text's HTML segments, so it can't be nested inside
    // a surrounding <table>/<div> the author writes in cText — this is the way to center it.
    const { justifyContent: tokenJustifyContent, ...styleOverride } = fullStyleOverride;
    // An explicit justify-content in the token wins; otherwise the row follows overrideQAlign
    // (left leaves it undefined so the row keeps its default inline placement).
    const questionAlign = getQuestionAlignOverride();
    const rowJustifyContent = tokenJustifyContent
      ?? (questionAlign === 'left' ? undefined : QUESTION_ALIGN_JUSTIFY[questionAlign]);
    // Native RN style values can't carry a "px" suffix the way DOM inline styles can —
    // pre-convert once per render rather than per-dropdown.
    const nativeStyleOverride = toNativeStyleOverride(styleOverride);
    const questionId = `dt_${baseName}`;
    const dayField = `${baseName}day`;
    const monField = `${baseName}mon`;
    const yrField = `${baseName}yr`;

    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    if (yearsParam < 0) {
      for (let y = currentYear; y >= currentYear + yearsParam; y--) years.push(y);
    } else if (yearsParam > 0) {
      for (let y = currentYear; y <= currentYear + yearsParam; y++) years.push(y);
    } else {
      years.push(currentYear);
    }

    const monthOptions: Array<[string, string]> = [
      ['01', 'January'], ['02', 'February'], ['03', 'March'], ['04', 'April'],
      ['05', 'May'], ['06', 'June'], ['07', 'July'], ['08', 'August'],
      ['09', 'September'], ['10', 'October'], ['11', 'November'], ['12', 'December'],
    ];
    const dayOptions = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
    const yearOptions = years.map(String);

    const currentDay = attemptPreselect ? (getAnswerValue(dayField) || '') : '';
    const currentMon = attemptPreselect ? (getAnswerValue(monField) || '') : '';
    const currentYr = attemptPreselect ? (getAnswerValue(yrField) || '') : '';

    // Recomputes the combined baseName field from whatever's currently saved for each
    // part, blanking it out again if the set is no longer complete (e.g. after only one
    // of two/three parts has been picked so far).
    //
    // `justChanged` carries the field+value onFieldChange just wrote, straight from the
    // caller's hand — NOT re-read via getAnswerValue. handleAnswerChange's write only lands in
    // adminStateStorage + a fresh loadAdminState()/setAdminState() call; setAdminState is an
    // async React state update that hasn't flushed by the time this runs (we've only awaited
    // the storage write, not a re-render), so getAnswerValue(<the field that just changed>)
    // would still read the PRE-change value off the stale adminState closure — meaning the
    // last field the respondent picks (whichever it is) would never see its own new value here,
    // and the combined field could never complete. The other two (unchanged) fields are safe to
    // read via getAnswerValue since they were set on an earlier interaction, giving React a
    // chance to re-render (and this closure to refresh) before this one fired.
    const recomputeCombined = async (justChanged: { field: string; value: string }) => {
      const day = approxFlg ? '' : (justChanged.field === dayField ? justChanged.value : (getAnswerValue(dayField) || ''));
      const mon = justChanged.field === monField ? justChanged.value : (getAnswerValue(monField) || '');
      const yr = justChanged.field === yrField ? justChanged.value : (getAnswerValue(yrField) || '');
      const complete = approxFlg ? !!(mon && yr) : !!(day && mon && yr);
      const combined = complete ? (approxFlg ? `${yr}-${mon}` : `${yr}-${mon}-${day}`) : '';
      await handleAnswerChange(questionId, baseName, combined, false);
    };

    const onFieldChange = async (fieldName: string, value: string) => {
      await handleAnswerChange(questionId, fieldName, value, false);
      await recomputeCombined({ field: fieldName, value });
    };

    const renderSelect = (
      fieldName: string,
      currentValue: string,
      options: string[],
      labelFor: (v: string) => string,
      blankLabel: string
    ) => {
      if (Platform.OS === 'web') {
        return React.createElement(
          'select',
          {
            key: fieldName,
            // Token's optional 6th field wins over the default select styling — lets the
            // calling PreScript/content control font size, color, etc. per DateControlToken's
            // doc comment above.
            style: { ...DATE_CONTROL_SELECT_STYLE, ...styleOverride },
            value: currentValue,
            onChange: (event: any) => { void onFieldChange(fieldName, String(event?.target?.value ?? '')); },
          },
          [
            showBlankLabels
              ? React.createElement('option', { key: 'blank', value: '' }, blankLabel)
              : null,
            ...options.map((opt) => React.createElement('option', { key: opt, value: opt }, labelFor(opt))),
          ]
        );
      }

      // Native — no <select> available; cycle through options on tap, matching the inline
      // dropdown pattern already used elsewhere in this codebase (e.g. ProfileRowEditor.tsx).
      const allOptions = showBlankLabels ? ['', ...options] : options;
      const currentIndex = Math.max(0, allOptions.indexOf(currentValue));
      const cycle = () => {
        const nextIndex = (currentIndex + 1) % allOptions.length;
        void onFieldChange(fieldName, allOptions[nextIndex]);
      };
      const displayLabel = currentValue ? labelFor(currentValue) : blankLabel;
      return (
        <TouchableOpacity key={fieldName} style={styles.dateControlDropdownBtn} onPress={cycle}>
          <Text style={[styles.dateControlDropdownText, nativeStyleOverride as any]}>{displayLabel}</Text>
          <Text style={styles.dateControlDropdownChevron}>▼</Text>
        </TouchableOpacity>
      );
    };

    const monthSelect = renderSelect(
      monField, currentMon, monthOptions.map(([v]) => v),
      (v) => monthOptions.find(([mv]) => mv === v)?.[1] || v, 'Month'
    );
    const daySelect = approxFlg ? null : renderSelect(
      dayField, currentDay, dayOptions, (v) => String(parseInt(v, 10)), 'Day'
    );
    const yearSelect = renderSelect(yrField, currentYr, yearOptions, (v) => v, 'Year');

    return (
      <View
        key={`dtctrl_${baseName}`}
        // renderHtmlText renders this as a sibling of dangerouslySetInnerHTML <span> segments
        // inside one wrapping <div> so the control lands exactly where the <<dt:...>> token
        // sat in the text — but a plain View renders as display:flex on web, which (like any
        // block-level box) forces a line break before/after it, making it look like it always
        // drops below the rest of the text instead of sitting inline within it. inline-flex
        // keeps the row's own flex layout (month/day/year side by side) while letting it
        // participate in the surrounding text's inline flow, same as an inline-block would.
        // Native has no such distinction (Views are never "inline"), so leave it untouched there.
        // A justified row has to span the full line (block-level flex), so it can't be inline-flex.
        style={[
          styles.dateControlRow,
          Platform.OS === 'web' && ({ display: rowJustifyContent ? 'flex' : 'inline-flex' } as any),
          rowJustifyContent ? ({ justifyContent: rowJustifyContent } as any) : null,
        ]}
      >
        {monthSelect}
        {daySelect}
        {yearSelect}
      </View>
    );
  };

  // Helper function to render HTML text in React Native
  const renderHtmlText = (htmlString: string) => {
    // overrideQAlign — applied on top of the left-aligned defaults (QUESTION_TEXT_FONT /
    // HTML_QUESTION_TEXT_STYLE / styles.htmlTextContainer), in each rendering path below.
    const qAlign = getQuestionAlignOverride();
    const webTextStyle = { ...HTML_QUESTION_TEXT_STYLE, textAlign: qAlign };
    const questionTextStyle = { ...styles.questionText, textAlign: qAlign };
    const htmlTextContainerStyle = [styles.htmlTextContainer, { justifyContent: QUESTION_ALIGN_JUSTIFY[qAlign] }];

    if (!htmlString) {
      return <Text style={questionTextStyle}>No text available</Text>;
    }

    // Insertion-function tokens (e.g. <<dt:...>>) render as live controls rather than raw
    // HTML — split them out up front so they can be interleaved as real React children
    // instead of being swallowed into a single dangerouslySetInnerHTML/parsed-text blob.
    const segments = splitInsertionFunctionSegments(htmlString);
    const hasControls = segments.some((seg) => seg.type === 'control');

    if (Platform.OS === 'web') {
      // Render the actual markup rather than hand-parsing a handful of tags — qText/cText
      // (including whatever a PreScript sets cText to) can carry arbitrary HTML from the
      // assessment author, and the manual parser below only ever understood
      // br/b/i/u/strong/em/p. HTML_QUESTION_TEXT_STYLE is a plain object (not the
      // StyleSheet.create-produced `styles.questionText`) because a dangerouslySetInnerHTML
      // host <div> needs a literal CSS-in-JS object — same reasoning as
      // SubscriptionView.tsx's renderDescription.
      if (!hasControls) {
        return React.createElement('div', {
          style: webTextStyle,
          dangerouslySetInnerHTML: { __html: htmlString },
        });
      }

      // Mixed content — one dangerouslySetInnerHTML <span> per HTML segment, interleaved
      // with live controls; both inherit HTML_QUESTION_TEXT_STYLE from the wrapping div
      // since a dangerouslySetInnerHTML node can't host a real React component itself.
      return React.createElement(
        'div',
        { style: webTextStyle },
        segments.map((seg, i) =>
          seg.type === 'control'
            ? renderDateControl(seg.token)
            : React.createElement('span', { key: `h_${i}`, dangerouslySetInnerHTML: { __html: seg.content } })
        )
      );
    }

    // Native fallback — no HTML renderer available without an extra dependency, so keep
    // the manual tag parser here.
    // Parse HTML into an array of segments.  Each segment is either:
    //   { type: 'text', content, style }   — a run of styled (or plain) text
    //   { type: 'br' }                     — a line break (<br>)
    //
    // Processing in segments — rather than converting <br> to '\n' — avoids the
    // React-Native-Web problem where '\n' inside a <span> is invisible (no
    // white-space:pre-wrap) and text after the newline disappears on screen.
    const parseHtml = (rawText: string) => {
      // Decode common HTML entities first
      let text = rawText
        .replace(/&nbsp;/g, '\u00a0')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");

      // Normalise <p> tags → double-br
      text = text.replace(/<p[^>]*>/gi, '').replace(/<\/p>/gi, '<br><br>');

      // Build a token list by splitting on <br>, <b>, <i>, <u>, <strong>, <em> tags.
      // Split pattern captures the delimiter so we can inspect each token.
      const tokenRegex = /(<br\s*\/?>|<(b|i|u|strong|em)(?:[^>]*)>.*?<\/\2>)/gi;
      const parts: JSX.Element[] = [];
      let partKey = 0;
      let lastIndex = 0;

      // Iterate over all recognised tag occurrences in document order.
      // We use a combined approach: first split on <br> to handle line breaks,
      // then scan each chunk for inline styling tags.
      const processChunk = (chunk: string, baseKey: number): { els: JSX.Element[], nextKey: number } => {
        let k = baseKey;
        const inlineRegex = /<(b|i|u|strong|em)(?:[^>]*)>(.*?)<\/\1>/gi;
        let m;
        let ci = 0;
        const inlineChildren: (JSX.Element | string)[] = [];
        let hasInline = false;

        while ((m = inlineRegex.exec(chunk)) !== null) {
          hasInline = true;
          // plain text before this tag
          if (m.index > ci) {
            const before = chunk.substring(ci, m.index);
            if (before) inlineChildren.push(before);
          }
          // styled text — nested <Text> renders inline inside parent <Text>
          const tag = m[1].toLowerCase();
          const content = m[2];
          let textStyle: any = { ...questionTextStyle };
          if (tag === 'b' || tag === 'strong') textStyle = { ...textStyle, fontWeight: 'bold' as any };
          else if (tag === 'i' || tag === 'em')  textStyle = { ...textStyle, fontStyle: 'italic' as any };
          else if (tag === 'u')                  textStyle = { ...textStyle, textDecorationLine: 'underline' as any };
          inlineChildren.push(<Text key={k++} style={textStyle}>{content}</Text>);
          ci = m.index + m[0].length;
        }

        if (!hasInline) {
          // No inline tags — plain text chunk
          const els = chunk ? [<Text key={k++} style={questionTextStyle}>{chunk}</Text>] : [];
          return { els, nextKey: k };
        }

        // Remaining plain text after last tag
        if (ci < chunk.length) {
          const rest = chunk.substring(ci);
          if (rest) inlineChildren.push(rest);
        }

        // Wrap all inline children in a single <Text> so styled spans render inline
        // (sibling <Text> inside a <View> are block-level in React Native)
        return {
          els: [<Text key={k++} style={questionTextStyle}>{inlineChildren}</Text>],
          nextKey: k,
        };
      };

      // Split the whole text on <br> tags, process each line-chunk separately,
      // and insert View line-break separators between chunks.
      const brSplit = text.split(/<br\s*\/?>\s*/i);
      brSplit.forEach((chunk, idx) => {
        const { els, nextKey } = processChunk(chunk, partKey);
        partKey = nextKey;
        els.forEach(el => parts.push(el));
        // Add a visible line-break separator between chunks (not after the last one)
        if (idx < brSplit.length - 1) {
          parts.push(<View key={partKey++} style={{ height: 8 }} />);
        }
      });

      if (parts.length === 0) {
        return <Text style={questionTextStyle}>{text}</Text>;
      }
      return <>{parts}</>;
    };

    if (!hasControls) {
      return (
        <View style={htmlTextContainerStyle}>
          {parseHtml(htmlString)}
        </View>
      );
    }

    // Mixed content — parse each HTML segment independently (keeping parseHtml's own key
    // counter scoped per segment via the outer Fragment key) and interleave live controls.
    return (
      <View style={htmlTextContainerStyle}>
        {segments.map((seg, i) =>
          seg.type === 'control'
            ? renderDateControl(seg.token)
            : <React.Fragment key={`h_${i}`}>{parseHtml(seg.content)}</React.Fragment>
        )}
      </View>
    );
  };




  // Question layout overrides — read directly from the AdminStateDto Data array (set via
  // putData(), a Preload, or any other route into adminState.data) rather than the PreScript
  // JS interface, so once set they stay in effect across pages without needing to be
  // re-asserted by every page's script. Blank/absent falls back to the built-in default.
  const getAdminDataValue = (name: string): string =>
    adminState?.data?.find(d => d.name === name)?.value?.trim() || '';

  // overrideQHeight — pixel height override for the question/answer content area.
  const getQuestionHeightOverride = (): number | undefined => {
    const raw = getAdminDataValue('overrideQHeight');
    const parsed = parseInt(raw, 10);
    return raw !== '' && !isNaN(parsed) ? parsed : undefined;
  };

  // overrideQAlign — 'left' | 'center' | 'right' alignment of the question text (and inline
  // controls). Blank/absent/unrecognized is left, the default.
  const getQuestionAlignOverride = (): QuestionAlign => {
    const raw = getAdminDataValue('overrideQAlign').toLowerCase();
    return raw === 'center' || raw === 'right' ? raw : 'left';
  };

  // overrideQWidth — pixel width override for the main question card.
  const getQuestionWidthOverride = (): number | undefined => {
    const raw = getAdminDataValue('overrideQWidth');
    const parsed = parseInt(raw, 10);
    return raw !== '' && !isNaN(parsed) ? parsed : undefined;
  };

  // overrideQLayout — overrides AdminPageDto.Layout (question/answer layout & skin-driven
  // styling variant, not yet implemented downstream). Non-blank override wins; otherwise
  // falls back to the current page's own Layout value.
  const getEffectiveQLayout = (currentAdminPage: any): string => {
    const override = getAdminDataValue('overrideQLayout');
    return override !== '' ? override : (currentAdminPage?.Layout || currentAdminPage?.layout || '');
  };

  // Single-answer (MvarTypeId=1) answer section layout — recognizes the 3 explicit
  // overrideQLayout values below; anything else (blank, or an unrecognized future
  // AdminPageDto.Layout value) falls back to today's skinType-driven `answerLayout`
  // default so behavior is unchanged for pages that don't set an override.
  type AnswerLayoutMode = 'vertical-button' | 'horizontal-button' | '2-column';
  const getAnswerLayoutMode = (currentAdminPage: any): AnswerLayoutMode => {
    const override = getEffectiveQLayout(currentAdminPage).trim().toLowerCase();
    if (override === 'vertical-button' || override === 'horizontal-button' || override === '2-column') {
      return override;
    }
    return answerLayout === 'vertical2column' ? '2-column' : 'vertical-button';
  };

  // Render AdminSegment-based page content
  const renderAdminSegmentPage = () => {

    const currentAdminPage = getCurrentAdminPage();
    if (!currentAdminPage) {

      return null;
    }

    // Handle both camelCase and PascalCase properties
    const pageName = currentAdminPage.name || currentAdminPage.Name;
    const pageNum = currentAdminPage.pageNum || currentAdminPage.PageNum;
    const pageType = currentAdminPage.type || currentAdminPage.Type;
    const questions = currentAdminPage.questions || currentAdminPage.Questions;

    // Rendering AdminSegment page

    // Show questions only for question pages (type >= 3)
    const pageTypeNum = parseInt(pageType || "1");

    // overrideQHeight (AdminStateDto Data array) — pixel height for just the question TEXT
    // display (styles.htmlQuestionContainer below), not the answer controls under it;
    // blank/absent uses htmlQuestionContainer's default (auto-height). With a height set, the
    // text sits in a nested ScrollView instead of a clipped View, so text that wraps to more
    // lines than fit (e.g. a narrow phone screen) scrolls rather than being cut off, and stays
    // top-aligned rather than centered vertically.
    const qHeightOverride = getQuestionHeightOverride();
    const renderQuestionTextContainer = (children: React.ReactNode) =>
      qHeightOverride !== undefined ? (
        <ScrollView
          style={[styles.htmlQuestionScroll, { height: qHeightOverride }]}
          nestedScrollEnabled
        >
          {children}
        </ScrollView>
      ) : (
        <View style={styles.htmlQuestionContainer}>{children}</View>
      );

    if (pageTypeNum >= 3 && questions && questions.length > 0) {

      return (
        <ScrollView style={[styles.content, isPhone && styles.contentPhone]}>
          {questions && questions.map((questionItem: any, qIndex: number) => {

            
            const question = questionItem.question || questionItem.Question;
            if (!question) {
              return null;
            }

            // Try both camelCase and PascalCase, and handle null values
            const htmlQuestions = question.htmlQuestion || question.HtmlQuestion || [];
            
            // If HtmlQuestion is null or undefined, create a default question from MvarName
            if (!htmlQuestions || !Array.isArray(htmlQuestions) || htmlQuestions.length === 0) {
              console.log(`⚠️ No HTML questions array for question ${qIndex}, using fallback`);
              
              // Create a fallback question using the MvarName
              const mvarName = question.MvarName || question.mvarName || `Question ${qIndex + 1}`;
              return (
                <View key={`q_${qIndex}_fallback`} style={[styles.questionContainer, isPhone && styles.questionContainerPhone]}>
                  {renderQuestionTextContainer(renderHtmlText(mvarName))}
                  <Text style={styles.pageInfoText}>
                    (No HTML question data available - showing variable name)
                  </Text>
                </View>
              );
            }

            return htmlQuestions.map((htmlQuestion: any, hIndex: number) => {
              // Handle both camelCase and PascalCase properties
              const qText = htmlQuestion.qText || htmlQuestion.QText;
              const languageTypeId = htmlQuestion.languageTypeId || htmlQuestion.LanguageTypeId || 
                                   adminSegment.languageTypeId || adminSegment.LanguageTypeId || languageId;
              const mvarName = question.MvarName || question.mvarName;
              const mvarTypeId = question.MvarTypeId || question.mvarTypeId;
              const uvarTypeId = question.UvarTypeId || question.uvarTypeId || '';
              const isUvarQuestion = !!uvarTypeId && uvarTypeId !== '0';
              // Uvar questions answer exclusively from HtmlUvarAns (the account-restricted set,
              // or the content-authored list as a fallback) — HtmlAnswer is ignored for these so
              // a uvar question never shows both its own answers and the uvar's.
              const htmlAnswers = isUvarQuestion ? [] : (htmlQuestion.htmlAnswer || htmlQuestion.HtmlAnswer || []);
              const contentUvarAnswers = htmlQuestion.htmlUvarAns || htmlQuestion.HtmlUvarAns || [];
              const htmlUvarAnswers = resolveUvarAnswers(uvarTypeId, contentUvarAnswers, effectiveUVars);
              if (isUvarQuestion && htmlUvarAnswers === contentUvarAnswers) {
                warnUvarFallback(uvarTypeId, mvarName);
              }
              
              // Simplified logging: Single entry per question
              // console.log(`🔍 Q${qIndex}.${hIndex}: ${mvarName} (type: ${mvarTypeId}) - ${htmlAnswers.length + htmlUvarAnswers.length} answers`);
              
              // Show English questions (languageTypeId === "1")
              if (languageTypeId === "1" || languageTypeId === 1) {
                // Use cText from PreScript if available, otherwise use original qText
                const displayText = (adminState as any)?.cText || qText;
                const substitutedText = displayText ? performPreloadSubstitutions(displayText) : 'Question text not available';
                return (
                  <View key={`q_${qIndex}_${hIndex}`} style={[styles.questionContainer, isPhone && styles.questionContainerPhone]}>
                    {renderQuestionTextContainer(renderHtmlText(substitutedText))}

                    {/* Render answers based on MvarTypeId */}
                    {renderAnswersByType(mvarName, mvarTypeId, htmlAnswers, htmlUvarAnswers, qIndex, hIndex, currentAdminPage)}
                  </View>
                );
              }
              return null;
            });
          })}
          
          {(!questions || questions.length === 0) && (
            <View style={[styles.questionContainer, isPhone && styles.questionContainerPhone]}>
              <Text style={styles.questionText}>No questions found for this page.</Text>
            </View>
          )}
        </ScrollView>
      );
    }

    // For other page types, show page info
    return (
      <ScrollView style={[styles.content, isPhone && styles.contentPhone]}>
        <View style={[styles.questionContainer, isPhone && styles.questionContainerPhone]}>

          {/* Use cText from PreScript if available for pageType 1, otherwise show page info */}
          {pageTypeNum === 1 && (adminState as any)?.cText ? 
            renderHtmlText((adminState as any).cText) :
            <Text style={styles.questionText}>
              {`Page Type ${pageType}: ${pageName || `Page ${pageNum}`}`}
            </Text>
          }
          {questions && questions.length > 0 && pageTypeNum !== 1 && (
            <Text style={styles.pageInfoText}>
              This page has {questions.length} question(s) but is not a question page (type 3).
            </Text>
          )}

          {/* enableConsent PreScript flag — Agree/Exit answer set in place of Continue */}
          {isConsentGateActive() ? (
            <View style={styles.consentButtonRow}>
              <TouchableOpacity
                style={styles.consentAgreeButton}
                onPress={() => { void handleConsentAnswer('1'); }}
                accessibilityRole="button"
                accessibilityLabel="Agree"
              >
                <Text style={styles.consentAgreeButtonText}>Agree</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.consentExitButton}
                onPress={() => { void handleConsentAnswer('2'); }}
                accessibilityRole="button"
                accessibilityLabel="Exit"
              >
                <Text style={styles.consentExitButtonText}>Exit</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      </ScrollView>
    );
  };

  // Check if we have AdminSegment data loaded




  
  // Helper function to format segment display - looks up correct segment info for each entry
  const formatSegmentDisplay = (segment: string) => {
    // Map segment IDs to their display names
    const segmentDisplayMap: { [key: string]: string } = {
      '1987': 'PHQ-9 Depression Screening (1987)',
      '1988': 'GAD-7 Anxiety Screening (1988)'
    };
    
    // Return mapped display name or fallback to segment ID
    return segmentDisplayMap[segment] || segment;
  };
  
  // Helper function to get current page questions
  const getCurrentPageQuestions = () => {
    
    // Try to get questions from AdminSegment first
    const currentAdminPage = getCurrentAdminPage();
    
    if (currentAdminPage) {
      const questions = currentAdminPage.questions || currentAdminPage.Questions;
      if (questions && questions.length > 0) {
        return questions;
      }
    }
    
    // Try to get from contentTables
    if (contentTables.questions && adminState) {
      const currentPageNum = adminState.pageNum;
      
      const pageQuestions: any[] = [];
      contentTables.questions.getAllKeys().forEach((key: any) => {
        const question = contentTables.questions!.get(key);
        if (question && (question.htmlpage_id === currentPageNum || question.htmlquestion_page_id === currentPageNum)) {
          pageQuestions.push(question);
        }
      });
      
      if (pageQuestions.length > 0) {
        return pageQuestions;
      }
    }
    
    // Fallback to currentQuestions state (legacy)
    console.log('🐛 Fallback to current questions state:', currentQuestions);
    console.log('🐛 currentQuestions detailed structure:', currentQuestions?.map((q: any, idx: number) => ({
      index: idx,
      keys: Object.keys(q),
      hasName: !!q.Name,
      hasMvarName: !!q.mvarName,
      hasMVarName: !!q.MvarName,
      hasHtmlQuestionMvarName: !!q.htmlquestion_mvar_name,
      hasHtmlQuestionId: !!q.htmlquestion_id,
      questionStructure: q
    })));
    return currentQuestions || [];
  };

  // Render page logic editor component
  const renderPageLogic = () => {
    // Essential validation - need all components available
    if (!editPageLogicEnabled || !adminState || !adminSegment) {
      return null;
    }

    const currentAdminPage = getCurrentPageFromSegment();
    const preScript = currentAdminPage ? (currentAdminPage.preScript || currentAdminPage.PreScript || '') : '';
    
    // Additional validation - ensure we have a valid page
    if (!currentAdminPage) {
      return null;
    }
    
    // Handle continue button press - use the main handleContinue function
    const handleContinueLocal = () => {
      handleContinue();
    };

    const handleScriptChange = (newScript: string) => {
      // Compare against the entry value (which may be a saved edit from adminSegment, not the raw
      // page preScript) so that clearing a saved edit back to blank is correctly treated as dirty.
      const entryValue = pageEntryPreScriptRef.current;
      // console.log('✏️ SCRIPT-CHANGE:', { newScript: JSON.stringify(newScript), entryValue: JSON.stringify(entryValue), willBeDirty: newScript !== entryValue });
      setEditedPreScriptWithRef(newScript);
      setPreScriptDirty(newScript !== entryValue);
    };

    return (
      <View style={styles.debugContainer}>
        <View style={styles.pageLogicHeader}>
          <Text style={styles.debugTitle}>⚙️ Page Logic</Text>
          <TouchableOpacity 
            style={[styles.continueButton, { opacity: 1}]} 
            onPress={handleContinueLocal}
          >
            <Text style={styles.continueButtonText}>
              {/* {preScriptExecuted ? 'Executed ✅' : 'Continue ▶️'} */}
              {'Continue ▶️'}
            </Text>
          </TouchableOpacity>
        </View>
        
        <View style={styles.debugSection}>
          <Text style={styles.debugSectionTitle}>
            PreScript JavaScript — {currentAdminPage.htmlpage_title || currentAdminPage.name || currentAdminPage.Name || ''} {preScriptDirty ? '(Modified ⚠️)' : ''} [htm:{currentAdminPage.htmPageId || currentAdminPage.HtmPageId || ''}] [node:{currentAdminPage.NodePageId || ''}]:
          </Text>
          <TextInput
            style={styles.scriptEditor}
            value={editedPreScript !== undefined ? editedPreScript : preScript}
            onChangeText={handleScriptChange}
            multiline
            placeholder="No PreScript defined for this page"
            placeholderTextColor="#999"
          />
          {preScriptDirty && (
            <Text style={styles.dirtyIndicator}>
              ⚠️ Script modified - will be used when Continue is pressed
            </Text>
          )}
        </View>
        
        {/* Save button and status */}
        <View style={styles.debugSection}>
          <TouchableOpacity 
            style={[
              styles.continueButton, 
              { 
                opacity: isSavingPageLogic ? 0.5 : 1,
                backgroundColor: isSavingPageLogic ? '#666' : '#007AFF'
              }
            ]} 
            onPress={savePageLogicToBackend}
            disabled={isSavingPageLogic}
          >
            <Text style={styles.continueButtonText}>
              {isSavingPageLogic ? 'Saving...' : `💾 Save to Backend${dirtyPreScriptsCount > 0 ? ` (${dirtyPreScriptsCount})` : ''}`}
            </Text>
          </TouchableOpacity>
          
          {savePageLogicResult && (
            <Text style={[
              styles.dirtyIndicator,
              { color: savePageLogicResult.success ? '#28a745' : '#dc3545' }
            ]}>
              {savePageLogicResult.success ? '✅' : '❌'} {savePageLogicResult.message}
            </Text>
          )}
        </View>
        <Text style={styles.hintText}>
          hint: getData(), getAdmin(), getPreload(), putData(), putAdmin(), putPreload(), skipPage, gotoPage, skipNode, cText, enableConsent, disableBack, disableSave, enableDone, renderReport, log()
        </Text>
      </View>
    );
  };

  // Render calc logic editor component
  const renderCalcLogic = () => {
    
    // Essential validation - need all components available
    if (!editCalcLogicEnabled || !adminSegment) {
      return null;
    }

    const calcScript = adminSegment.calcScript || adminSegment.CalcScript || '';

    // Determine if this is a node segment
    const segTypeForRender = String(adminSegment.SegmentType || adminSegment.segmentType || '');
    const isNodeSegForRender = segTypeForRender === '5' || segTypeForRender === 'PAS';
    const nodeIdxForRender = adminState?.nodeIndex ?? 0;
    const nodesForRender = adminSegment.Nodes || adminSegment.nodes;
    const currentNodeForRender = nodesForRender?.[nodeIdxForRender];
    const originalNodeCalcScript = currentNodeForRender?.calcScript || currentNodeForRender?.CalcScript || '';
    const nodeIdForRender = currentNodeForRender?.nodeId || currentNodeForRender?.NodeId || String(nodeIdxForRender);
    const nodeNameForRender = currentNodeForRender?.name || currentNodeForRender?.Name || currentNodeForRender?.nodeName || currentNodeForRender?.NodeName || '';
    const nodeTypeForRender = currentNodeForRender?.nodeType || currentNodeForRender?.NodeType || currentNodeForRender?.type || currentNodeForRender?.Type || '';
    const nodeHeaderDetail = [nodeNameForRender, nodeIdForRender, nodeTypeForRender].filter(Boolean).join(' | ');
    
    const handleScriptChange = (newScript: string) => {
      setEditedCalcScript(newScript);
      setCalcScriptDirty(newScript !== calcScript);
    };

    const handleNodeScriptChange = (newScript: string) => {
      setEditedNodeCalcScriptWithRef(newScript);
      setNodeCalcScriptDirty(newScript !== originalNodeCalcScript);
      // Persist to storage asynchronously
      saveNodeCalcScriptToStorage(nodeIdForRender, newScript).catch(() => {});
    };

    return (
      <View style={styles.debugContainer}>
        <View style={styles.pageLogicHeader}>
          <Text style={styles.debugTitle}>📊 Calc Logic</Text>
        </View>

        {/* Node CalcScript section — only shown for node segments (SegmentType 5 / PAS) */}
        {isNodeSegForRender && (
          <View style={styles.debugSection}>
            <Text style={styles.debugSectionTitle}>
              Node CalcScript JavaScript ({nodeHeaderDetail || `Node ${nodeIdxForRender}`}{nodeCalcScriptDirty ? ' — Modified ⚠️' : ''}):
            </Text>
            <TextInput
              style={styles.scriptEditor}
              value={editedNodeCalcScript}
              onChangeText={handleNodeScriptChange}
              multiline
              placeholder="No CalcScript defined for this node"
              placeholderTextColor="#999"
            />
            {nodeCalcScriptDirty && (
              <Text style={styles.dirtyIndicator}>
                ⚠️ Node script modified — use Save to Backend to persist
              </Text>
            )}
            {!nodeCalcScriptDirty && !!editedNodeCalcScript && (
              <Text style={styles.singleStepIndicator}>
                📊 Node CalcScript staged — will execute when leaving this node
              </Text>
            )}
          </View>
        )}
        
        <View style={styles.debugSection}>
          <Text style={styles.debugSectionTitle}>
            {isNodeSegForRender ? 'Segment ' : ''}CalcScript JavaScript {calcScriptDirty ? '(Modified ⚠️)' : ''}:
          </Text>
          <TextInput
            style={styles.scriptEditor}
            value={editedCalcScript || calcScript}
            onChangeText={handleScriptChange}
            multiline
            placeholder="No CalcScript defined for this segment"
            placeholderTextColor="#999"
          />
          {calcScriptDirty && (
            <Text style={styles.dirtyIndicator}>
              ⚠️ Script modified — use Save to Backend to persist
            </Text>
          )}
          {!calcScriptDirty && !!editedCalcScript && (
            <Text style={styles.singleStepIndicator}>
              📊 CalcScript saved to AdminSegment - will execute during segment transitions
            </Text>
          )}
        </View>
        
        {/* Save button and status */}
        <View style={styles.debugSection}>
          <TouchableOpacity 
            style={[
              styles.continueButton, 
              { 
                opacity: isSavingCalcLogic ? 0.5 : 1,
                backgroundColor: isSavingCalcLogic ? '#666' : '#007AFF'
              }
            ]} 
            onPress={saveCalcLogicToBackend}
            disabled={isSavingCalcLogic}
          >
            <Text style={styles.continueButtonText}>
              {isSavingCalcLogic ? 'Saving...' : `💾 Save to Backend${dirtyCalcScriptsCount > 0 ? ` (${dirtyCalcScriptsCount})` : ''}`}
            </Text>
          </TouchableOpacity>
          
          {saveCalcLogicResult && (
            <Text style={[
              styles.dirtyIndicator,
              { color: saveCalcLogicResult.success ? '#28a745' : '#dc3545' }
            ]}>
              {saveCalcLogicResult.success ? '✅' : '❌'} {saveCalcLogicResult.message}
            </Text>
          )}
        </View>
      </View>
    );
  };

  // Render debug info component
  const renderDebugInfo = () => {
    if (!debugInfoEnabled || !adminState) {
      if (debugInfoEnabled && !adminState) {
        console.log('🐛 Debug enabled but no adminState available');
      }
      return null;
    }



    // Get current page info from AdminSegment
    const currentAdminPage = getCurrentAdminPage();
    const currentPageName = currentAdminPage ? (currentAdminPage.name || currentAdminPage.Name || 'Unknown Page') : 'No Page';
    const currentPageNum = currentAdminPage ? (currentAdminPage.pageNum || currentAdminPage.PageNum || '?') : '?';
    const currentPageType = currentAdminPage ? (currentAdminPage.type || currentAdminPage.Type || '?') : '?';

    return (
      <View style={styles.debugContainer}>
        <Text style={styles.debugTitle}>🐛 Debug Information</Text>
        
        {/* AdminStateDto Data Array - Dictionary behavior: same MVar names update existing entries */}
        <View style={styles.debugSection}>
          <Text style={styles.debugSectionTitle}>AdminStateDto Data Array ({adminState.data?.length || 0} items):</Text>
          <Text style={styles.debugSubtitle}>💡 Dictionary mode: Same MVar updates value, different MVars add entries</Text>
          <View style={styles.debugDataItem}>
            {adminState.data && adminState.data.length > 0 ? (
              <Text style={styles.debugDataValue}>
                {adminState.data.map((dataItem, index) => `${dataItem.name}: ${dataItem.value}`).join('\n')}
              </Text>
            ) : (
              <View>
                <Text style={styles.debugText}>No data entries found</Text>
                <Text style={styles.debugText}>💡 Data entries are created when you answer questions</Text>
              </View>
            )}
          </View>
        </View>

        {/* Question layout overrides (overrideQHeight/overrideQWidth/overrideQAlign/overrideQLayout) —
            resolved from the AdminStateDto Data array above. overrideQLayout has no
            rendering effect yet (AdminPageDto.Layout isn't implemented downstream); shown
            here so the resolved value can be verified ahead of that work. */}
        <View style={styles.debugSection}>
          <Text style={styles.debugSectionTitle}>Question Layout Overrides:</Text>
          <View style={styles.debugDataItem}>
            <Text style={styles.debugDataValue}>
              {[
                ['overrideQHeight', getQuestionHeightOverride()?.toString() ?? '(default)'],
                ['overrideQWidth', getQuestionWidthOverride()?.toString() ?? '(default)'],
                ['overrideQAlign', getAdminDataValue('overrideQAlign') ? getQuestionAlignOverride() : '(default: left)'],
                ['overrideQLayout (effective)', getEffectiveQLayout(currentAdminPage) || '(default)'],
              ].map(([k, v]) => `${k}: ${v}`).join('\n')}
            </Text>
          </View>
        </View>

        {/* AdminStateDto BackTable Array */}
        <View style={styles.debugSection}>
          <Text style={styles.debugSectionTitle}>AdminStateDto BackTable Array ({adminState.backTable?.length || 0} items):</Text>
          <View style={styles.debugDataItem}>
            {adminState.backTable && adminState.backTable.length > 0 ? (
              <Text style={styles.debugDataValue}>
                {adminState.backTable.map((backItem, index) => 
                  `${backItem.name} | Seg: ${backItem.segment} | Node: ${backItem.node ?? ''}(${backItem.nodeIndex ?? '—'}) | Page: ${backItem.pageNum}`
                ).join('\n')}
              </Text>
            ) : (
              <Text style={styles.debugText}>No back table entries found</Text>
            )}
          </View>
        </View>

        {/* Admin State Properties (getAdmin) */}
        <View style={styles.debugSection}>
            <Text style={styles.debugSectionTitle}>Admin State Properties — getAdmin():</Text>
            <Text style={styles.debugSubtitle}>💡 Read these in scripts with getAdmin("fieldName")</Text>
            <View style={styles.debugDataItem}>
              <Text style={styles.debugDataValue}>
                {[
                  ['profileId',     adminState.profileId],
                  ['pageNum',       adminState.pageNum],
                  ['node',          `${adminState.node ?? ''} (${adminState.nodeIndex ?? ''})`],
                  ['segment',       adminState.segment],
                  ['segmentList',   adminState.segmentList],
                  ['surveyId',      adminState.surveyId],
                  ['sourceId',      adminState.sourceId],
                  ['dName',         adminState.dName],
                  ['languageTypeId',adminState.languageTypeId],
                  ['recId',         adminState.recId],
                  ['status',        adminState.status],
                  ['launchId',      adminState.launchId],
                  ['adminRequestId',adminState.adminRequestId],
                  ['patEvntId',     adminState.patEvntId],
                  ['patRefId',      adminState.patRefId],
                  ['dueDate',       adminState.dueDate],
                  ['modified',      adminState.modified],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join('\n')}
              </Text>
            </View>
          </View>

        {/* Segment Preloads (getPreload) — shows the EFFECTIVE value (runtime putPreload
            overrides applied on top of the segment's original static list, see
            runtimePreloadOverridesRef), not just what the backend originally sent. */}
        <View style={styles.debugSection}>
            <Text style={styles.debugSectionTitle}>Segment Preloads — getPreload():</Text>
            <Text style={styles.debugSubtitle}>💡 Read these in scripts with getPreload("fieldCode", "langId"). 🔄 = overridden via putPreload. Account Preloads (below) win on a fieldCode collision.</Text>
            <View style={styles.debugDataItem}>
              {(() => {
                const rawPreloads: any[] = adminSegment?.preloads || adminSegment?.Preloads || [];
                const overrides = runtimePreloadOverridesRef.current;
                const typeLabel: Record<string, string> = {
                  '1': 'Boolean',
                  '2': 'Number',
                  '4': 'List',
                  '5': 'N/A answer',
                  '6': 'Trigger',
                };

                const seenFieldCodes = new Set<string>();
                const lines = rawPreloads.map((p: any) => {
                  const rawFc = p.fieldCode  || p.FieldCode  || '?';
                  const fc  = rawFc.replace(/^<<|>>$/g, ''); // strip delimiters if stored as <<fieldCode>>
                  seenFieldCodes.add(fc);
                  const isOverridden = overrides.has(fc);
                  const v = isOverridden ? overrides.get(fc) : (p.value || p.Value || '');
                  const lid = p.languageTypeId || p.LanguageTypeId || '';
                  const typeId = String(p.type || p.Type || '');
                  const typeStr = typeId ? ` [${typeLabel[typeId] ?? `type:${typeId}`}]` : '';
                  const langStr = lid ? ` (lang:${lid})` : '';
                  return `${fc}${typeStr}${langStr}: ${v}${isOverridden ? ' 🔄' : ''}`;
                });

                // putPreload can introduce a brand-new fieldCode that was never in the
                // segment's original list at all (same as putData) — surface those too.
                overrides.forEach((value, fc) => {
                  if (!seenFieldCodes.has(fc)) {
                    lines.push(`${fc} [putPreload-only]: ${value} 🔄`);
                  }
                });

                if (lines.length === 0) {
                  return <Text style={styles.debugText}>No segment preloads defined</Text>;
                }
                return <Text style={styles.debugDataValue}>{lines.join('\n')}</Text>;
              })()}
            </View>
          </View>

        {/* Account Preloads (AccountPlaDto.Preloads, e.g. surveyblist/adminreportid — configured
            via Account > Assessment Options) — resolved once for this assessment at session
            creation and carried on adminState.accountPreloads, so getPreload() can see these on
            EVERY segment, not just whichever one is currently loaded. They win over a
            same-fieldCode segment preload (see mergePreloadsIntoMap). */}
        <View style={styles.debugSection}>
            <Text style={styles.debugSectionTitle}>Account Preloads (PLA) — getPreload():</Text>
            <Text style={styles.debugSubtitle}>💡 Configured in Account &gt; Assessment Options; persists across all segments.</Text>
            <View style={styles.debugDataItem}>
              {(() => {
                const accountPreloads: any[] = adminState?.accountPreloads || [];
                if (accountPreloads.length === 0) {
                  return <Text style={styles.debugText}>No account preloads configured</Text>;
                }
                const lines = accountPreloads.map((p: any) => {
                  const rawFc = p.fieldCode || p.FieldCode || '?';
                  const fc = rawFc.replace(/^<<|>>$/g, '');
                  const v = p.value || p.Value || '';
                  const lid = p.languageTypeId || p.LanguageTypeId || '';
                  const langStr = lid ? ` (lang:${lid})` : '';
                  return `${fc}${langStr}: ${v}`;
                });
                return <Text style={styles.debugDataValue}>{lines.join('\n')}</Text>;
              })()}
            </View>
          </View>
      </View>
    );
  };

  if (adminSegment) {

    const pages = adminSegment.pages || adminSegment.Pages;
    const segType = String(adminSegment.SegmentType || adminSegment.segmentType || '');
    const isNodeSeg = segType === '5' || segType === 'PAS';
    const totalPages = isNodeSeg
      ? ((adminSegment.Nodes || adminSegment.nodes) as any[] || []).reduce(
          (sum: number, n: any) => sum + ((n.Pages || n.pages) as any[] || []).length, 0)
      : (pages ? pages.length : 0);
    const pageNumInt = parseInt(String(adminState?.pageNum || '1'), 10) || 1;
    const progressPercent = totalPages > 0 ? Math.min(100, Math.max(0, (pageNumInt / totalPages) * 100)) : 0;
    const questionWidthOverride = getQuestionWidthOverride();

    return (
      <View style={styles.container}>
        {renderSkinHeader()}

        {/* Save & Finish Later — web-friendly confirmation dialog */}
        <Modal
          visible={showCancelDialog}
          transparent
          animationType="fade"
          onRequestClose={() => setShowCancelDialog(false)}
        >
          <View style={styles.dialogBackdrop}>
            <View style={styles.dialogCard}>
              <Text style={styles.dialogTitle}>Save and Finish Later</Text>
              <Text style={styles.dialogMessage}>
                Your progress will be saved. You can continue this assessment later.
              </Text>
              <View style={styles.dialogButtons}>
                <TouchableOpacity
                  style={styles.dialogBtnSecondary}
                  onPress={() => setShowCancelDialog(false)}
                >
                  <Text style={styles.dialogBtnSecondaryText}>Continue Assessment</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.dialogBtnPrimary}
                  onPress={async () => { setShowCancelDialog(false); await doSaveAndExit(); }}
                >
                  <Text style={styles.dialogBtnPrimaryText}>{'Save & Finish Later'}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {/* Question card — page content + footer live inside this one bordered/shadowed card.
            overrideQWidth (AdminStateDto Data array) overrides the card's max width; blank/
            absent uses styles.questionCard's default. */}
        <View style={[
          styles.questionCard,
          isPhone && styles.questionCardPhone,
          questionWidthOverride !== undefined && { maxWidth: questionWidthOverride },
        ]}
        >
          {renderAdminSegmentPage()}

          {/* Footer — row 1: Back / Continue / spacer (3 columns); row 2: full-width
              'Save and finish later' so the link never has to wrap inside a 1/3-width cell.
              No border/background of its own (inherits the card's). */}
          <View style={[styles.questionFooter, isPhone && styles.questionFooterPhone]}>
            <View style={styles.questionFooterRow}>
              <View style={styles.questionFooterCol}>
                {/* disableBack PreScript flag — hidden, but the column keeps its layout slot */}
                {isBackButtonHiddenByScript() ? null : (
                  <TouchableOpacity
                    onPress={goToPreviousPage}
                    disabled={!hasPreviousQuestionPage()}
                    style={styles.footerBackButton}
                    accessibilityRole="button"
                    accessibilityLabel="Back"
                  >
                    {/* Same undecorated "← Back" link ReportView uses (reportOutputBackText) */}
                    <Text
                      style={[styles.footerBackButtonText, !hasPreviousQuestionPage() && styles.footerBackButtonTextDisabled]}
                      numberOfLines={1}
                    >
                      ← Back
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
              <View style={[styles.questionFooterCol, styles.questionFooterColCenter]}>
                {isConsentGateActive() ? null : shouldHideNextButton() ? (
                  <View style={styles.footerProgressTrack}>
                    <View style={[styles.footerProgressFill, { width: `${progressPercent}%` }]} />
                  </View>
                ) : (
                  <TouchableOpacity
                    onPress={handleNextOrDone}
                    disabled={!(hasNextQuestionPage() || (isLastPageOfLastSegment() && !!isDoneEnabledByScript())) || isDoneProcessing}
                    style={[
                      styles.footerContinueButton,
                      (!(hasNextQuestionPage() || (isLastPageOfLastSegment() && !!isDoneEnabledByScript())) || isDoneProcessing) && styles.footerContinueButtonDisabled,
                    ]}
                  >
                    {isDoneProcessing ? (
                      <ActivityIndicator size="small" color="#000000" />
                    ) : (
                      <Text style={styles.footerContinueButtonText}>
                        {isLastPageOfLastSegment() ? (isDoneEnabledByScript() || 'Continue') : 'Continue'}
                      </Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
              <View style={styles.questionFooterCol} />
            </View>
            <View style={styles.questionFooterRow}>
              {/* disableSave PreScript flag — hidden, but the row keeps its layout slot */}
              {isSaveLinkHiddenByScript() ? null : (
                <TouchableOpacity onPress={handleCancel} style={styles.footerSaveLinkButton}>
                  <Text style={styles.footerSaveLink} numberOfLines={1}>Save and finish later</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Debug tools — deliberately below the footer controls (back/continue/save-and-
              finish-later) rather than scrolling with the question content, so they read as
              a fixed debug panel rather than competing with the actual question UI. */}
          {renderPageLogic()}
          {renderCalcLogic()}
          {renderDebugInfo()}
        </View>
      </View>
    );
  }

  // This should never happen since AdminSegment is always provided
  return null;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#3498db',
    fontWeight: '600',
  },
  // Save & Finish Later dialog styles
  dialogBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  dialogCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 420,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 8,
  },
  dialogTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: 10,
  },
  dialogMessage: {
    fontSize: 14,
    color: '#555',
    lineHeight: 20,
    marginBottom: 24,
  },
  dialogButtons: {
    flexDirection: 'column',
    alignItems: 'stretch',
    marginTop: 4,
  },
  dialogBtnSecondary: {
    paddingVertical: 12,
    marginHorizontal: 8,
    marginBottom: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#3498db',
    alignItems: 'center',
  },
  dialogBtnSecondaryText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#3498db',
    textAlign: 'center',
  },
  dialogBtnPrimary: {
    paddingVertical: 12,
    marginHorizontal: 8,
    borderRadius: 8,
    backgroundColor: '#3498db',
    alignItems: 'center',
  },
  dialogBtnPrimaryText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
  content: {
    padding: 16,
  },
  contentPhone: {
    padding: 8,
  },
  debugHeader: {
    backgroundColor: '#f1f3f4',
    padding: 12,
    marginBottom: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#4285f4',
  },
  debugHeaderText: {
    fontSize: 12,
    color: '#5f6368',
    fontFamily: 'monospace',
    lineHeight: 16,
  },
  questionContainer: {
    padding: 20,
    marginBottom: 16,
    maxWidth: 650,
    width: '100%',
    alignSelf: 'center',
  },
  // Same symmetric compaction as contentPhone/questionCardPhone — the desktop 20px here plus
  // the card's 20px and content's 16px is ~56px a side, which crowds a phone-width screen.
  questionContainerPhone: {
    padding: 8,
  },
  questionText: {
    ...QUESTION_TEXT_FONT,
    marginBottom: 2,
  },
  // Question text block — left-justified and top-aligned (not vertically centered, which
  // clipped the text once a fixed overrideQHeight got too short for the wrapped text). The
  // 24px bottom margin (on top of questionText's own 2px) is the gap between the question
  // text and the answer buttons.
  htmlQuestionContainer: {
    marginBottom: 24,
    justifyContent: 'flex-start',
    alignItems: 'stretch',
  },
  // Scrolling variant (overrideQHeight set) — margin only; RN wants child layout props like
  // alignItems on a ScrollView's contentContainerStyle, not its own style, and the content
  // already starts top-left by default.
  htmlQuestionScroll: {
    marginBottom: 24,
  },
  htmlTextContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
  },
  // 'dt' insertion function — row of month/day/year dropdowns (see renderDateControl).
  dateControlRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginVertical: 4,
  },
  // Native inline-dropdown look (no <select> available) — cycles through options on tap.
  dateControlDropdownBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#c7c7c7',
    borderRadius: 4,
    backgroundColor: '#ffffff',
    gap: 6,
  },
  dateControlDropdownText: {
    fontFamily: 'Arial',
    fontSize: 15,
    color: '#2c3e50',
  },
  dateControlDropdownChevron: {
    fontFamily: 'Arial',
    fontSize: 11,
    color: '#7f8c8d',
  },
  // Used by 2-column (left-justified, per its own row maxWidth) and non-button answer
  // types (checkboxes, etc.) — deliberately NOT centered, unlike the two below.
  answersContainer: {
    gap: 8,
  },
  // vertical-button overrideQLayout — stacked column, centered as a cluster. The container
  // itself shrink-wraps to its widest button (alignSelf center = content-sized, capped at the
  // parent's width) and alignItems 'stretch' then gives every button that same common width,
  // so no answer's text has to wrap or truncate just because a sibling is shorter/longer.
  answersContainerButtonVertical: {
    gap: 8,
    alignSelf: 'center',
    alignItems: 'stretch',
    maxWidth: '100%',
  },
  // horizontal-button overrideQLayout — buttons sit side by side and wrap instead of
  // stacking, centered as a cluster.
  answersContainerHorizontal: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 24,
  },
  // enableConsent PreScript flag — Agree/Exit buttons, styled to match the reference markup
  // (rounded/shadowed "Agree" button, plain-text-style "Exit").
  consentButtonRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 34,
    marginBottom: -10,
    gap: 24,
  },
  consentAgreeButton: {
    minWidth: 140,
    height: 55,
    borderRadius: 10,
    backgroundColor: '#e5e5e5',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 15,
    shadowColor: '#b7b7b7',
    shadowOffset: { width: 1, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
  },
  consentAgreeButtonText: {
    fontSize: 20,
    color: '#2c3e50',
    fontWeight: '600',
  },
  consentExitButton: {
    paddingHorizontal: 30,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  consentExitButtonText: {
    fontSize: 18,
    color: '#666666',
  },
  footer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#e1e8ed',
    flexDirection: 'row',
    alignItems: 'center',
  },
  navButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#3498db',
    borderRadius: 8,
  },
  navButtonText: {
    color: '#ffffff',
    fontWeight: '600',
  },
  spacer: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#7f8c8d',
  },
  completionContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  completionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#27ae60',
    marginBottom: 16,
    textAlign: 'center',
  },
  completionText: {
    fontSize: 16,
    color: '#2c3e50',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 24,
  },
  completionButton: {
    backgroundColor: '#3498db',
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
  },
  completionButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  assessmentControls: {
    backgroundColor: '#ffffff',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e8ed',
    alignItems: 'center',
  },
  startButton: {
    backgroundColor: '#27ae60',
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    minWidth: 200,
    alignItems: 'center',
  },
  startButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  statusContainer: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#27ae60',
  },
  statusText: {
    color: '#27ae60',
    fontSize: 14,
    fontWeight: '600',
  },
  // Question-page skin header — logo + background only, no menu/nav buttons.
  skinHeaderShell: {
    width: '100%',
    height: QUESTION_HEADER_HEIGHT,
    backgroundColor: 'transparent',
  },
  skinHeaderBackgroundImage: {
    width: '100%',
    height: QUESTION_HEADER_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  skinHeaderLogo: {
    height: QUESTION_HEADER_LOGO_HEIGHT,
    width: QUESTION_HEADER_LOGO_HEIGHT,
    marginLeft: 24,
  },
  // Question card — wraps the page content + footer in the requested rounded/bordered panel.
  questionCard: {
    width: '100%',
    maxWidth: 690,
    alignSelf: 'center',
    marginTop: 16,
    marginBottom: 16,
    borderRadius: 20,
    backgroundColor: '#f7f7f7',
    borderWidth: 1,
    borderColor: '#f7f7f7',
    paddingLeft: 20,
    paddingRight: 20,
    // Extra breathing room above the question text, on top of whatever the scroll
    // content/questionContainer padding already contributes.
    paddingTop: 16,
    shadowColor: '#dbdbdb',
    shadowOffset: { width: 1, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
    overflow: 'hidden',
  },
  // Phone width: 8px card padding + 8px content + 8px question container = 24px a side, so the
  // card's padding is the smaller of the two here and the footer adds the remaining 16px on
  // BOTH sides (desktop's footer is left-padded only, which off-centers the Continue button).
  questionCardPhone: {
    paddingLeft: 8,
    paddingRight: 8,
  },
  questionFooter: {
    paddingTop: 8,
    paddingBottom: 16,
    paddingLeft: 25,
  },
  questionFooterPhone: {
    paddingLeft: 16,
    paddingRight: 16,
  },
  questionFooterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 40,
  },
  questionFooterCol: {
    flex: 1,
    alignItems: 'center',
  },
  questionFooterColCenter: {
    alignItems: 'center',
  },
  // Undecorated text link — mirrors ReportView's reportOutputBackText.
  footerBackButton: {
    alignSelf: 'flex-start',
  },
  footerBackButtonText: {
    fontSize: 16,
    fontWeight: 'normal',
    color: '#2c3e50',
  },
  footerBackButtonTextDisabled: {
    color: '#c0c0c0',
  },
  footerProgressTrack: {
    width: '100%',
    maxWidth: 220,
    height: 11,
    borderRadius: 4,
    backgroundColor: '#e2e2e2',
    overflow: 'hidden',
  },
  footerProgressFill: {
    height: '100%',
    borderRadius: 4,
    backgroundColor: '#b7b7b7',
  },
  footerContinueButton: {
    width: 140,
    height: 55,
    borderRadius: 4,
    backgroundColor: '#efefef',
    shadowColor: '#b7b7b7',
    shadowOffset: { width: 1, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerContinueButtonDisabled: {
    opacity: 0.5,
  },
  footerContinueButtonText: {
    color: '#000000',
    fontWeight: '400',
    fontSize: 18,
  },
  footerSaveLinkButton: {
    alignSelf: 'flex-start',
  },
  footerSaveLink: {
    fontFamily: 'Arial',
    fontSize: 14, // 2px under footerBackButtonText's 16
    color: '#468E74',
  },
  pageInfoText: {
    fontSize: 14,
    color: '#7f8c8d',
    marginTop: 8,
    fontStyle: 'italic',
  },
  
  // Answer component styles
  singleAnswerButton: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    borderWidth: 2,
    borderColor: '#e1e8ed',
    marginBottom: 8,
  },
  selectedAnswerButton: {
    backgroundColor: '#e3f2fd',
    borderColor: '#3498db',
  },
  radioContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#bdc3c7',
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: {
    borderColor: '#3498db',
  },
  radioInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#3498db',
  },
  singleAnswerText: {
    fontSize: 14,
    color: '#2c3e50',
    flex: 1,
  },
  selectedAnswerText: {
    color: '#3498db',
    fontWeight: '600',
  },
  
  // Submit button styles (for single answer auto-advance)
  submitAnswerButton: {
    backgroundColor: '#3498db',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  selectedSubmitButton: {
    backgroundColor: '#27ae60',
    transform: [{ scale: 0.98 }],
  },
  submitAnswerText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
    textAlign: 'center',
  },
  selectedSubmitText: {
    color: '#ffffff',
  },
  submitModeText: {
    fontSize: 12,
    color: '#7f8c8d',
    fontStyle: 'italic',
  },

  // ── vertical-button / horizontal-button overrideQLayout ───────────────────────────
  // Rounded panel button matching the reference <input type="submit"> markup (light gray,
  // subtle box-shadow, ~20px text). No alignSelf here deliberately — sizing to content
  // (minWidth as a floor, not a fixed width) and centering both come from the container
  // (answersContainerButtonVertical / answersContainerHorizontal below): their non-stretch
  // alignItems/justifyContent already keeps each button its own content width without
  // needing a per-button override, and a per-button alignSelf would win over the
  // container's cross-axis alignItems, which is exactly what broke vertical centering.
  panelButton: {
    borderRadius: 10,
    backgroundColor: '#e5e5e5',
    shadowColor: '#b7b7b7',
    shadowOffset: { width: 1, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
    paddingVertical: 3,
    paddingHorizontal: 15,
    minHeight: 55,
    minWidth: 150,
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  panelButtonText: {
    fontSize: 20,
    color: '#000000',
    textAlign: 'center',
  },
  // Selected state — background intentionally unchanged; the highlight is the text itself
  // going bold and slightly larger instead.
  panelButtonTextSelected: {
    fontWeight: 'bold',
    fontSize: 22,
  },

  // ── vertical2column answer layout ─────────────────────────────────────────
  // Row: left badge (numeric keyValue) + right label (aText). Full row is tappable.
  twoColAnswerRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#e1e8ed',
    marginBottom: 10,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 2,
  },
  twoColAnswerRowSelected: {
    borderColor: '#27ae60',
    backgroundColor: '#f0faf4',
  },
  twoColKeyCell: {
    width: 52,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#10b981', // default; overridden inline via answerButtonColor
    paddingVertical: 14,
  },
  twoColKeyCellSelected: {
    backgroundColor: '#27ae60', // overridden inline when selected
  },
  twoColKeyText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
  },
  twoColKeyTextSelected: {
    color: '#ffffff',
  },
  twoColTextCell: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 14,
  },
  twoColAnswerText: {
    fontSize: 15,
    color: '#2c3e50',
    lineHeight: 21,
  },
  twoColAnswerTextSelected: {
    color: '#1a7a45',
    fontWeight: '600',
  },
  
  multipleAnswerButton: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e1e8ed',
    marginBottom: 8,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxOuter: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#bdc3c7',
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#3498db',
    borderColor: '#3498db',
  },
  checkboxInner: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  multipleAnswerText: {
    fontSize: 14,
    color: '#2c3e50',
    flex: 1,
  },
  
  textInput: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e1e8ed',
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: '#2c3e50',
    minHeight: 44,
  },
  
  memoInput: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e1e8ed',
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: '#2c3e50',
    minHeight: 100,
    textAlignVertical: 'top',
  },
  
  dateButton: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e1e8ed',
    paddingHorizontal: 12,
    paddingVertical: 12,
    minHeight: 44,
    justifyContent: 'center',
  },
  dateButtonText: {
    fontSize: 16,
    color: '#2c3e50',
  },
  
  unsupportedText: {
    fontSize: 14,
    color: '#e74c3c',
    fontStyle: 'italic',
    padding: 12,
    backgroundColor: '#fff5f5',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f8d7da',
  },

  // Debug styles
  debugContainer: {
    backgroundColor: '#f8f9fa',
    borderTopWidth: 2,
    borderTopColor: '#e9ecef',
    padding: 12,
    margin: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#dee2e6',
  },
  debugTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#495057',
    marginBottom: 8,
    textAlign: 'center',
  },
  debugSection: {
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  debugSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6c757d',
    marginBottom: 4,
  },
  debugSubtitle: {
    fontSize: 12,
    fontStyle: 'italic',
    color: '#7f8c8d',
    marginBottom: 8,
  },
  debugText: {
    fontSize: 12,
    color: '#495057',
    marginLeft: 8,
    marginBottom: 2,
  },
  debugDataItem: {
    backgroundColor: '#ffffff',
    padding: 8,
    marginLeft: 8,
    marginBottom: 4,
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: '#007bff',
  },
  debugDataName: {
    fontSize: 11,
    fontWeight: '600',
    color: '#495057',
  },
  debugDataValue: {
    fontSize: 11,
    color: '#007bff',
    marginTop: 2,
  },
  debugDataMeta: {
    fontSize: 10,
    color: '#6c757d',
    marginTop: 2,
    fontStyle: 'italic',
  },
  debugButtonsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  debugButton: {
    backgroundColor: '#007bff',
    padding: 8,
    borderRadius: 4,
    marginBottom: 5,
    minWidth: 80,
    alignItems: 'center',
  },
  debugButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  pageLogicHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  continueButton: {
    backgroundColor: '#28a745',
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 5,
    alignItems: 'center',
  },
  continueButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  scriptEditor: {
    backgroundColor: '#f8f9fa',
    borderColor: '#dee2e6',
    borderWidth: 1,
    borderRadius: 4,
    padding: 10,
    fontSize: 12,
    fontFamily: 'monospace',
    textAlignVertical: 'top',
    minHeight: 320,
  },
  dirtyIndicator: {
    fontSize: 11,
    color: '#ffc107',
    marginTop: 5,
    fontWeight: '600',
  },
  hintText: {
    fontSize: 10,
    color: '#999',
    marginTop: 8,
    marginHorizontal: 4,
    fontStyle: 'italic',
  },
  singleStepIndicator: {
    fontSize: 11,
    color: '#007bff',
    marginTop: 5,
    fontWeight: '600',
  },
});

export default AdminView;